/* eslint-disable react-hooks/exhaustive-deps */
import { useState, useEffect, useCallback, useRef } from "react";

// ── Simple URL Router ────────────────────────────────────────
// Check if we're on the estimate signing page before loading the main app
const currentPath = window.location.pathname;

// ============================================================
// API CLIENT — talks to your backend on localhost:3001
// ============================================================
const API_BASE = (process.env.REACT_APP_API_URL || "http://localhost:3001") + "/api";

let authToken = localStorage.getItem("apex_token") || null;

const api = {
  setToken(t) { authToken = t; if (t) localStorage.setItem("apex_token", t); else localStorage.removeItem("apex_token"); },
  headers() {
    return { "Content-Type": "application/json", ...(authToken ? { Authorization: `Bearer ${authToken}` } : {}) };
  },
  async request(method, path, body) {
    const res = await fetch(`${API_BASE}${path}`, { method, headers: this.headers(), body: body ? JSON.stringify(body) : undefined });
    const data = await res.json();
    if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`);
    return data;
  },
  get: (path) => api.request("GET", path),
  post: (path, body) => api.request("POST", path, body),
  put: (path, body) => api.request("PUT", path, body),
  delete: (path) => api.request("DELETE", path),
};

// ============================================================
// STYLES
// ============================================================
const css = `
  @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;600;700;800&family=DM+Sans:ital,wght@0,300;0,400;0,500;1,300&display=swap');
  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
  :root {
    --bg: #0a0d12; --surface: #12161d; --surface2: #1a1f29; --border: #252c3a;
    --accent: #00d4aa; --accent2: #ff6b35; --text: #e8ecf4; --muted: #6b7590;
    --paid: #00d4aa; --pending: #f5a623; --overdue: #ff4757; --draft: #6b7590;
    --font-head: 'Plus Jakarta Sans', sans-serif; --font-body: 'DM Sans', sans-serif; --radius: 10px;
  }
  body { background: var(--bg); color: var(--text); font-family: var(--font-body); }
  .app { display: flex; min-height: 100vh; }
  .sidebar { width: 230px; min-height: 100vh; background: var(--surface); border-right: 1px solid var(--border); padding: 28px 0; display: flex; flex-direction: column; position: fixed; top: 0; left: 0; z-index: 100; }
  .logo { padding: 0 24px 32px; display: flex; align-items: center; }
  .logo img { height: 80px; width: auto; object-fit: contain; }
  .nav-item { display: flex; align-items: center; gap: 12px; padding: 12px 24px; cursor: pointer; font-size: 14px; font-weight: 500; color: var(--muted); border-left: 3px solid transparent; transition: all 0.2s; user-select: none; }
  .nav-item:hover { color: var(--text); background: var(--surface2); }
  .nav-item.active { color: var(--accent); border-left-color: var(--accent); background: rgba(0,212,170,0.06); }
  .nav-icon { font-size: 16px; width: 20px; text-align: center; }
  .sidebar-footer { margin-top: auto; padding: 20px 24px; border-top: 1px solid var(--border); }
  .qbo-badge { display: flex; align-items: center; gap: 8px; padding: 10px 12px; background: rgba(44,160,44,0.1); border: 1px solid rgba(44,160,44,0.25); border-radius: var(--radius); cursor: pointer; font-size: 12px; font-weight: 500; color: #4ade80; transition: all 0.2s; }
  .qbo-badge.off { background: rgba(107,117,144,0.1); border-color: rgba(107,117,144,0.25); color: var(--muted); }
  .qbo-dot { width: 7px; height: 7px; border-radius: 50%; background: #4ade80; animation: pulse 2s infinite; }
  .qbo-dot.off { background: var(--muted); animation: none; }
  @keyframes pulse { 0%,100%{opacity:1} 50%{opacity:.4} }
  .main { margin-left: 230px; flex: 1; padding: 36px 40px; min-height: 100vh; }
  .topbar { display: flex; justify-content: space-between; align-items: center; margin-bottom: 36px; }
  .page-title { font-family: var(--font-head); font-size: 28px; font-weight: 800; letter-spacing: -0.5px; }
  .page-title span { color: var(--accent); }
  .topbar-actions { display: flex; gap: 10px; align-items: center; }
  .btn { padding: 10px 20px; border-radius: var(--radius); font-size: 14px; font-weight: 600; cursor: pointer; border: none; font-family: var(--font-body); transition: all 0.2s; display: inline-flex; align-items: center; gap: 6px; }
  .btn:disabled { opacity: 0.5; cursor: not-allowed; }
  .btn-primary { background: var(--accent); color: #0a0d12; }
  .btn-primary:hover:not(:disabled) { background: #00eabb; transform: translateY(-1px); box-shadow: 0 4px 16px rgba(0,212,170,0.3); }
  .btn-secondary { background: var(--surface2); color: var(--text); border: 1px solid var(--border); }
  .btn-secondary:hover:not(:disabled) { border-color: var(--accent); color: var(--accent); }
  .btn-sm { padding: 7px 14px; font-size: 13px; }
  .stats { display: grid; grid-template-columns: repeat(4, 1fr); gap: 18px; margin-bottom: 32px; }
  .stat-card { background: var(--surface); border: 1px solid var(--border); border-radius: var(--radius); padding: 22px; position: relative; overflow: hidden; }
  .stat-card::before { content: ''; position: absolute; top: 0; left: 0; right: 0; height: 2px; }
  .stat-card.green::before { background: var(--paid); }
  .stat-card.orange::before { background: var(--pending); }
  .stat-card.red::before { background: var(--overdue); }
  .stat-card.blue::before { background: #4f8ef7; }
  .stat-label { font-size: 12px; color: var(--muted); font-weight: 500; text-transform: uppercase; letter-spacing: 0.5px; margin-bottom: 10px; }
  .stat-value { font-family: var(--font-head); font-size: 26px; font-weight: 700; }
  .stat-sub { font-size: 12px; color: var(--muted); margin-top: 4px; }
  .table-card { background: var(--surface); border: 1px solid var(--border); border-radius: var(--radius); overflow: hidden; }
  .table-header { padding: 18px 24px; border-bottom: 1px solid var(--border); display: flex; justify-content: space-between; align-items: center; flex-wrap: wrap; gap: 10px; }
  .table-title { font-family: var(--font-head); font-size: 16px; font-weight: 700; }
  .filters { display: flex; gap: 8px; flex-wrap: wrap; }
  .filter-btn { padding: 6px 14px; border-radius: 20px; font-size: 12px; font-weight: 600; cursor: pointer; border: 1px solid var(--border); background: transparent; color: var(--muted); transition: all 0.2s; font-family: var(--font-body); }
  .filter-btn.active { background: var(--accent); color: #0a0d12; border-color: var(--accent); }
  table { width: 100%; border-collapse: collapse; }
  th { padding: 12px 16px; text-align: left; font-size: 11px; font-weight: 600; color: var(--muted); text-transform: uppercase; letter-spacing: 0.5px; border-bottom: 1px solid var(--border); }
  td { padding: 14px 16px; font-size: 14px; border-bottom: 1px solid rgba(37,44,58,0.5); }
  tr:last-child td { border-bottom: none; }
  tr:hover td { background: rgba(255,255,255,0.02); }
  .status-badge { display: inline-flex; align-items: center; padding: 4px 10px; border-radius: 20px; font-size: 11px; font-weight: 600; text-transform: uppercase; letter-spacing: 0.3px; }
  .status-paid { background: rgba(0,212,170,0.12); color: var(--paid); }
  .status-pending { background: rgba(245,166,35,0.12); color: var(--pending); }
  .status-overdue { background: rgba(255,71,87,0.12); color: var(--overdue); }
  .status-draft { background: rgba(107,117,144,0.12); color: var(--draft); }
  .status-accepted { background: rgba(79,142,247,0.12); color: #4f8ef7; }
  .amount { font-family: var(--font-head); font-weight: 600; }
  .action-btn { padding: 5px 10px; border-radius: 6px; font-size: 12px; font-weight: 500; cursor: pointer; border: 1px solid var(--border); background: transparent; color: var(--muted); transition: all 0.15s; font-family: var(--font-body); margin-right: 4px; }
  .action-btn:hover { color: var(--accent); border-color: var(--accent); }
  .action-btn.danger:hover { color: var(--overdue); border-color: var(--overdue); }
  .modal-overlay { position: fixed; inset: 0; background: rgba(0,0,0,0.75); z-index: 1000; display: flex; align-items: center; justify-content: center; padding: 20px; animation: fadeIn 0.2s ease; }
  @keyframes fadeIn { from{opacity:0} to{opacity:1} }
  .modal { background: var(--surface); border: 1px solid var(--border); border-radius: 14px; width: 100%; max-width: 760px; max-height: 90vh; overflow-y: auto; animation: slideUp 0.25s ease; }
  .modal-wide { max-width: 900px; }
  @keyframes slideUp { from{transform:translateY(20px);opacity:0} to{transform:translateY(0);opacity:1} }
  .modal-head { padding: 24px 28px; border-bottom: 1px solid var(--border); display: flex; justify-content: space-between; align-items: flex-start; }
  .modal-title { font-family: var(--font-head); font-size: 20px; font-weight: 700; }
  .modal-body { padding: 28px; }
  .modal-footer { padding: 20px 28px; border-top: 1px solid var(--border); display: flex; justify-content: flex-end; gap: 10px; }
  .close-btn { background: none; border: none; color: var(--muted); cursor: pointer; font-size: 24px; line-height: 1; padding: 0; }
  .close-btn:hover { color: var(--text); }
  .form-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 16px; }
  .form-group { display: flex; flex-direction: column; gap: 6px; }
  .form-group.full { grid-column: 1/-1; }
  label { font-size: 12px; font-weight: 600; color: var(--muted); text-transform: uppercase; letter-spacing: 0.4px; }
  input, select, textarea { background: var(--surface2); border: 1px solid var(--border); border-radius: 8px; padding: 10px 14px; color: var(--text); font-family: var(--font-body); font-size: 14px; outline: none; transition: border-color 0.2s; width: 100%; }
  input:focus, select:focus, textarea:focus { border-color: var(--accent); }
  textarea { resize: vertical; min-height: 80px; }
  select option { background: var(--surface2); }
  .line-items-header { display: grid; grid-template-columns: 3fr 1fr 1.2fr auto; gap: 8px; margin-bottom: 6px; }
  .line-item-row { display: grid; grid-template-columns: 3fr 1fr 1.2fr auto; gap: 8px; align-items: center; margin-bottom: 8px; }
  .remove-btn { background: none; border: none; color: var(--muted); cursor: pointer; font-size: 18px; padding: 4px; transition: color 0.15s; }
  .remove-btn:hover { color: var(--overdue); }
  .totals-box { background: var(--surface2); border-radius: var(--radius); padding: 18px 20px; margin-top: 16px; }
  .total-row { display: flex; justify-content: space-between; font-size: 14px; margin-bottom: 8px; color: var(--muted); }
  .total-row.grand { font-family: var(--font-head); font-size: 20px; font-weight: 700; color: var(--text); border-top: 1px solid var(--border); padding-top: 10px; margin-top: 4px; margin-bottom: 0; }
  .total-row.grand span:last-child { color: var(--accent); }
  .payment-methods { display: flex; gap: 10px; margin-bottom: 20px; }
  .pm-btn { flex: 1; padding: 14px; border-radius: var(--radius); border: 2px solid var(--border); background: var(--surface2); cursor: pointer; text-align: center; transition: all 0.2s; color: var(--muted); font-size: 13px; font-weight: 600; font-family: var(--font-body); }
  .pm-btn.active { border-color: var(--accent); color: var(--accent); background: rgba(0,212,170,0.06); }
  .pm-icon { font-size: 22px; display: block; margin-bottom: 4px; }
  .success-screen { text-align: center; padding: 30px 0; }
  .success-icon { font-size: 56px; margin-bottom: 16px; }
  .success-title { font-family: var(--font-head); font-size: 22px; font-weight: 700; margin-bottom: 8px; color: var(--paid); }
  .success-sub { color: var(--muted); font-size: 14px; margin-top: 6px; }
  .detail-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 24px; margin-bottom: 24px; }
  .detail-section { background: var(--surface2); border-radius: var(--radius); padding: 18px; }
  .login-screen { min-height: 100vh; display: flex; align-items: center; justify-content: center; background: var(--bg); }
  .login-card { background: var(--surface); border: 1px solid var(--border); border-radius: 16px; padding: 48px; width: 420px; }
  .login-logo { margin-bottom: 4px; display: flex; justify-content: center; align-items: center; }
  .login-logo img { height: 150px; width: auto; object-fit: contain; }
  .login-sub { color: var(--muted); font-size: 14px; margin-bottom: 28px; }
  .login-error { background: rgba(255,71,87,0.1); border: 1px solid rgba(255,71,87,0.3); color: var(--overdue); padding: 10px 14px; border-radius: 8px; font-size: 13px; margin-bottom: 16px; }
  .login-tabs { display: flex; gap: 0; margin-bottom: 28px; background: var(--surface2); border-radius: 8px; padding: 4px; }
  .login-tab { flex: 1; padding: 8px; text-align: center; border-radius: 6px; font-size: 14px; font-weight: 600; cursor: pointer; color: var(--muted); transition: all 0.2s; }
  .login-tab.active { background: var(--accent); color: #0a0d12; }
  .login-switch { text-align: center; margin-top: 16px; font-size: 13px; color: var(--muted); }
  .login-switch a { color: var(--accent); cursor: pointer; text-decoration: none; }
  .login-switch a:hover { text-decoration: underline; }
  .loading { display: flex; align-items: center; justify-content: center; padding: 60px; color: var(--muted); gap: 10px; }
  .spinner { width: 20px; height: 20px; border: 2px solid var(--border); border-top-color: var(--accent); border-radius: 50%; animation: spin 0.7s linear infinite; }
  @keyframes spin { to { transform: rotate(360deg); } }
  .empty { text-align: center; padding: 60px; color: var(--muted); }
  .empty-icon { font-size: 40px; margin-bottom: 12px; opacity: 0.5; }
  .toast { position: fixed; bottom: 30px; right: 30px; background: var(--surface); border: 1px solid var(--border); border-left: 3px solid var(--accent); border-radius: var(--radius); padding: 14px 20px; z-index: 2000; font-size: 14px; animation: toastIn 0.3s ease; min-width: 280px; max-width: 400px; }
  .toast.error { border-left-color: var(--overdue); }
  .toast.warning { border-left-color: var(--pending); }
  @keyframes toastIn { from{transform:translateX(120%);opacity:0} to{transform:translateX(0);opacity:1} }
  .info-box { padding: 12px 14px; border-radius: 8px; font-size: 12px; margin-top: 12px; }
  .info-box.green { background: rgba(0,212,170,0.06); border: 1px solid rgba(0,212,170,0.2); color: var(--paid); }
  .info-box.yellow { background: rgba(245,166,35,0.06); border: 1px solid rgba(245,166,35,0.2); color: var(--pending); }
  .int-card { background: var(--surface2); border: 1px solid var(--border); border-radius: var(--radius); padding: 22px; margin-bottom: 16px; }
  .int-header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 16px; }
  .int-title { font-family: var(--font-head); font-size: 16px; font-weight: 700; }
  .int-status { font-size: 12px; padding: 4px 10px; border-radius: 20px; font-weight: 600; }
  .int-connected { background: rgba(0,212,170,0.12); color: var(--paid); }
  .int-disconnected { background: rgba(107,117,144,0.12); color: var(--muted); }
  .settings-title { font-family: var(--font-head); font-size: 13px; font-weight: 700; color: var(--muted); text-transform: uppercase; letter-spacing: 0.8px; margin-bottom: 14px; }
  .divider { border: none; border-top: 1px solid var(--border); margin: 20px 0; }
  code { background: var(--surface); padding: 2px 6px; border-radius: 4px; font-size: 11px; color: var(--accent); }
`;

const fmt = (n) => "$" + (parseFloat(n) || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const addDays = (days) => new Date(Date.now() + days * 86400000).toISOString().split("T")[0];
const fmtDate = (d) => {
  if (!d) return "—";
  try {
    // Handle both "2026-04-27" and "2026-04-27T00:00:00.000Z"
    const dateStr = typeof d === 'string' ? d.split('T')[0] : d;
    const [year, month, day] = dateStr.split('-').map(Number);
    return new Date(year, month - 1, day).toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" });
  } catch { return String(d).split('T')[0]; }
};

// ── PAYMENT PORTAL PAGE ──────────────────────────────────────
const payPortalCss = `
  @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@700;800&family=DM+Sans:wght@300;400;500&display=swap');
  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
  body { background: #f0f4f8; color: #1a1a2e; font-family: 'DM Sans', sans-serif; }
  .pay-page { max-width: 680px; margin: 0 auto; padding: 32px 20px 80px; min-height: 100vh; }
  .pay-header { background: #0a0d12; border-radius: 12px; padding: 24px 32px; margin-bottom: 28px; display: flex; justify-content: space-between; align-items: center; }
  .pay-logo { display: flex; align-items: center; }
  .pay-logo img { height: 150px; width: auto; object-fit: contain; }
  .pay-secure { font-size: 12px; color: #6b7590; }
  .pay-card { background: #fff; border-radius: 12px; padding: 28px 32px; margin-bottom: 20px; box-shadow: 0 2px 12px rgba(0,0,0,0.06); }
  .pay-card-title { font-family: 'Plus Jakarta Sans', sans-serif; font-size: 16px; font-weight: 700; margin-bottom: 16px; }
  .pay-meta { display: grid; grid-template-columns: 1fr 1fr; gap: 16px; }
  .pay-meta-label { font-size: 11px; color: #999; text-transform: uppercase; letter-spacing: 0.5px; margin-bottom: 3px; }
  .pay-meta-value { font-size: 14px; font-weight: 600; }
  .pay-meta-sub { font-size: 12px; color: #666; margin-top: 2px; }
  .pay-table { width: 100%; border-collapse: collapse; }
  .pay-table th { padding: 10px 0; text-align: left; font-size: 11px; color: #999; text-transform: uppercase; border-bottom: 2px solid #f0f0f0; }
  .pay-table th:not(:first-child) { text-align: right; }
  .pay-table td { padding: 10px 0; border-bottom: 1px solid #f5f5f5; font-size: 14px; }
  .pay-table td:not(:first-child) { text-align: right; }
  .pay-totals { margin-top: 12px; }
  .pay-total-row { display: flex; justify-content: space-between; padding: 5px 0; font-size: 14px; color: #666; }
  .pay-total-row.grand { font-family: 'Plus Jakarta Sans', sans-serif; font-size: 20px; font-weight: 700; color: #1a1a2e; border-top: 2px solid #eee; padding-top: 10px; margin-top: 4px; }
  .pay-total-row.grand span:last-child { color: #00a87a; }
  .pay-total-row.paid-row { color: #00a87a; }
  .pay-form-group { display: flex; flex-direction: column; gap: 6px; margin-bottom: 14px; }
  .pay-form-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 14px; }
  .pay-label { font-size: 11px; font-weight: 600; color: #666; text-transform: uppercase; letter-spacing: 0.4px; }
  .pay-input { background: #f8f9fa; border: 1px solid #e0e0e0; border-radius: 8px; padding: 10px 14px; font-size: 14px; font-family: 'DM Sans', sans-serif; outline: none; width: 100%; transition: border-color 0.2s; color: #1a1a2e; }
  .pay-input:focus { border-color: #00d4aa; background: #fff; }
  .pay-iframe-wrap { background: #f8f9fa; border: 1px solid #e0e0e0; border-radius: 8px; overflow: hidden; height: 44px; }
  .pay-iframe-wrap iframe { width: 100%; height: 44px; border: none; display: block; }
  .pay-btn { width: 100%; background: #00d4aa; color: #0a0d12; border: none; border-radius: 10px; padding: 16px; font-size: 16px; font-weight: 700; font-family: 'Plus Jakarta Sans', sans-serif; cursor: pointer; margin-top: 8px; transition: all 0.2s; }
  .pay-btn:hover:not(:disabled) { background: #00eabb; transform: translateY(-1px); box-shadow: 0 4px 16px rgba(0,212,170,0.3); }
  .pay-btn:disabled { opacity: 0.5; cursor: not-allowed; }
  .pay-security { display: flex; align-items: center; gap: 6px; font-size: 11px; color: #999; justify-content: center; margin-top: 10px; }
  .pay-success { text-align: center; padding: 32px 0; }
  .pay-success-icon { font-size: 64px; margin-bottom: 16px; }
  .pay-success-title { font-family: 'Plus Jakarta Sans', sans-serif; font-size: 26px; font-weight: 800; color: #00a87a; margin-bottom: 8px; }
  .pay-success-sub { color: #666; font-size: 15px; line-height: 1.6; }
  .pay-receipt { background: #f8f9fa; border-radius: 10px; padding: 20px 24px; margin: 20px auto; max-width: 340px; text-align: left; }
  .pay-receipt-row { display: flex; justify-content: space-between; font-size: 14px; margin-bottom: 8px; }
  .pay-receipt-row:last-child { margin-bottom: 0; font-weight: 700; color: #00a87a; }
  .pay-loading { text-align: center; padding: 80px 20px; color: #999; }
  .pay-spinner { width: 36px; height: 36px; border: 3px solid #eee; border-top-color: #00d4aa; border-radius: 50%; animation: pspin 0.7s linear infinite; margin: 0 auto 16px; }
  @keyframes pspin { to { transform: rotate(360deg); } }
  .pay-error-box { background: #fff5f5; border: 1px solid #ff4757; border-radius: 8px; padding: 12px 16px; font-size: 13px; color: #ff4757; margin-bottom: 14px; }
  .pay-already-paid { background: #f0fffe; border: 1px solid #00d4aa; border-radius: 10px; padding: 24px; text-align: center; }
`;

function PaymentPortalPage() {
  const invoiceId = currentPath.split("/pay/")[1];
  const [invoice, setInvoice] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [payError, setPayError] = useState("");
  const [processing, setProcessing] = useState(false);
  const [success, setSuccess] = useState(null);
  const [cardToken, setCardToken] = useState("");
  const [form, setForm] = useState({ name: "", expiry: "", cvv2: "", address: "", zip: "" });
  const [payMode, setPayMode] = useState("full");
  const [customAmt, setCustomAmt] = useState("");
  const API_BASE_PAY = (process.env.REACT_APP_API_URL || "http://localhost:3001") + "/api";

  useEffect(() => {
    if (!invoiceId) { setError("Invalid payment link"); setLoading(false); return; }
    fetch(`${API_BASE_PAY}/public/invoice/${invoiceId}`)
      .then(r => r.json())
      .then(data => { if (data.error) setError(data.error); else setInvoice(data); setLoading(false); })
      .catch(() => { setError("Could not load invoice"); setLoading(false); });
  }, [invoiceId]);

  useEffect(() => {
    const handler = (e) => {
      try {
        const data = typeof e.data === "string" ? JSON.parse(e.data) : e.data;
        if (data?.token) setCardToken(data.token);
        if (data?.message) setCardToken(data.message);
      } catch {}
    };
    window.addEventListener("message", handler);
    return () => window.removeEventListener("message", handler);
  }, []);

  const handlePay = async (payAmount) => {
    if (!form.name) return setPayError("Please enter the cardholder name");
    if (!cardToken) return setPayError("Please enter your card number");
    console.log('[Pay] Token being sent:', cardToken);
    console.log('[Pay] Token length:', cardToken.length);
    const amount = payAmount || balanceDue;
    const surcharge = invoice.fee_model !== "absorb" ? Math.round(amount * (invoice.surcharge_percent/100) * 100)/100 : 0;
    const totalCharge = amount + surcharge;
    setPayError(""); setProcessing(true);
    try {
      const res = await fetch(`${API_BASE_PAY}/public/invoice/${invoiceId}/pay`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token: cardToken, ...form, amount: totalCharge }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Payment failed");
      setSuccess(data);
    } catch (err) { setPayError(err.message); }
    finally { setProcessing(false); }
  };

  const fmtp = (n) => "$" + (parseFloat(n) || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const fmtDate = (d) => { if (!d) return "Upon receipt"; try { return new Date(d).toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" }); } catch { return d; } };

  if (loading) return <><style>{payPortalCss}</style><div className="pay-page"><div className="pay-loading"><div className="pay-spinner" /><div>Loading invoice…</div></div></div></>;
  if (error) return <><style>{payPortalCss}</style><div className="pay-page"><div className="pay-header"><div className="pay-logo"><img src={require("./logo.png")} alt="GoApexPay" /></div></div><div style={{textAlign:"center",padding:"80px 20px"}}><div style={{fontSize:48,marginBottom:16}}>⚠️</div><h2>Not Found</h2><p style={{color:"#666",marginTop:8}}>{error}</p></div></div></>;

  const isPaid = invoice.status === "paid";
  const balanceDue = parseFloat(invoice.balance_due) || 0;

  return (
    <><style>{payPortalCss}</style>
    <div className="pay-page">
      <div className="pay-header">
        <div className="pay-logo"><img src={require("./logo.png")} alt="GoApexPay" /></div>
        <div className="pay-secure">🔒 Secure Payment</div>
      </div>
      {success ? (
        <div className="pay-card">
          <div className="pay-success">
            <div className="pay-success-icon">✅</div>
            <div className="pay-success-title">Payment Successful!</div>
            <div className="pay-success-sub">Thank you! A receipt has been sent to {invoice.client_email}</div>
            <div className="pay-receipt">
              <div className="pay-receipt-row"><span style={{color:"#666"}}>Invoice</span><span>{invoice.id}</span></div>
              <div className="pay-receipt-row"><span style={{color:"#666"}}>Auth Code</span><span>{success.authcode}</span></div>
              {success.last4 && <div className="pay-receipt-row"><span style={{color:"#666"}}>Card</span><span>····{success.last4}</span></div>}
              <div className="pay-receipt-row"><span>Amount Paid</span><span>{fmtp(success.amount)}</span></div>
            </div>
          </div>
        </div>
      ) : <>
        <div className="pay-card">
          <div className="pay-meta">
            <div><div className="pay-meta-label">From</div><div className="pay-meta-value">{invoice.company_name}</div><div className="pay-meta-sub">{invoice.company_email}</div></div>
            <div><div className="pay-meta-label">Billed To</div><div className="pay-meta-value">{invoice.client_name}</div><div className="pay-meta-sub">{invoice.client_email}</div></div>
            <div><div className="pay-meta-label">Invoice #</div><div className="pay-meta-value">{invoice.id}</div></div>
            <div><div className="pay-meta-label">Due Date</div><div className="pay-meta-value" style={{color:invoice.status==="overdue"?"#ff4757":"inherit"}}>{fmtDate(invoice.due_date)}</div></div>
          </div>
        </div>
        <div className="pay-card">
          <div className="pay-card-title">Invoice Details</div>
          <table className="pay-table">
            <thead><tr><th>Description</th><th>Qty</th><th>Amount</th></tr></thead>
            <tbody>{(invoice.lineItems||[]).map((item,i) => <tr key={i}><td>{item.description}</td><td style={{textAlign:"right"}}>{item.quantity}</td><td style={{textAlign:"right",fontWeight:600}}>{fmtp(item.amount)}</td></tr>)}</tbody>
          </table>
          <div className="pay-totals">
            <div className="pay-total-row"><span>Subtotal</span><span>{fmtp(invoice.subtotal)}</span></div>
            {parseFloat(invoice.tax_amount) > 0 && <div className="pay-total-row"><span>Tax</span><span>{fmtp(invoice.tax_amount)}</span></div>}
            <div className="pay-total-row grand"><span>Total</span><span>{fmtp(invoice.total)}</span></div>
            {parseFloat(invoice.amount_paid) > 0 && <div className="pay-total-row paid-row"><span>Already Paid</span><span>-{fmtp(invoice.amount_paid)}</span></div>}
            {balanceDue > 0.01 && <div className="pay-total-row" style={{fontWeight:700,fontSize:15}}><span>Balance Due</span><span style={{color:"#ff4757"}}>{fmtp(balanceDue)}</span></div>}
            {invoice.fee_model !== "absorb" && invoice.surcharge_amount > 0 && (
              <>
                <div className="pay-total-row" style={{color:"#f5a623",marginTop:8}}><span>Card processing fee ({invoice.surcharge_percent}%)</span><span>{fmtp(invoice.surcharge_amount)}</span></div>
                <div className="pay-total-row" style={{fontWeight:700,fontSize:16,borderTop:"2px solid #eee",paddingTop:10,marginTop:4}}><span>Total Due by Card</span><span style={{color:"#1a1a2e"}}>{fmtp(invoice.total_with_surcharge)}</span></div>
              </>
            )}
          </div>
        </div>
        {isPaid ? (
          <div className="pay-card">
            <div className="pay-already-paid">
              <div style={{fontSize:40,marginBottom:12}}>✅</div>
              <div style={{fontFamily:"'Plus Jakarta Sans',sans-serif",fontSize:18,fontWeight:700,color:"#00a87a",marginBottom:6}}>Invoice Paid in Full</div>
              <div style={{fontSize:13,color:"#666"}}>Thank you for your payment!</div>
            </div>
          </div>
        ) : !invoice.has_payment_processing ? (
          <div className="pay-card" style={{textAlign:"center",padding:"32px"}}>
            <div style={{fontSize:32,marginBottom:12}}>💳</div>
            <div style={{fontSize:15,fontWeight:600,marginBottom:8}}>Online payment not available</div>
            <div style={{fontSize:13,color:"#666"}}>Please contact {invoice.company_name} at {invoice.company_email} to arrange payment.</div>
          </div>
        ) : (
          <div className="pay-card">
            <div className="pay-card-title">Pay Invoice</div>
            {payError && <div className="pay-error-box">{payError}</div>}

            {/* Payment amount selector */}
            {(() => {
              const presets = [
                { label: "50%", amount: Math.round(balanceDue * 0.5 * 100) / 100 },
                { label: "25%", amount: Math.round(balanceDue * 0.25 * 100) / 100 },
              ];
              const payAmount = payMode === "full" ? balanceDue
                : payMode === "custom" ? Math.min(Math.max(parseFloat(customAmt) || 0, 1), balanceDue)
                : presets.find(p => p.label === payMode)?.amount || balanceDue;
              const surcharge = invoice.fee_model !== "absorb" ? Math.round(payAmount * ((invoice.surcharge_percent||2.75)/100) * 100)/100 : 0;
              const totalCharge = payAmount + surcharge;
              return <>
                <div style={{marginBottom:16}}>
                  <div style={{fontSize:11,color:"#999",textTransform:"uppercase",letterSpacing:"0.5px",marginBottom:10}}>Payment Amount</div>
                  <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
                    <button onClick={() => setPayMode("full")} style={{padding:"8px 16px",borderRadius:8,border:`2px solid ${payMode==="full"?"#00d4aa":"#e0e0e0"}`,background:payMode==="full"?"rgba(0,212,170,0.1)":"#f8f9fa",fontWeight:700,fontSize:14,cursor:"pointer",color:payMode==="full"?"#00a87a":"#333",fontFamily:"'DM Sans',sans-serif"}}>
                      Full Balance {fmtp(balanceDue)}
                    </button>
                    {presets.map(p => (
                      <button key={p.label} onClick={() => setPayMode(p.label)} style={{padding:"8px 16px",borderRadius:8,border:`2px solid ${payMode===p.label?"#4f8ef7":"#e0e0e0"}`,background:payMode===p.label?"rgba(79,142,247,0.1)":"#f8f9fa",fontWeight:600,fontSize:14,cursor:"pointer",color:payMode===p.label?"#4f8ef7":"#666",fontFamily:"'DM Sans',sans-serif"}}>
                        {p.label} · {fmtp(p.amount)}
                      </button>
                    ))}
                    <button onClick={() => setPayMode("custom")} style={{padding:"8px 16px",borderRadius:8,border:`2px solid ${payMode==="custom"?"#f5a623":"#e0e0e0"}`,background:payMode==="custom"?"rgba(245,166,35,0.1)":"#f8f9fa",fontWeight:600,fontSize:14,cursor:"pointer",color:payMode==="custom"?"#b8760a":"#666",fontFamily:"'DM Sans',sans-serif"}}>
                      Other amount
                    </button>
                  </div>
                  {payMode === "custom" && (
                    <div style={{marginTop:10,display:"flex",alignItems:"center",gap:8}}>
                      <span style={{fontSize:16,color:"#666"}}>$</span>
                      <input className="pay-input" type="number" min="1" max={balanceDue} step="0.01" placeholder={`Max ${fmtp(balanceDue)}`} value={customAmt} onChange={e => setCustomAmt(e.target.value)} style={{width:160}} autoFocus />
                      <span style={{fontSize:12,color:"#999"}}>max {fmtp(balanceDue)}</span>
                    </div>
                  )}
                </div>

                <div className="pay-form-group"><label className="pay-label">Cardholder Name</label><input className="pay-input" placeholder="Jane Smith" value={form.name} onChange={e => setForm(p => ({...p, name: e.target.value}))} /></div>
                <div className="pay-form-group">
                  <label className="pay-label">Card Number</label>
                  {invoice.cardsecure_url ? (
                    <div className="pay-iframe-wrap"><iframe title="Card Number" src={`${invoice.cardsecure_url}?invalidinputevent=true&enhancedresponse=true&css=body{font-family:sans-serif;font-size:14px;padding:10px 14px;}`} scrolling="no" /></div>
                  ) : (
                    <input className="pay-input" placeholder="Enter card token (sandbox testing)" value={cardToken} onChange={e => setCardToken(e.target.value)} />
                  )}
                </div>
                <div className="pay-form-grid">
                  <div className="pay-form-group"><label className="pay-label">Expiry (MM/YY)</label><input className="pay-input" placeholder="09/27" value={form.expiry} onChange={e => setForm(p => ({...p, expiry: e.target.value}))} maxLength={5} /></div>
                  <div className="pay-form-group"><label className="pay-label">CVV</label><input className="pay-input" placeholder="123" type="password" value={form.cvv2} onChange={e => setForm(p => ({...p, cvv2: e.target.value}))} maxLength={4} /></div>
                  <div className="pay-form-group"><label className="pay-label">Billing Address</label><input className="pay-input" placeholder="123 Main St" value={form.address} onChange={e => setForm(p => ({...p, address: e.target.value}))} /></div>
                  <div className="pay-form-group"><label className="pay-label">Billing ZIP</label><input className="pay-input" placeholder="33602" value={form.zip} onChange={e => setForm(p => ({...p, zip: e.target.value}))} maxLength={5} /></div>
                </div>

                {surcharge > 0 && (
                  <div style={{background:"#f8f9fa",borderRadius:8,padding:"12px 16px",marginBottom:12,fontSize:13}}>
                    <div style={{display:"flex",justifyContent:"space-between",color:"#666",marginBottom:4}}><span>Payment amount</span><span>{fmtp(payAmount)}</span></div>
                    <div style={{display:"flex",justifyContent:"space-between",color:"#f5a623"}}><span>Card processing fee ({invoice.surcharge_percent}%)</span><span>{fmtp(surcharge)}</span></div>
                    <div style={{display:"flex",justifyContent:"space-between",fontWeight:700,borderTop:"1px solid #eee",paddingTop:8,marginTop:8}}><span>Total charged to card</span><span>{fmtp(totalCharge)}</span></div>
                  </div>
                )}

                <button className="pay-btn" onClick={() => handlePay(payAmount)} disabled={processing || (payMode==="custom" && (!customAmt || parseFloat(customAmt)<1))}>
                  {processing ? "Processing…" : `Pay ${fmtp(totalCharge)} Securely`}
                </button>
                {surcharge > 0 && <div style={{fontSize:12,color:"#999",textAlign:"center",marginTop:8}}>{invoice.surcharge_disclosure}</div>}
                <div className="pay-security">🔒 Secured by CardPointe (FiServ) · PCI DSS Compliant</div>
              </>;
            })()}
          </div>
        )}
      </>}
    </div></>
  );
}

// ── LANDING PAGE ─────────────────────────────────────────────
const landingCss = `
  @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@700;800&family=DM+Sans:wght@300;400;500;600&display=swap');
  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
  html { scroll-behavior: smooth; }
  body { font-family: 'DM Sans', sans-serif; background: #0a0d12; color: #e8ecf4; }

  /* NAV */
  .lp-nav { position: fixed; top: 0; left: 0; right: 0; z-index: 100; padding: 0 40px; height: 68px; display: flex; align-items: center; justify-content: space-between; background: rgba(10,13,18,0.85); backdrop-filter: blur(12px); border-bottom: 1px solid rgba(255,255,255,0.06); }
  .lp-logo { cursor: pointer; display: flex; align-items: center; }
  .lp-logo img { height: 150px; width: auto; object-fit: contain; }
  .lp-nav-links { display: flex; align-items: center; gap: 8px; }
  .lp-nav-link { color: #8892a4; font-size: 14px; font-weight: 500; cursor: pointer; padding: 8px 16px; border-radius: 8px; transition: color 0.2s; background: none; border: none; font-family: 'DM Sans', sans-serif; }
  .lp-nav-link:hover { color: #e8ecf4; }
  .lp-nav-btn { background: #00d4aa; color: #0a0d12; font-size: 14px; font-weight: 700; padding: 9px 22px; border-radius: 8px; cursor: pointer; border: none; font-family: 'DM Sans', sans-serif; transition: all 0.2s; }
  .lp-nav-btn:hover { background: #00eabb; transform: translateY(-1px); }
  .lp-nav-btn.outline { background: transparent; color: #e8ecf4; border: 1px solid rgba(255,255,255,0.15); margin-right: 4px; }
  .lp-nav-btn.outline:hover { border-color: rgba(255,255,255,0.35); background: rgba(255,255,255,0.05); transform: none; }

  /* HERO */
  .lp-hero { min-height: 100vh; display: flex; align-items: center; justify-content: center; text-align: center; padding: 120px 24px 80px; position: relative; overflow: hidden; }
  .lp-hero::before { content: ''; position: absolute; top: -200px; left: 50%; transform: translateX(-50%); width: 800px; height: 800px; background: radial-gradient(circle, rgba(0,212,170,0.12) 0%, transparent 70%); pointer-events: none; }
  .lp-hero-eyebrow { display: inline-flex; align-items: center; gap: 8px; background: rgba(0,212,170,0.1); border: 1px solid rgba(0,212,170,0.25); color: #00d4aa; font-size: 13px; font-weight: 600; padding: 6px 16px; border-radius: 20px; margin-bottom: 28px; }
  .lp-hero-title { font-family: 'Plus Jakarta Sans', sans-serif; font-size: clamp(42px, 7vw, 76px); font-weight: 800; line-height: 1.05; letter-spacing: -2px; margin-bottom: 24px; }
  .lp-hero-title .accent { color: #00d4aa; }
  .lp-hero-sub { font-size: clamp(17px, 2.5vw, 20px); color: #8892a4; max-width: 560px; margin: 0 auto 44px; line-height: 1.65; font-weight: 400; }
  .lp-hero-btns { display: flex; align-items: center; justify-content: center; gap: 14px; flex-wrap: wrap; margin-bottom: 60px; }
  .lp-btn-primary { background: #00d4aa; color: #0a0d12; font-family: 'Plus Jakarta Sans', sans-serif; font-size: 16px; font-weight: 700; padding: 16px 36px; border-radius: 10px; cursor: pointer; border: none; transition: all 0.2s; display: inline-flex; align-items: center; gap: 8px; }
  .lp-btn-primary:hover { background: #00eabb; transform: translateY(-2px); box-shadow: 0 8px 32px rgba(0,212,170,0.3); }
  .lp-btn-secondary { background: rgba(255,255,255,0.06); color: #e8ecf4; font-family: 'DM Sans', sans-serif; font-size: 16px; font-weight: 600; padding: 16px 36px; border-radius: 10px; cursor: pointer; border: 1px solid rgba(255,255,255,0.1); transition: all 0.2s; }
  .lp-btn-secondary:hover { background: rgba(255,255,255,0.1); border-color: rgba(255,255,255,0.2); }
  .lp-hero-proof { display: flex; align-items: center; justify-content: center; gap: 32px; flex-wrap: wrap; }
  .lp-proof-item { display: flex; align-items: center; gap: 8px; font-size: 13px; color: #6b7590; }
  .lp-proof-item span { color: #00d4aa; font-size: 15px; }

  /* SECTIONS */
  .lp-section { padding: 100px 24px; max-width: 1100px; margin: 0 auto; }
  .lp-section-label { font-size: 12px; font-weight: 700; color: #00d4aa; text-transform: uppercase; letter-spacing: 2px; margin-bottom: 16px; text-align: center; }
  .lp-section-title { font-family: 'Plus Jakarta Sans', sans-serif; font-size: clamp(30px, 4vw, 44px); font-weight: 800; text-align: center; margin-bottom: 16px; letter-spacing: -1px; }
  .lp-section-sub { font-size: 17px; color: #8892a4; text-align: center; max-width: 560px; margin: 0 auto 60px; line-height: 1.65; }
  .lp-divider { border: none; border-top: 1px solid rgba(255,255,255,0.06); margin: 0; }

  /* FEATURES GRID */
  .lp-features { display: grid; grid-template-columns: repeat(3, 1fr); gap: 24px; }
  .lp-feature { background: rgba(255,255,255,0.03); border: 1px solid rgba(255,255,255,0.07); border-radius: 16px; padding: 32px; transition: all 0.3s; }
  .lp-feature:hover { background: rgba(255,255,255,0.05); border-color: rgba(0,212,170,0.2); transform: translateY(-2px); }
  .lp-feature-icon { font-size: 32px; margin-bottom: 16px; }
  .lp-feature-title { font-family: 'Plus Jakarta Sans', sans-serif; font-size: 17px; font-weight: 700; margin-bottom: 10px; }
  .lp-feature-desc { font-size: 14px; color: #8892a4; line-height: 1.65; }

  /* HOW IT WORKS */
  .lp-steps { display: grid; grid-template-columns: repeat(3, 1fr); gap: 0; position: relative; }
  .lp-steps::before { content: ''; position: absolute; top: 32px; left: calc(16.67% + 32px); right: calc(16.67% + 32px); height: 1px; background: linear-gradient(90deg, rgba(0,212,170,0.5), rgba(0,212,170,0.5)); }
  .lp-step { text-align: center; padding: 0 24px; }
  .lp-step-num { width: 64px; height: 64px; border-radius: 50%; background: linear-gradient(135deg, #00d4aa, #4f8ef7); display: flex; align-items: center; justify-content: center; font-family: 'Plus Jakarta Sans', sans-serif; font-size: 22px; font-weight: 800; color: #0a0d12; margin: 0 auto 24px; position: relative; z-index: 1; }
  .lp-step-title { font-family: 'Plus Jakarta Sans', sans-serif; font-size: 18px; font-weight: 700; margin-bottom: 10px; }
  .lp-step-desc { font-size: 14px; color: #8892a4; line-height: 1.65; }

  /* PRICING */
  .lp-pricing { display: grid; grid-template-columns: 1fr 1fr; gap: 24px; max-width: 800px; margin: 0 auto; }
  .lp-price-card { border-radius: 20px; padding: 40px; border: 1px solid rgba(255,255,255,0.08); background: rgba(255,255,255,0.03); }
  .lp-price-card.featured { background: linear-gradient(135deg, rgba(0,212,170,0.12), rgba(79,142,247,0.08)); border-color: rgba(0,212,170,0.3); position: relative; overflow: hidden; }
  .lp-price-card.featured::before { content: 'RECOMMENDED'; position: absolute; top: 20px; right: -28px; background: #00d4aa; color: #0a0d12; font-size: 10px; font-weight: 800; padding: 4px 36px; transform: rotate(45deg); letter-spacing: 1px; }
  .lp-price-label { font-size: 13px; color: #8892a4; font-weight: 600; margin-bottom: 8px; text-transform: uppercase; letter-spacing: 0.5px; }
  .lp-price-name { font-family: 'Plus Jakarta Sans', sans-serif; font-size: 22px; font-weight: 800; margin-bottom: 20px; }
  .lp-price-amount { font-family: 'Plus Jakarta Sans', sans-serif; font-size: 48px; font-weight: 800; color: #00d4aa; line-height: 1; margin-bottom: 4px; }
  .lp-price-amount span { font-size: 18px; color: #8892a4; font-weight: 400; }
  .lp-price-sub { font-size: 13px; color: #8892a4; margin-bottom: 28px; }
  .lp-price-features { list-style: none; display: flex; flex-direction: column; gap: 12px; }
  .lp-price-features li { display: flex; align-items: flex-start; gap: 10px; font-size: 14px; color: #c8d0dc; }
  .lp-price-features li::before { content: '✓'; color: #00d4aa; font-weight: 700; flex-shrink: 0; margin-top: 1px; }
  .lp-price-features li.no::before { content: '✗'; color: #ff4757; }
  .lp-price-features li.no { color: #6b7590; }

  /* COMPARISON */
  .lp-compare-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 16px; }
  .lp-compare-card { background: rgba(255,255,255,0.03); border: 1px solid rgba(255,255,255,0.08); border-radius: 14px; padding: 22px 20px; transition: border-color 0.2s, transform 0.2s; }
  .lp-compare-card:hover { border-color: rgba(0,212,170,0.25); transform: translateY(-2px); }
  .lp-compare-card-feature { font-size: 11px; font-weight: 700; color: #6b7590; text-transform: uppercase; letter-spacing: 0.6px; margin-bottom: 16px; }
  .lp-compare-card-row { display: flex; justify-content: space-between; align-items: center; gap: 8px; }
  .lp-compare-card-row + .lp-compare-card-row { margin-top: 10px; padding-top: 10px; border-top: 1px solid rgba(255,255,255,0.05); }
  .lp-compare-card-brand { font-size: 11px; font-weight: 600; color: #6b7590; flex-shrink: 0; }
  .lp-compare-card-val-us { font-size: 14px; font-weight: 700; color: #00d4aa; text-align: right; }
  .lp-compare-card-val-them { font-size: 14px; font-weight: 600; color: #6b7590; text-align: right; }
  @media (max-width: 900px) { .lp-compare-grid { grid-template-columns: repeat(2, 1fr); } }
  @media (max-width: 480px) { .lp-compare-grid { grid-template-columns: 1fr; } }
  .lp-qbo-cards { display: grid; grid-template-columns: 1fr 1fr 1fr; gap: 20px; margin-bottom: 40px; }
  .lp-qbo-card { background: var(--surface, rgba(255,255,255,0.03)); border: 1px solid rgba(255,255,255,0.08); border-radius: 14px; padding: 28px 24px; }
  .lp-qbo-card-num { width: 32px; height: 32px; border-radius: 50%; background: rgba(0,212,170,0.15); border: 1px solid rgba(0,212,170,0.3); color: #00d4aa; font-family: 'Plus Jakarta Sans', sans-serif; font-size: 14px; font-weight: 700; display: flex; align-items: center; justify-content: center; margin-bottom: 16px; }
  .lp-qbo-card-title { font-family: 'Plus Jakarta Sans', sans-serif; font-size: 16px; font-weight: 700; color: #e8ecf4; margin-bottom: 10px; }
  .lp-qbo-card-desc { font-size: 14px; color: #8892a4; line-height: 1.6; }
  .lp-qbo-flow { display: flex; flex-direction: row; align-items: center; justify-content: center; flex-wrap: nowrap; gap: 0; margin: 0 auto; }
  .lp-qbo-flow-step { background: rgba(255,255,255,0.03); border: 1px solid rgba(255,255,255,0.08); border-radius: 20px; padding: 10px 16px; font-size: 13px; font-weight: 600; color: #e8ecf4; text-align: center; white-space: nowrap; flex-shrink: 0; }
  .lp-qbo-flow-step.accent { background: rgba(0,212,170,0.1); border-color: rgba(0,212,170,0.3); color: #00d4aa; }
  .lp-qbo-flow-arrow { color: #00d4aa; font-size: 16px; padding: 0 6px; flex-shrink: 0; }
  @media (max-width: 768px) {
    .lp-qbo-flow { flex-direction: column; align-items: center; }
    .lp-qbo-flow-step { white-space: normal; width: 200px; }
    .lp-qbo-flow-arrow { transform: rotate(90deg); padding: 2px 0; }
  }
  .lp-biz-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 20px; }
  .lp-biz-card { background: rgba(255,255,255,0.03); border: 1px solid rgba(255,255,255,0.08); border-radius: 14px; padding: 28px 24px; transition: border-color 0.2s, transform 0.2s; }
  .lp-biz-card:hover { border-color: rgba(0,212,170,0.3); transform: translateY(-2px); }
  .lp-biz-icon { font-size: 28px; margin-bottom: 14px; line-height: 1; }
  .lp-biz-title { font-family: 'Plus Jakarta Sans', sans-serif; font-size: 15px; font-weight: 700; color: #e8ecf4; margin-bottom: 8px; }
  .lp-biz-desc { font-size: 13px; color: #8892a4; line-height: 1.6; }
  @media (max-width: 900px) { .lp-biz-grid { grid-template-columns: repeat(2, 1fr); } }
  .lp-trust-panel { background: rgba(255,255,255,0.02); border: 1px solid rgba(255,255,255,0.08); border-radius: 16px; padding: 40px; }
  .lp-trust-points { display: grid; grid-template-columns: repeat(3, 1fr); gap: 32px; margin-bottom: 32px; }
  .lp-trust-point-icon { font-size: 22px; margin-bottom: 10px; }
  .lp-trust-point-title { font-family: 'Plus Jakarta Sans', sans-serif; font-size: 14px; font-weight: 700; color: #00d4aa; margin-bottom: 8px; text-transform: uppercase; letter-spacing: 0.4px; }
  .lp-trust-point-desc { font-size: 14px; color: #8892a4; line-height: 1.65; }
  .lp-trust-closing { border-top: 1px solid rgba(255,255,255,0.07); padding-top: 24px; text-align: center; font-family: 'Plus Jakarta Sans', sans-serif; font-size: 18px; font-weight: 700; color: #e8ecf4; }
  .lp-trust-closing span { color: #00d4aa; }
  @media (max-width: 700px) { .lp-trust-points { grid-template-columns: 1fr; gap: 24px; } .lp-trust-panel { padding: 28px 20px; } }
  @media (max-width: 560px) { .lp-biz-grid { grid-template-columns: 1fr; } }
  @media (max-width: 640px) {
    .lp-qbo-cards { grid-template-columns: 1fr; }
  }
  .lp-calc { max-width: 860px; margin: 0 auto 56px; }
  .lp-calc-inputs { display: grid; grid-template-columns: 1fr 1fr; gap: 20px; margin-bottom: 28px; }
  .lp-calc-field { display: flex; flex-direction: column; gap: 8px; }
  .lp-calc-field label { font-size: 13px; font-weight: 600; color: #8892a4; text-transform: uppercase; letter-spacing: 0.5px; }
  .lp-calc-field input { background: rgba(255,255,255,0.04); border: 1px solid rgba(255,255,255,0.1); border-radius: 10px; padding: 14px 16px; font-size: 18px; font-family: 'Plus Jakarta Sans', sans-serif; font-weight: 600; color: #e8ecf4; outline: none; transition: border-color 0.2s; }
  .lp-calc-field input:focus { border-color: #00d4aa; }
  .lp-calc-results { display: grid; grid-template-columns: 1fr 1fr 1fr; gap: 16px; }
  .lp-calc-card { background: rgba(255,255,255,0.03); border: 1px solid rgba(255,255,255,0.08); border-radius: 14px; padding: 24px; text-align: center; }
  .lp-calc-card.savings { background: linear-gradient(135deg, rgba(0,212,170,0.12), rgba(0,212,170,0.06)); border-color: rgba(0,212,170,0.35); }
  .lp-calc-card-label { font-size: 12px; font-weight: 600; color: #8892a4; text-transform: uppercase; letter-spacing: 0.5px; margin-bottom: 12px; }
  .lp-calc-card.savings .lp-calc-card-label { color: #00d4aa; }
  .lp-calc-card-amount { font-family: 'Plus Jakarta Sans', sans-serif; font-size: 36px; font-weight: 800; color: #e8ecf4; line-height: 1; }
  .lp-calc-card.savings .lp-calc-card-amount { color: #00d4aa; }
  .lp-calc-card-sub { font-size: 12px; color: #6b7590; margin-top: 6px; }
  @media (max-width: 640px) {
    .lp-calc-inputs { grid-template-columns: 1fr; }
    .lp-calc-results { grid-template-columns: 1fr; }
    .lp-calc-card-amount { font-size: 28px; }
  }

  /* FINAL CTA */
  .lp-cta { text-align: center; padding: 100px 24px; background: radial-gradient(ellipse at center, rgba(0,212,170,0.08) 0%, transparent 70%); }
  .lp-cta-title { font-family: 'Plus Jakarta Sans', sans-serif; font-size: clamp(32px, 5vw, 52px); font-weight: 800; margin-bottom: 20px; letter-spacing: -1.5px; }
  .lp-cta-sub { font-size: 18px; color: #8892a4; margin-bottom: 40px; }

  /* FOOTER */
  .lp-footer { border-top: 1px solid rgba(255,255,255,0.06); padding: 32px 40px; display: flex; justify-content: space-between; align-items: center; font-size: 13px; color: #6b7590; }

  @media (max-width: 768px) {
    .lp-features { grid-template-columns: 1fr; }
    .lp-steps { grid-template-columns: 1fr; }
    .lp-steps::before { display: none; }
    .lp-pricing { grid-template-columns: 1fr; }
    .lp-nav { padding: 0 20px; }
    .lp-nav-links .lp-nav-link { display: none; }
  }
`;

function SavingsCalculator() {
  const [volume, setVolume] = useState(10000);
  const [avgInvoice, setAvgInvoice] = useState(500);

  const GAP_RATE = 0.0275;
  const QBO_RATE = 0.029;
  const QBO_FEE  = 30;

  const qboAnnual = (volume * QBO_RATE * 12) + (QBO_FEE * 12);
  const gapAnnual = (volume * GAP_RATE * 12);
  const savings   = qboAnnual - gapAnnual;

  const fmt = (n) => "$" + Math.round(n).toLocaleString("en-US");

  return (
    <div className="lp-calc">
      <div className="lp-calc-inputs">
        <div className="lp-calc-field">
          <label>Monthly Card Volume</label>
          <input
            type="number"
            min="0"
            step="500"
            value={volume}
            onChange={e => setVolume(parseFloat(e.target.value) || 0)}
          />
        </div>
        <div className="lp-calc-field">
          <label>Average Invoice Amount</label>
          <input
            type="number"
            min="0"
            step="50"
            value={avgInvoice}
            onChange={e => setAvgInvoice(parseFloat(e.target.value) || 0)}
          />
        </div>
      </div>
      <div className="lp-calc-results">
        <div className="lp-calc-card">
          <div className="lp-calc-card-label">QuickBooks Annual Cost</div>
          <div className="lp-calc-card-amount">{fmt(qboAnnual)}</div>
          <div className="lp-calc-card-sub">2.9% + $30/mo software fee</div>
        </div>
        <div className="lp-calc-card">
          <div className="lp-calc-card-label">GoApexPay Annual Cost</div>
          <div className="lp-calc-card-amount">{fmt(gapAnnual)}</div>
          <div className="lp-calc-card-sub">2.75% · no monthly fee</div>
        </div>
        <div className="lp-calc-card savings">
          <div className="lp-calc-card-label">Estimated Annual Savings</div>
          <div className="lp-calc-card-amount">{fmt(savings)}</div>
          <div className="lp-calc-card-sub">switching to GoApexPay</div>
        </div>
      </div>
    </div>
  );
}

function LandingPage({ onGetStarted, onLogin }) {
  return (
    <>
      <style>{landingCss}</style>

      {/* NAV */}
      <nav className="lp-nav">
        <div className="lp-logo"><img src={require("./logo.png")} alt="GoApexPay" /></div>
        <div className="lp-nav-links">
          <a href="#features" className="lp-nav-link" onClick={e => { e.preventDefault(); document.getElementById('features')?.scrollIntoView({behavior:'smooth'}); }}>Features</a>
          <a href="#how" className="lp-nav-link" onClick={e => { e.preventDefault(); document.getElementById('how')?.scrollIntoView({behavior:'smooth'}); }}>How It Works</a>
          <a href="#pricing" className="lp-nav-link" onClick={e => { e.preventDefault(); document.getElementById('pricing')?.scrollIntoView({behavior:'smooth'}); }}>Pricing</a>
          <button className="lp-nav-btn outline" onClick={onLogin}>Log In</button>
          <button className="lp-nav-btn" onClick={onGetStarted}>Sign Up Free</button>
        </div>
      </nav>

      {/* HERO */}
      <section className="lp-hero">
        <div>
          <div className="lp-hero-eyebrow"><span>✦</span> Free invoicing software + lower processing rates</div>
          <h1 className="lp-hero-title">
            Stop paying QuickBooks<br />
            to get <span className="accent">paid.</span>
          </h1>
          <p className="lp-hero-sub">
            Free invoicing software, lower processing rates, e-signature estimates, and optional surcharging. Everything you need to invoice clients and collect payments without monthly software fees.
          </p>
          <div className="lp-hero-btns">
            <button className="lp-btn-primary" onClick={onGetStarted}>Sign Up Free →</button>
            <button className="lp-btn-secondary" onClick={onLogin}>Log In to Your Account</button>
          </div>
          <div className="lp-hero-proof">
            <div className="lp-proof-item"><span>✓</span> No monthly software fee</div>
            <div className="lp-proof-item"><span>✓</span> 2.75% flat processing rate</div>
            <div className="lp-proof-item"><span>✓</span> Lower than QuickBooks Payments</div>
            <div className="lp-proof-item"><span>✓</span> QuickBooks sync included</div>
          </div>
        </div>
      </section>


      <hr className="lp-divider" />

      {/* PRICING */}
      <section id="pricing">
        <div className="lp-section">
          <div className="lp-section-label">Pricing</div>
          <h2 className="lp-section-title">Simple, transparent pricing</h2>
          <p className="lp-section-sub">The software is free. You only pay when you process a card payment — and your customers can cover that too.</p>

          <div className="lp-pricing" style={{marginBottom: 48}}>
            <div className="lp-price-card featured">
              <div className="lp-price-label">GoApexPay</div>
              <div className="lp-price-name">Free + Processing</div>
              <div className="lp-price-amount">$0<span>/mo</span></div>
              <div className="lp-price-sub">+ 2.75% per card transaction</div>
              <ul className="lp-price-features">
                <li>Unlimited invoices & estimates</li>
                <li>E-signature estimates</li>
                <li>Online payment portal</li>
                <li>Pass fees to customers (surcharging)</li>
                <li>Deposit & progress billing</li>
                <li>QuickBooks Online sync</li>
                <li>Team members included</li>
                <li>No setup fees, no contracts</li>
              </ul>
            </div>
            <div className="lp-price-card">
              <div className="lp-price-label">QuickBooks Payments</div>
              <div className="lp-price-name">QBO + Payments</div>
              <div className="lp-price-amount" style={{color:"#6b7590"}}>$30<span>/mo</span></div>
              <div className="lp-price-sub">+ 2.9% per invoiced card transaction</div>
              <ul className="lp-price-features">
                <li>Basic invoicing</li>
                <li>No e-signature estimates</li>
                <li>Online payment portal</li>
                <li className="no">Cannot pass fees to customers</li>
                <li className="no">No deposit billing</li>
                <li>QuickBooks sync (native)</li>
                <li>Team members (paid add-on)</li>
                <li className="no">Monthly subscription required</li>
              </ul>
            </div>
          </div>

          {/* Savings Calculator */}
          <div style={{marginBottom: 56}}>
            <div className="lp-section-label" style={{textAlign:"center",marginBottom:8}}>Savings Calculator</div>
            <h3 className="lp-section-title" style={{textAlign:"center",marginBottom:8}}>How Much Could You Save?</h3>
            <p className="lp-section-sub" style={{textAlign:"center",marginBottom:32}}>Enter your estimated monthly card volume and see the difference.</p>
            <SavingsCalculator />
          </div>
        </div>
      </section>

      <hr className="lp-divider" />

      <section>
        <div className="lp-section">
            {/* QBO Integration Section */}
            <div style={{marginBottom: 56}}>
              <h3 className="lp-section-title" style={{textAlign:"center",marginBottom:8}}>Already Using QuickBooks Online? <span style={{color:"#00d4aa"}}>Perfect.</span></h3>
              <p className="lp-section-sub" style={{textAlign:"center",marginBottom:40}}>GoApexPay works alongside QuickBooks so you don't have to change your accounting workflow.</p>
              <div className="lp-qbo-cards">
                <div className="lp-qbo-card">
                  <div className="lp-qbo-card-num">1</div>
                  <div className="lp-qbo-card-title">Import Existing Customers</div>
                  <div className="lp-qbo-card-desc">Pull your existing customers and invoice history from QuickBooks Online into GoApexPay.</div>
                </div>
                <div className="lp-qbo-card">
                  <div className="lp-qbo-card-num">2</div>
                  <div className="lp-qbo-card-title">Send Invoices &amp; Collect Payments</div>
                  <div className="lp-qbo-card-desc">Create invoices, send estimates, collect payments, and manage customer billing inside GoApexPay.</div>
                </div>
                <div className="lp-qbo-card">
                  <div className="lp-qbo-card-num">3</div>
                  <div className="lp-qbo-card-title">Sync Back to QuickBooks</div>
                  <div className="lp-qbo-card-desc">Payments and invoice activity automatically sync back to QuickBooks so your books stay accurate.</div>
                </div>
              </div>
              <div className="lp-qbo-flow">
                <div className="lp-qbo-flow-step accent">QuickBooks Online</div>
                <div className="lp-qbo-flow-arrow">→</div>
                <div className="lp-qbo-flow-step">Import Customers</div>
                <div className="lp-qbo-flow-arrow">→</div>
                <div className="lp-qbo-flow-step">Create Invoice in GoApexPay</div>
                <div className="lp-qbo-flow-arrow">→</div>
                <div className="lp-qbo-flow-step">Customer Pays</div>
                <div className="lp-qbo-flow-arrow">→</div>
                <div className="lp-qbo-flow-step">Payment Syncs Back</div>
                <div className="lp-qbo-flow-arrow">→</div>
                <div className="lp-qbo-flow-step accent">QuickBooks Updated ✓</div>
              </div>
            </div>
        </div>
      </section>

      <hr className="lp-divider" />

      <section id="pricing-compare">
        <div className="lp-section">
          {/* Comparison Cards */}
          <div className="lp-section-label" style={{textAlign:"center",marginBottom:8}}>GoApexPay vs QuickBooks Payments</div>
          <h3 className="lp-section-title" style={{textAlign:"center",marginBottom:8}}>See How We Compare</h3>
          <p className="lp-section-sub" style={{textAlign:"center",marginBottom:32}}>Every feature, side by side.</p>
          <div className="lp-compare-grid">
            {[
              ["Monthly Software Fee",   "Free",    "$30+/mo"],
              ["Card Processing Rate",   "2.75%",   "2.9%"],
              ["Per-Transaction Fee",    "None",    "Varies"],
              ["Pass Fees to Customers", "✓ Yes",   "✗ No"],
              ["E-Signature Estimates",  "✓ Yes",   "✗ No"],
              ["Auto Invoice on Signing","✓ Yes",   "✗ No"],
              ["Deposit Billing",        "✓ Yes",   "✗ No"],
              ["QuickBooks Sync",        "✓ Yes",   "✓ Yes"],
            ].map(([feat, us, them], i) => (
              <div key={i} className="lp-compare-card">
                <div className="lp-compare-card-feature">{feat}</div>
                <div className="lp-compare-card-row">
                  <span className="lp-compare-card-brand">GoApexPay</span>
                  <span className="lp-compare-card-val-us">{us}</span>
                </div>
                <div className="lp-compare-card-row">
                  <span className="lp-compare-card-brand">QuickBooks</span>
                  <span className="lp-compare-card-val-them">{them}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <hr className="lp-divider" />

      <section>
        <div className="lp-section">
            {/* Built For Section */}
            <div style={{marginBottom: 56}}>
              <h3 className="lp-section-title" style={{textAlign:"center",marginBottom:8}}>Built for Service Businesses That Need to Get Paid</h3>
              <p className="lp-section-sub" style={{textAlign:"center",marginBottom:40}}>Whether you send one invoice a month or manage deposits, estimates, and progress billing, GoApexPay gives you a cleaner way to collect.</p>
              <div className="lp-biz-grid">
                {[
                  { icon: "🏗️", title: "Contractors", desc: "Send estimates, collect deposits, and bill by milestone as projects move forward." },
                  { icon: "📊", title: "Accountants & Bookkeepers", desc: "Invoice clients professionally while keeping QuickBooks Online connected." },
                  { icon: "💼", title: "Consultants", desc: "Send clean invoices, collect retainers, and get paid without chasing checks." },
                  { icon: "⚖️", title: "Law Firms", desc: "Bill clients, accept card payments, and keep payment records organized." },
                  { icon: "🔧", title: "Home Services", desc: "Turn approved estimates into invoices and collect payments from the field." },
                  { icon: "🎨", title: "Agencies & Creatives", desc: "Manage project billing, deposits, and client payments in one simple workflow." },
                ].map((biz, i) => (
                  <div key={i} className="lp-biz-card">
                    <div className="lp-biz-icon">{biz.icon}</div>
                    <div className="lp-biz-title">{biz.title}</div>
                    <div className="lp-biz-desc">{biz.desc}</div>
                  </div>
                ))}
              </div>
            </div>
        </div>
      </section>

      <hr className="lp-divider" />

      <section>
        <div className="lp-section">
            {/* Trust / Credibility Section */}
            <div style={{marginBottom: 56}}>
              <h3 className="lp-section-title" style={{textAlign:"center",marginBottom:8}}>Built by People Who Work With Small Businesses Every Day</h3>
              <p className="lp-section-sub" style={{textAlign:"center",marginBottom:32}}>Developed by accounting, payroll, and payment processing professionals who work with thousands of small business clients every year.</p>
              <div className="lp-trust-panel">
                <div className="lp-trust-points">
                  <div>
                    <div className="lp-trust-point-icon">📒</div>
                    <div className="lp-trust-point-title">Accounting Experience</div>
                    <div className="lp-trust-point-desc">Designed with the realities of invoicing, bookkeeping, and QuickBooks workflows in mind.</div>
                  </div>
                  <div>
                    <div className="lp-trust-point-icon">💳</div>
                    <div className="lp-trust-point-title">Payment Processing Knowledge</div>
                    <div className="lp-trust-point-desc">Built around the merchant services model so businesses can collect payments without unnecessary software fees.</div>
                  </div>
                  <div>
                    <div className="lp-trust-point-icon">🏢</div>
                    <div className="lp-trust-point-title">Small Business Focus</div>
                    <div className="lp-trust-point-desc">Created for contractors, professionals, and service businesses that need simple tools and faster collections.</div>
                  </div>
                </div>
                <p style={{textAlign:"center",fontSize:"14px",color:"#8892a4",marginBottom:"20px",fontStyle:"italic"}}>Built from real client workflows—not software industry assumptions.</p>
              <div className="lp-trust-closing">Keep QuickBooks. <span>Replace QuickBooks Payments.</span></div>
              </div>
            </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="lp-cta">
        <h2 className="lp-cta-title">Ready to get paid faster?</h2>
        <p className="lp-cta-sub">Join thousands of businesses sending smarter invoices. Free forever.</p>
        <button className="lp-btn-primary" onClick={onGetStarted} style={{fontSize:18,padding:"18px 48px"}}>
          Create Your Free Account →
        </button>
        <div style={{marginTop:20,fontSize:13,color:"#6b7590"}}>No credit card required · Setup takes under 2 minutes</div>
      </section>

      {/* FOOTER */}
      <footer className="lp-footer">
        <div className="lp-logo"><img src={require("./logo.png")} alt="GoApexPay" style={{height:"150px"}} /></div>
        <div>© 2026 GoApexPay · Powered by CardPointe (FiServ)</div>
        <div style={{display:"flex",gap:20}}>
          <a href="/privacy" style={{color:"#6b7590",textDecoration:"none",cursor:"pointer"}} onClick={e=>{e.preventDefault();window.location.href="/privacy"}}>Privacy Policy</a>
          <a href="/terms" style={{color:"#6b7590",textDecoration:"none",cursor:"pointer"}} onClick={e=>{e.preventDefault();window.location.href="/terms"}}>Terms of Service</a>
          <span style={{cursor:"pointer"}} onClick={onLogin}>Log In</span>
          <span style={{cursor:"pointer",color:"#00d4aa"}} onClick={onGetStarted}>Sign Up Free</span>
        </div>
      </footer>
    </>
  );
}

// ── LOGIN + SIGNUP ───────────────────────────────────────────
function LoginScreen({ onLogin, initialMode = "login", onBack }) {
  const [mode, setMode] = useState(initialMode);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [loginEmail, setLoginEmail] = useState("");
  const [loginPassword, setLoginPassword] = useState("");
  const [signupCompany, setSignupCompany] = useState("");
  const [signupName, setSignupName] = useState("");
  const [signupEmail, setSignupEmail] = useState("");
  const [signupPassword, setSignupPassword] = useState("");
  const [signupConfirm, setSignupConfirm] = useState("");
  const [agreeTerms, setAgreeTerms] = useState(false);
  const [agreeMarketing, setAgreeMarketing] = useState(true);

  const handleLogin = async (e) => {
    e.preventDefault(); setLoading(true); setError("");
    try {
      const d = await api.post("/auth/login", { email: loginEmail, password: loginPassword });
      api.setToken(d.token); onLogin(d.user, d.company);
    } catch (err) { setError(err.message); }
    finally { setLoading(false); }
  };

  const handleSignup = async (e) => {
    e.preventDefault(); setError("");
    if (!agreeTerms) return setError("You must agree to the Terms of Service and Privacy Policy to create an account");
    if (signupPassword !== signupConfirm) return setError("Passwords do not match");
    if (signupPassword.length < 8) return setError("Password must be at least 8 characters");
    setLoading(true);
    try {
      const d = await api.post("/auth/signup", { company_name: signupCompany, name: signupName, email: signupEmail, password: signupPassword, marketing_consent: agreeMarketing });
      api.setToken(d.token); onLogin(d.user, d.company);
    } catch (err) { setError(err.message); }
    finally { setLoading(false); }
  };

  return (
    <div className="login-screen">
      <div className="login-card">
        <div className="login-logo"><img src={require("./logo.png")} alt="GoApexPay" /></div>
        <div className="login-sub">{mode === "login" ? "Sign in to your account" : "Create your free account"}</div>
        {onBack && (
          <button onClick={onBack} style={{background:"none",border:"none",color:"var(--muted)",fontSize:13,cursor:"pointer",padding:"0 0 16px",display:"flex",alignItems:"center",gap:4}}>
            ← Back to home
          </button>
        )}
        <div className="login-tabs">
          <div className={`login-tab ${mode === "login" ? "active" : ""}`} onClick={() => { setMode("login"); setError(""); }}>Sign In</div>
          <div className={`login-tab ${mode === "signup" ? "active" : ""}`} onClick={() => { setMode("signup"); setError(""); }}>Create Account</div>
        </div>
        {error && <div className="login-error">{error}</div>}
        {mode === "login" ? (
          <form onSubmit={handleLogin}>
            <div className="form-group" style={{ marginBottom: 14 }}><label>Email</label><input type="email" value={loginEmail} onChange={e => setLoginEmail(e.target.value)} placeholder="you@company.com" required /></div>
            <div className="form-group" style={{ marginBottom: 24 }}><label>Password</label><input type="password" value={loginPassword} onChange={e => setLoginPassword(e.target.value)} placeholder="••••••••" required /></div>
            <button className="btn btn-primary" type="submit" disabled={loading} style={{ width: "100%", justifyContent: "center" }}>{loading ? "Signing in…" : "Sign In"}</button>
          </form>
        ) : (
          <form onSubmit={handleSignup}>
            <div className="form-group" style={{ marginBottom: 14 }}><label>Business Name *</label><input value={signupCompany} onChange={e => setSignupCompany(e.target.value)} placeholder="Acme Landscaping LLC" required /></div>
            <div className="form-group" style={{ marginBottom: 14 }}><label>Your Name *</label><input value={signupName} onChange={e => setSignupName(e.target.value)} placeholder="Jane Smith" required /></div>
            <div className="form-group" style={{ marginBottom: 14 }}><label>Email *</label><input type="email" value={signupEmail} onChange={e => setSignupEmail(e.target.value)} placeholder="jane@acme.com" required /></div>
            <div className="form-group" style={{ marginBottom: 14 }}><label>Password *</label><input type="password" value={signupPassword} onChange={e => setSignupPassword(e.target.value)} placeholder="Min 8 characters" required /></div>
            <div className="form-group" style={{ marginBottom: 16 }}><label>Confirm Password *</label><input type="password" value={signupConfirm} onChange={e => setSignupConfirm(e.target.value)} placeholder="Repeat password" required /></div>

            {/* Terms checkbox — required */}
            <div style={{ display: "flex", alignItems: "flex-start", gap: 10, marginBottom: 12, padding: "12px 0", borderTop: "1px solid var(--border)" }}>
              <input type="checkbox" id="agree-terms" checked={agreeTerms} onChange={e => setAgreeTerms(e.target.checked)} style={{ width: 16, height: 16, marginTop: 2, cursor: "pointer", flexShrink: 0 }} />
              <label htmlFor="agree-terms" style={{ fontSize: 13, color: "var(--muted)", cursor: "pointer", lineHeight: 1.5 }}>
                I agree to the <a href="/terms" target="_blank" rel="noopener noreferrer" style={{ color: "var(--accent)", textDecoration: "none" }}>Terms of Service</a> and <a href="/privacy" target="_blank" rel="noopener noreferrer" style={{ color: "var(--accent)", textDecoration: "none" }}>Privacy Policy</a> *
              </label>
            </div>

            {/* Marketing checkbox — optional, pre-checked */}
            <div style={{ display: "flex", alignItems: "flex-start", gap: 10, marginBottom: 24 }}>
              <input type="checkbox" id="agree-marketing" checked={agreeMarketing} onChange={e => setAgreeMarketing(e.target.checked)} style={{ width: 16, height: 16, marginTop: 2, cursor: "pointer", flexShrink: 0 }} />
              <label htmlFor="agree-marketing" style={{ fontSize: 13, color: "var(--muted)", cursor: "pointer", lineHeight: 1.5 }}>
                I'd like to receive occasional updates about business services that may benefit me, including payroll and bookkeeping services. <span style={{ color: "var(--muted)", fontSize: 12 }}>(Optional)</span>
              </label>
            </div>
            <button className="btn btn-primary" type="submit" disabled={loading} style={{ width: "100%", justifyContent: "center" }}>{loading ? "Creating account…" : "Create Account & Sign In"}</button>
            <div className="info-box green" style={{ marginTop: 16 }}>Your data is completely separate from all other accounts. Connect your own QuickBooks and CardPointe after signing up.</div>
          </form>
        )}
      </div>
    </div>
  );
}

// ── INVOICE / ESTIMATE FORM ──────────────────────────────────
function InvoiceFormModal({ type, clients, items = [], onSave, onClose, showToast, onNewItem, qboConnected }) {
  const [form, setForm] = useState({ client_id: "", due_date: addDays(30), po_number: "", tax_rate: 0, notes: "", line_items: [{ description: "", quantity: 1, unit_price: 0, taxable: false }], deposit_required: false, deposit_percent: 33, billing_type: "standard", billing_schedule: [{ name: "Deposit", percent: 33 }, { name: "Completion", percent: 67 }] });
  const [saving, setSaving] = useState(false);
  const [showingNewItem, setShowingNewItem] = useState(false);
  const sub = form.line_items.reduce((s, l) => s + (parseFloat(l.quantity)||0) * (parseFloat(l.unit_price)||0), 0);
  const taxableSub = form.line_items.reduce((s, l) => l.taxable ? s + (parseFloat(l.quantity)||0) * (parseFloat(l.unit_price)||0) : s, 0);
  const tax = taxableSub * ((parseFloat(form.tax_rate)||0) / 100);
  const total = sub + tax;
  const depositAmt = form.deposit_required ? Math.round(total * (form.deposit_percent / 100) * 100) / 100 : 0;
  const completionAmt = form.deposit_required ? Math.round((total - depositAmt) * 100) / 100 : 0;
  const hasTaxableItems = form.line_items.some(l => l.taxable);

  const updateLine = (i, f, v) => {
    const lines = [...form.line_items];
    lines[i] = { ...lines[i], [f]: f === "quantity" || f === "unit_price" ? parseFloat(v) || 0 : v };
    setForm(p => ({ ...p, line_items: lines }));
  };

  const pickItem = (i, itemId) => {
    if (!itemId) return;
    const item = items.find(it => it.id === itemId);
    if (!item) return;
    const lines = [...form.line_items];
    lines[i] = { ...lines[i], description: item.description || item.name, unit_price: item.unit_price, taxable: !!item.taxable };
    setForm(p => ({ ...p, line_items: lines }));
  };

  const handleSave = async () => {
    if (!form.client_id) return showToast("Please select a client", "error");
    if (!form.line_items.some(l => l.description)) return showToast("Add at least one line item", "error");
    setSaving(true);
    try {
      const result = await api.post(type === "estimate" ? "/estimates" : "/invoices", { ...form, tax_rate: parseFloat(form.tax_rate) || 0 });
      onSave(result); showToast(`${type === "estimate" ? "Estimate" : "Invoice"} ${result.id} created!`); onClose();
    } catch (err) { showToast(err.message, "error"); }
    finally { setSaving(false); }
  };

  const handleSaveAndSend = async () => {
    if (!form.client_id) return showToast("Please select a client", "error");
    if (!form.line_items.some(l => l.description)) return showToast("Add at least one line item", "error");
    setSaving(true);
    try {
      const result = await api.post("/estimates", { ...form, tax_rate: parseFloat(form.tax_rate) || 0 });
      onSave(result);
      // Now send it immediately
      const sendResult = await api.post(`/estimates/${result.id}/send`);
      showToast(`✅ Estimate ${result.id} saved and emailed to ${sendResult.sentTo || "client"}!`);
      onClose();
    } catch (err) { showToast(err.message, "error"); }
    finally { setSaving(false); }
  };

  const handleSaveAndSendInvoice = async () => {
    if (!form.client_id) return showToast("Please select a client", "error");
    if (!form.line_items.some(l => l.description)) return showToast("Add at least one line item", "error");
    setSaving(true);
    try {
      const result = await api.post("/invoices", { ...form, tax_rate: parseFloat(form.tax_rate) || 0 });
      onSave(result);
      // Send the invoice immediately
      const sendResult = await api.post(`/invoices/${result.id}/send`);
      showToast(`✅ Invoice ${result.id} saved and emailed to ${sendResult.sentTo || "client"}!`);
      onClose();
    } catch (err) { showToast(err.message, "error"); }
    finally { setSaving(false); }
  };

  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal modal-wide">
        {showingNewItem && (
          <NewItemModal
            onSave={newItem => {
              items.push(newItem);
              setForm(p => ({ ...p, line_items: [...p.line_items, { description: newItem.description || newItem.name, quantity: 1, unit_price: newItem.unit_price, taxable: !!newItem.taxable }] }));
              setShowingNewItem(false);
            }}
            onClose={() => setShowingNewItem(false)}
            showToast={showToast}
          />
        )}
        <div className="modal-head"><div className="modal-title">New {type === "estimate" ? "Estimate" : "Invoice"}</div><button className="close-btn" onClick={onClose}>×</button></div>
        <div className="modal-body">
          <div className="form-grid" style={{ marginBottom: 20 }}>
            <div className="form-group"><label>Client *</label><select value={form.client_id} onChange={e => setForm(p => ({ ...p, client_id: e.target.value }))}><option value="">Select client…</option>{clients.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}</select></div>
            <div className="form-group"><label>{type === "estimate" ? "Expiry Date" : "Due Date"}</label><input type="date" value={form.due_date} onChange={e => setForm(p => ({ ...p, due_date: e.target.value }))} /></div>
            <div className="form-group"><label>PO Number</label><input placeholder="Optional" value={form.po_number} onChange={e => setForm(p => ({ ...p, po_number: e.target.value }))} /></div>
            <div className="form-group"><label>Tax Rate (%)</label><input type="number" min="0" step="0.1" value={form.tax_rate} onChange={e => setForm(p => ({ ...p, tax_rate: e.target.value }))} /></div>
          </div>

          <div>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
              <label>Line Items</label>
              <div style={{ display: "flex", gap: 8 }}>
                <button className="btn btn-secondary btn-sm" onClick={onNewItem}>+ New Item</button>
                <button className="btn btn-secondary btn-sm" onClick={() => setForm(p => ({ ...p, line_items: [...p.line_items, { description: "", quantity: 1, unit_price: 0, taxable: false }] }))}>+ Add Line</button>
              </div>
            </div>

            {/* Column headers */}
            <div style={{ display: "grid", gridTemplateColumns: items.length ? "160px 1fr 80px 110px 50px 24px" : "1fr 80px 110px 50px 24px", gap: 8, marginBottom: 6 }}>
              {items.length > 0 && <label>Item</label>}
              <label>Description</label><label>Qty</label><label>Unit Price</label><label style={{fontSize:10}}>Tax?</label><div />
            </div>

            {form.line_items.map((line, i) => (
              <div key={i} style={{ display: "grid", gridTemplateColumns: items.length ? "160px 1fr 80px 110px 50px 24px" : "1fr 80px 110px 50px 24px", gap: 8, alignItems: "center", marginBottom: 8 }}>
                {items.length > 0 && (
                  <select style={{ fontSize: 12 }} value="" onChange={e => pickItem(i, e.target.value)}>
                    <option value="">Pick item…</option>
                    {items.map(it => <option key={it.id} value={it.id}>{it.name} — ${parseFloat(it.unit_price).toFixed(2)}{it.taxable ? " 🔶" : ""}</option>)}
                  </select>
                )}
                <input placeholder="Description…" value={line.description} onChange={e => updateLine(i, "description", e.target.value)} />
                <input type="number" min="0.01" step="any" value={line.quantity} onChange={e => updateLine(i, "quantity", e.target.value)} />
                <input type="number" min="0" step="0.01" value={line.unit_price} onChange={e => updateLine(i, "unit_price", e.target.value)} />
                <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 2 }}>
                  <input type="checkbox" checked={!!line.taxable} onChange={e => updateLine(i, "taxable", e.target.checked)} style={{ width: 16, height: 16, cursor: "pointer" }} />
                  {line.taxable && <span style={{ fontSize: 9, color: "var(--pending)" }}>TAX</span>}
                </div>
                <button className="remove-btn" onClick={() => setForm(p => ({ ...p, line_items: p.line_items.filter((_, idx) => idx !== i) }))} disabled={form.line_items.length === 1}>×</button>
              </div>
            ))}
          </div>

          <div className="totals-box">
            <div className="total-row"><span>Subtotal</span><span>{fmt(sub)}</span></div>
            {hasTaxableItems && taxableSub < sub && <div className="total-row" style={{fontSize:12,color:"var(--muted)"}}><span>Taxable portion</span><span>{fmt(taxableSub)}</span></div>}
            <div className="total-row"><span>Tax ({form.tax_rate}%{hasTaxableItems && taxableSub < sub ? " on taxable items" : ""})</span><span>{fmt(tax)}</span></div>
            <div className="total-row grand"><span>Total</span><span>{fmt(total)}</span></div>
            {form.deposit_required && (
              <>
                <div className="total-row" style={{ marginTop: 8, color: "var(--pending)", fontWeight: 600 }}>
                  <span>Deposit Due ({form.deposit_percent}%)</span><span>{fmt(depositAmt)}</span>
                </div>
                <div className="total-row" style={{ color: "var(--muted)" }}>
                  <span>Balance on Completion</span><span>{fmt(completionAmt)}</span>
                </div>
              </>
            )}
          </div>

          {/* Progress Billing section — only show for estimates */}
          {type === "estimate" && (
            <div style={{ background: "var(--surface2)", borderRadius: "var(--radius)", padding: "16px 20px", marginTop: 12, border: "1px solid var(--border)" }}>
              <div style={{ marginBottom: 14 }}>
                <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 4 }}>Payment Schedule</div>
                <div style={{ fontSize: 12, color: "var(--muted)", marginBottom: 12 }}>Choose how you want to bill this job</div>
                <div style={{ display: "flex", gap: 8 }}>
                  {[
                    { v: "standard", label: "Full amount on completion" },
                    { v: "deposit", label: "Deposit + final" },
                    { v: "progress", label: "Progress billing" },
                  ].map(opt => (
                    <button key={opt.v} onClick={() => setForm(p => ({ ...p, billing_type: opt.v }))}
                      style={{ padding: "7px 14px", borderRadius: 8, fontSize: 13, cursor: "pointer", fontFamily: "var(--font-body)", fontWeight: form.billing_type === opt.v ? 700 : 400, background: form.billing_type === opt.v ? "var(--accent)" : "var(--surface)", color: form.billing_type === opt.v ? "#0a0d12" : "var(--muted)", border: `1px solid ${form.billing_type === opt.v ? "var(--accent)" : "var(--border)"}`, transition: "all 0.2s" }}>
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Simple deposit */}
              {form.billing_type === "deposit" && (
                <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                  <div className="form-group" style={{ flex: 1, marginBottom: 0 }}>
                    <label>Deposit Percentage</label>
                    <input type="number" min="1" max="99" value={form.deposit_percent} onChange={e => setForm(p => ({ ...p, deposit_percent: parseFloat(e.target.value) || 33 }))} />
                  </div>
                  <div style={{ flex: 1, background: "var(--surface)", borderRadius: 8, padding: "10px 14px", fontSize: 13 }}>
                    <div style={{ color: "var(--muted)", marginBottom: 2 }}>Deposit amount</div>
                    <div style={{ fontWeight: 700, color: "var(--pending)", fontSize: 16 }}>{fmt(depositAmt)}</div>
                    <div style={{ color: "var(--muted)", fontSize: 11, marginTop: 2 }}>Balance: {fmt(completionAmt)}</div>
                  </div>
                </div>
              )}

              {/* Progress billing schedule */}
              {form.billing_type === "progress" && (
                <div>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 80px 100px 24px", gap: 8, marginBottom: 6 }}>
                    <label style={{ fontSize: 11, color: "var(--muted)", textTransform: "uppercase", letterSpacing: "0.5px" }}>Milestone Name</label>
                    <label style={{ fontSize: 11, color: "var(--muted)", textTransform: "uppercase", letterSpacing: "0.5px" }}>%</label>
                    <label style={{ fontSize: 11, color: "var(--muted)", textTransform: "uppercase", letterSpacing: "0.5px" }}>Amount</label>
                    <div />
                  </div>
                  {(form.billing_schedule || [{ name: "Deposit", percent: 33 }, { name: "Completion", percent: 67 }]).map((m, i) => (
                    <div key={i} style={{ display: "grid", gridTemplateColumns: "1fr 80px 100px 24px", gap: 8, alignItems: "center", marginBottom: 8 }}>
                      <input placeholder={`Milestone ${i + 1} name`} value={m.name} onChange={e => {
                        const s = [...(form.billing_schedule || [{ name: "Deposit", percent: 33 }, { name: "Completion", percent: 67 }])];
                        s[i] = { ...s[i], name: e.target.value };
                        setForm(p => ({ ...p, billing_schedule: s }));
                      }} />
                      <div style={{ position: "relative" }}>
                        <input type="number" min="1" max="99" value={m.percent} onChange={e => {
                          const s = [...(form.billing_schedule || [{ name: "Deposit", percent: 33 }, { name: "Completion", percent: 67 }])];
                          s[i] = { ...s[i], percent: parseFloat(e.target.value) || 0 };
                          setForm(p => ({ ...p, billing_schedule: s }));
                        }} style={{ paddingRight: 20 }} />
                        <span style={{ position: "absolute", right: 8, top: "50%", transform: "translateY(-50%)", fontSize: 12, color: "var(--muted)" }}>%</span>
                      </div>
                      <div style={{ fontSize: 13, color: "var(--accent)", fontWeight: 600 }}>{fmt(total * (m.percent / 100))}</div>
                      <button className="remove-btn" onClick={() => {
                        const s = (form.billing_schedule || []).filter((_, idx) => idx !== i);
                        setForm(p => ({ ...p, billing_schedule: s }));
                      }} disabled={(form.billing_schedule || []).length <= 2}>×</button>
                    </div>
                  ))}

                  {/* Total validation */}
                  {(() => {
                    const schedule = form.billing_schedule || [{ name: "Deposit", percent: 33 }, { name: "Completion", percent: 67 }];
                    const totalPct = schedule.reduce((s, m) => s + (parseFloat(m.percent) || 0), 0);
                    const valid = Math.abs(totalPct - 100) < 0.01;
                    return (
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "10px 0", borderTop: "1px solid var(--border)", marginTop: 4 }}>
                        <button className="btn btn-secondary btn-sm" onClick={() => {
                          const s = [...(form.billing_schedule || [{ name: "Deposit", percent: 33 }, { name: "Completion", percent: 67 }])];
                          s.push({ name: `Milestone ${s.length + 1}`, percent: 0 });
                          setForm(p => ({ ...p, billing_schedule: s }));
                        }}>+ Add Milestone</button>
                        <div style={{ fontSize: 13, fontWeight: 700, color: valid ? "var(--paid)" : "var(--overdue)" }}>
                          Total: {totalPct.toFixed(1)}% {valid ? "✓" : "(must equal 100%)"}
                        </div>
                      </div>
                    );
                  })()}

                  <div className="info-box green" style={{ marginTop: 10 }}>
                    When signed: Invoice A is sent immediately. Invoices B, C, D... are held as drafts. Release each one when the milestone is reached — you can adjust amounts before releasing for change orders.
                  </div>
                </div>
              )}

              {form.billing_type === "deposit" && (
                <div className="info-box yellow" style={{ marginTop: 10 }}>
                  When client signs: a deposit invoice for {fmt(depositAmt)} is sent immediately. The balance of {fmt(completionAmt)} is held until you release it.
                </div>
              )}
            </div>
          )}

          <div className="form-group" style={{ marginTop: 16 }}><label>Notes</label><textarea placeholder="Payment terms, notes…" value={form.notes} onChange={e => setForm(p => ({ ...p, notes: e.target.value }))} /></div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
          <button className="btn btn-secondary" onClick={handleSave} disabled={saving}>{saving ? "Saving…" : `Save ${type === "estimate" ? "Estimate" : "Invoice"}`}</button>
          {type === "estimate" && (
            <button className="btn btn-primary" onClick={handleSaveAndSend} disabled={saving}>{saving ? "Sending…" : "Save & Send to Client 📧"}</button>
          )}
          {type !== "estimate" && (
            <button className="btn btn-primary" onClick={handleSaveAndSendInvoice} disabled={saving}>{saving ? "Sending…" : "Save & Send Invoice 📧"}</button>
          )}
        </div>
      </div>
    </div>
  );
}

// ── CLIENT FORM ──────────────────────────────────────────────
function ClientFormModal({ client, onSave, onClose, showToast }) {
  const [form, setForm] = useState(client || { name: "", email: "", phone: "", address: "", city: "", state: "", zip: "" });
  const [saving, setSaving] = useState(false);
  const f = (field) => ({ value: form[field] || "", onChange: e => setForm(p => ({ ...p, [field]: e.target.value })) });
  const handleSave = async () => {
    if (!form.name) return showToast("Client name is required", "error");
    setSaving(true);
    try {
      const result = client ? await api.put(`/clients/${client.id}`, form) : await api.post("/clients", form);
      onSave(result); showToast(`Client ${client ? "updated" : "created"}!`); onClose();
    } catch (err) { showToast(err.message, "error"); }
    finally { setSaving(false); }
  };
  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal">
        <div className="modal-head"><div className="modal-title">{client ? "Edit Client" : "New Client"}</div><button className="close-btn" onClick={onClose}>×</button></div>
        <div className="modal-body">
          <div className="form-grid">
            <div className="form-group full"><label>Company / Name *</label><input placeholder="Acme Corp" {...f("name")} /></div>
            <div className="form-group"><label>Email</label><input type="email" placeholder="billing@acme.com" {...f("email")} /></div>
            <div className="form-group"><label>Phone</label><input placeholder="555-0100" {...f("phone")} /></div>
            <div className="form-group full"><label>Address</label><input placeholder="123 Main St" {...f("address")} /></div>
            <div className="form-group"><label>City</label><input placeholder="Tampa" {...f("city")} /></div>
            <div className="form-group"><label>State</label><input placeholder="FL" {...f("state")} /></div>
            <div className="form-group"><label>ZIP</label><input placeholder="33601" {...f("zip")} /></div>
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" onClick={handleSave} disabled={saving}>{saving ? "Saving…" : "Save Client"}</button>
        </div>
      </div>
    </div>
  );
}

// ── PAYMENT MODAL ────────────────────────────────────────────
function PaymentModal({ invoice, qboConnected, onSuccess, onClose, showToast }) {
  const [method, setMethod] = useState("card");
  const [card, setCard] = useState({ token: "", expiry: "", cvv2: "", name: "", address: "", city: "", zip: "" });
  const [processing, setProcessing] = useState(false);
  const [success, setSuccess] = useState(false);
  const [result, setResult] = useState(null);
  const [chargeAmt, setChargeAmt] = useState(parseFloat(invoice.balance_due).toFixed(2));
  const handleCharge = async () => {
    if (!card.token) return showToast("Enter a card token", "error");
    const amount = parseFloat(chargeAmt);
    if (!amount || amount <= 0) return showToast("Enter a valid amount", "error");
    if (amount > parseFloat(invoice.balance_due)) return showToast("Amount exceeds balance due", "error");
    setProcessing(true);
    try {
      const res = await api.post("/payments/charge", { invoice_id: invoice.id, ...card, amount });
      setResult(res);
      setSuccess(true);
      // Pass back actual status and remaining balance so UI updates correctly
      onSuccess(invoice.id, res.status, Math.max(0, parseFloat(invoice.balance_due) - amount));
    } catch (err) { showToast(err.message, "error"); }
    finally { setProcessing(false); }
  };
  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal">
        <div className="modal-head">
          <div><div className="modal-title">Collect Payment</div><div style={{ color: "var(--muted)", fontSize: 13, marginTop: 4 }}>{invoice.id} · {invoice.client_name}</div></div>
          <button className="close-btn" onClick={onClose}>×</button>
        </div>
        <div className="modal-body">
          {success ? (
            <div className="success-screen">
              <div className="success-icon">✅</div>
              <div className="success-title">Payment Successful!</div>
              <div className="success-sub">{fmt(chargeAmt)} collected via CardPointe</div>
              <div className="success-sub">Auth code: {result?.authcode}</div>
              {qboConnected && <div className="success-sub" style={{ color: "var(--paid)", marginTop: 8 }}>✓ Recorded in QuickBooks Online</div>}
            </div>
          ) : (
            <>
              <div style={{ background: "var(--surface2)", borderRadius: 8, padding: "14px 18px", marginBottom: 12, display: "flex", justifyContent: "space-between" }}>
                <span style={{ color: "var(--muted)" }}>Balance Due</span>
                <span style={{ fontFamily: "var(--font-head)", fontSize: 20, fontWeight: 700, color: "var(--accent)" }}>{fmt(invoice.balance_due)}</span>
              </div>
              <div className="form-group" style={{ marginBottom: 20 }}>
                <label>Amount to Charge</label>
                <input
                  type="number"
                  min="0.01"
                  max={parseFloat(invoice.balance_due)}
                  step="0.01"
                  value={chargeAmt}
                  onChange={e => setChargeAmt(e.target.value)}
                  style={{ fontSize: 18, fontWeight: 700 }}
                />
                {parseFloat(chargeAmt) < parseFloat(invoice.balance_due) && (
                  <div style={{ fontSize: 12, color: "var(--muted)", marginTop: 4 }}>
                    Partial payment — remaining balance will be {fmt(parseFloat(invoice.balance_due) - parseFloat(chargeAmt || 0))}
                  </div>
                )}
              </div>
              <div className="payment-methods">
                <div className={`pm-btn ${method === "card" ? "active" : ""}`} onClick={() => setMethod("card")}><span className="pm-icon">💳</span>Credit / Debit</div>
                <div className={`pm-btn ${method === "ach" ? "active" : ""}`} onClick={() => setMethod("ach")}><span className="pm-icon">🏦</span>ACH / Bank</div>
              </div>
              {method === "card" && (
                <div className="form-grid">
                  <div className="form-group full"><label>Cardholder Name</label><input placeholder="Jane Smith" value={card.name} onChange={e => setCard(p => ({ ...p, name: e.target.value }))} /></div>
                  <div className="form-group full">
                    <label>Card Token (from CardSecure)</label>
                    <input placeholder="CardSecure token — auto-filled in production" value={card.token} onChange={e => setCard(p => ({ ...p, token: e.target.value }))} />
                    <div className="info-box green">🔒 In production, CardSecure hosted fields fill this automatically. You never see raw card numbers.</div>
                  </div>
                  <div className="form-group"><label>Expiry (MMYY)</label><input placeholder="1230" value={card.expiry} onChange={e => setCard(p => ({ ...p, expiry: e.target.value }))} /></div>
                  <div className="form-group"><label>CVV</label><input type="password" placeholder="123" value={card.cvv2} onChange={e => setCard(p => ({ ...p, cvv2: e.target.value }))} /></div>
                  <div className="form-group"><label>Billing Address</label><input placeholder="123 Main St" value={card.address} onChange={e => setCard(p => ({ ...p, address: e.target.value }))} /></div>
                  <div className="form-group"><label>Billing ZIP</label><input placeholder="33602" value={card.zip} onChange={e => setCard(p => ({ ...p, zip: e.target.value }))} /></div>
                </div>
              )}
              {method === "ach" && (
                <div className="form-grid">
                  <div className="form-group"><label>Routing Number</label><input placeholder="021000021" /></div>
                  <div className="form-group"><label>Account Number</label><input type="password" placeholder="••••••••" /></div>
                  <div className="form-group"><label>Account Type</label><select><option>Checking</option><option>Savings</option></select></div>
                  <div className="form-group"><label>Account Holder</label><input placeholder="Business name" /></div>
                </div>
              )}
            </>
          )}
        </div>
        {!success && <div className="modal-footer"><button className="btn btn-secondary" onClick={onClose}>Cancel</button><button className="btn btn-primary" onClick={handleCharge} disabled={processing}>{processing ? "Processing…" : `Charge ${fmt(chargeAmt)}`}</button></div>}
      </div>
    </div>
  );
}

// ── PRINT INVOICE ────────────────────────────────────────────
function printInvoice(data, settings = {}) {
  const companyName = settings.name || settings.company_name || "GoApexPay";
  const companyEmail = settings.email || settings.company_email || "billing@goapexpay.com";
  const companyPhone = settings.phone || settings.company_phone || "";
  const companyAddress = [settings.address, settings.city, settings.state, settings.zip].filter(Boolean).join(", ");
  const logoData = settings.logo_data || null;

  const lineItemsHtml = (data.lineItems || []).map(item => `
    <tr>
      <td style="padding:10px 0;border-bottom:1px solid #eee;font-size:14px;">${item.description}</td>
      <td style="padding:10px 0;border-bottom:1px solid #eee;text-align:center;font-size:14px;color:#666;">${item.quantity}</td>
      <td style="padding:10px 0;border-bottom:1px solid #eee;text-align:right;font-size:14px;color:#666;">$${parseFloat(item.unit_price).toFixed(2)}</td>
      <td style="padding:10px 0;border-bottom:1px solid #eee;text-align:right;font-size:14px;font-weight:600;">$${parseFloat(item.amount).toFixed(2)}</td>
    </tr>
  `).join("");

  const paymentsHtml = (data.payments || []).length > 0 ? `
    <div style="margin-top:24px;">
      <div style="font-size:12px;color:#999;text-transform:uppercase;letter-spacing:0.5px;margin-bottom:8px;">Payment History</div>
      ${data.payments.map(p => `
        <div style="display:flex;justify-content:space-between;padding:6px 0;border-bottom:1px solid #eee;font-size:13px;">
          <span style="color:#666;">${p.created_at?.split("T")[0]} · ${p.method}${p.last4 ? ` ····${p.last4}` : ""}</span>
          <span style="color:#00a87a;font-weight:600;">$${parseFloat(p.amount).toFixed(2)}</span>
        </div>
      `).join("")}
    </div>
  ` : "";

  const statusColor = { paid: "#00a87a", pending: "#f5a623", overdue: "#ff4757", draft: "#999" };
  const color = statusColor[data.status] || "#999";

  const html = `<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8">
  <title>Invoice ${data.id}</title>
  <style>
    * { box-sizing: border-box; margin: 0; padding: 0; }
    body { font-family: 'Helvetica Neue', Arial, sans-serif; color: #1a1a2e; background: #fff; padding: 48px; max-width: 800px; margin: 0 auto; }
    @media print {
      body { padding: 24px; }
      .no-print { display: none !important; }
      @page { margin: 0.5in; }
    }
    .header { display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 48px; padding-bottom: 32px; border-bottom: 2px solid #0a0d12; }
    .company-name { font-size: 26px; font-weight: 800; color: #0a0d12; letter-spacing: -0.5px; margin-bottom: 6px; }
    .company-name span { color: #00a87a; }
    .company-details { font-size: 13px; color: #666; line-height: 1.6; }
    .invoice-meta { text-align: right; }
    .invoice-number { font-size: 22px; font-weight: 700; color: #0a0d12; margin-bottom: 8px; }
    .status-badge { display: inline-block; padding: 4px 12px; border-radius: 20px; font-size: 11px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.5px; background: ${color}18; color: ${color}; margin-bottom: 12px; }
    .meta-row { font-size: 13px; color: #666; margin-bottom: 4px; }
    .meta-row strong { color: #1a1a2e; }
    .parties { display: grid; grid-template-columns: 1fr 1fr; gap: 32px; margin-bottom: 40px; }
    .party-label { font-size: 11px; color: #999; text-transform: uppercase; letter-spacing: 0.5px; margin-bottom: 8px; }
    .party-name { font-size: 16px; font-weight: 700; margin-bottom: 4px; }
    .party-detail { font-size: 13px; color: #666; line-height: 1.6; }
    table { width: 100%; border-collapse: collapse; margin-bottom: 24px; }
    thead tr { background: #f8f9fa; }
    th { padding: 12px 0; text-align: left; font-size: 11px; color: #999; text-transform: uppercase; letter-spacing: 0.5px; border-bottom: 2px solid #eee; }
    th:not(:first-child) { text-align: right; }
    .totals { margin-left: auto; width: 280px; }
    .totals-row { display: flex; justify-content: space-between; padding: 6px 0; font-size: 14px; color: #666; border-bottom: 1px solid #f0f0f0; }
    .totals-row.grand { font-size: 18px; font-weight: 700; color: #0a0d12; border-bottom: none; border-top: 2px solid #0a0d12; padding-top: 10px; margin-top: 4px; }
    .totals-row.grand span:last-child { color: #00a87a; }
    .totals-row.paid-row span { color: #00a87a; }
    .totals-row.balance { font-weight: 700; color: #ff4757; }
    .notes { margin-top: 32px; padding: 16px 20px; background: #f8f9fa; border-left: 3px solid #00a87a; border-radius: 0 6px 6px 0; font-size: 13px; color: #555; }
    .footer { margin-top: 48px; padding-top: 24px; border-top: 1px solid #eee; text-align: center; font-size: 12px; color: #999; }
    .print-btn { position: fixed; bottom: 30px; right: 30px; background: #0a0d12; color: #fff; border: none; padding: 14px 28px; border-radius: 8px; font-size: 15px; font-weight: 600; cursor: pointer; box-shadow: 0 4px 20px rgba(0,0,0,0.2); }
    .print-btn:hover { background: #00a87a; }
  </style>
</head>
<body>
  <div class="header">
    <div>
      ${logoData
        ? `<img src="${logoData}" alt="${companyName}" style="max-height:70px;max-width:200px;object-fit:contain;margin-bottom:8px;" />`
        : `<img src="https://apex-invoice.vercel.app/logo.png" alt="GoApexPay" style="height:36px;width:auto;object-fit:contain;" />`}
      <div class="company-details">
        ${companyName}<br>
        ${companyAddress ? companyAddress + "<br>" : ""}
        ${companyEmail}${companyPhone ? " · " + companyPhone : ""}
      </div>
    </div>
    <div class="invoice-meta">
      <div class="invoice-number">${data.id}</div>
      <div class="status-badge">${data.status.toUpperCase()}</div>
      <div class="meta-row">Date: <strong>${data.invoice_date?.split('T')[0] || data.invoice_date}</strong></div>
      <div class="meta-row">Due: <strong>${data.due_date?.split('T')[0] || "Upon receipt"}</strong></div>
      ${data.po_number ? `<div class="meta-row">PO#: <strong>${data.po_number}</strong></div>` : ""}
    </div>
  </div>

  <div class="parties">
    <div>
      <div class="party-label">Billed To</div>
      <div class="party-name">${data.client_name}</div>
      <div class="party-detail">
        ${data.client_email ? data.client_email + "<br>" : ""}
        ${data.client_phone ? data.client_phone + "<br>" : ""}
        ${data.client_address ? data.client_address + "<br>" : ""}
        ${data.client_city ? data.client_city + (data.client_state ? ", " + data.client_state : "") + (data.client_zip ? " " + data.client_zip : "") : ""}
      </div>
    </div>
    <div>
      <div class="party-label">From</div>
      <div class="party-name">${companyName}</div>
      <div class="party-detail">${companyEmail}</div>
    </div>
  </div>

  <table>
    <thead>
      <tr>
        <th style="width:50%">Description</th>
        <th style="text-align:right">Qty</th>
        <th style="text-align:right">Unit Price</th>
        <th style="text-align:right">Amount</th>
      </tr>
    </thead>
    <tbody>${lineItemsHtml}</tbody>
  </table>

  <div class="totals">
    <div class="totals-row"><span>Subtotal</span><span>$${parseFloat(data.subtotal).toFixed(2)}</span></div>
    ${parseFloat(data.tax_amount) > 0 ? `<div class="totals-row"><span>Tax (${data.tax_rate}%)</span><span>$${parseFloat(data.tax_amount).toFixed(2)}</span></div>` : ""}
    <div class="totals-row grand"><span>Total</span><span>$${parseFloat(data.total).toFixed(2)}</span></div>
    ${parseFloat(data.amount_paid) > 0 ? `<div class="totals-row paid-row"><span>Paid</span><span>-$${parseFloat(data.amount_paid).toFixed(2)}</span></div>` : ""}
    ${parseFloat(data.balance_due) > 0.01 ? `<div class="totals-row balance"><span>Balance Due</span><span>$${parseFloat(data.balance_due).toFixed(2)}</span></div>` : ""}
  </div>

  ${data.notes ? `<div class="notes"><strong>Notes:</strong> ${data.notes}</div>` : ""}
  ${paymentsHtml}

  <div class="footer">
    Thank you for your business! · ${companyEmail}
  </div>

  <button class="print-btn no-print" onclick="window.print()">🖨️ Print / Save PDF</button>
</body>
</html>`;

  const win = window.open("", "_blank");
  win.document.write(html);
  win.document.close();
}

// ── INVOICE DETAIL MODAL ─────────────────────────────────────
function InvoiceDetailModal({ invoiceId, onClose, onSend, onPay, showToast, settings = {} }) {
  const [data, setData] = useState(null);
  const [sending, setSending] = useState(false);
  useEffect(() => { api.get(`/invoices/${invoiceId}`).then(setData).catch(err => showToast(err.message, "error")); }, [invoiceId]);
  const handleSend = async () => {
    setSending(true);
    try { await api.post(`/invoices/${invoiceId}/send`); showToast(`Invoice marked as sent`); setData(p => ({ ...p, status: "pending" })); onSend && onSend(invoiceId); }
    catch (err) { showToast(err.message, "error"); }
    finally { setSending(false); }
  };
  if (!data) return <div className="modal-overlay"><div className="modal"><div className="loading"><div className="spinner" />Loading…</div></div></div>;
  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal modal-wide">
        <div className="modal-head">
          <div><div className="modal-title">{data.id}</div><div style={{ marginTop: 8 }}><span className={`status-badge status-${data.status}`}>{data.status}</span></div></div>
          <button className="close-btn" onClick={onClose}>×</button>
        </div>
        <div className="modal-body">
          <div className="detail-grid">
            <div className="detail-section">
              <div style={{ fontSize: 11, color: "var(--muted)", textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: 8 }}>Client</div>
              <div style={{ fontWeight: 700 }}>{data.client_name}</div>
              {data.client_email && <div style={{ fontSize: 13, color: "var(--muted)", marginTop: 4 }}>{data.client_email}</div>}
            </div>
            <div className="detail-section">
              <div style={{ fontSize: 11, color: "var(--muted)", textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: 8 }}>Details</div>
              <div style={{ fontSize: 13, display: "grid", gridTemplateColumns: "auto 1fr", gap: "4px 12px" }}>
                <span style={{ color: "var(--muted)" }}>Date:</span><span>{fmtDate(data.invoice_date)}</span>
                <span style={{ color: "var(--muted)" }}>Due:</span><span>{data.due_date ? fmtDate(data.due_date) : "—"}</span>
                {data.po_number && <><span style={{ color: "var(--muted)" }}>PO#:</span><span>{data.po_number}</span></>}
              </div>
            </div>
          </div>
          <table style={{ width: "100%", borderCollapse: "collapse", marginBottom: 16 }}>
            <thead><tr>
              {["Description", "Qty", "Rate", "Amount"].map((h, i) => (
                <th key={h} style={{ padding: "10px 0", textAlign: i > 0 ? "right" : "left", fontSize: 11, color: "var(--muted)", textTransform: "uppercase", borderBottom: "1px solid var(--border)" }}>{h}</th>
              ))}
            </tr></thead>
            <tbody>
              {(data.lineItems || []).map((item, i) => (
                <tr key={i}>
                  <td style={{ padding: "10px 0", borderBottom: "1px solid rgba(37,44,58,0.4)" }}>{item.description}</td>
                  <td style={{ padding: "10px 0", textAlign: "right", color: "var(--muted)", borderBottom: "1px solid rgba(37,44,58,0.4)" }}>{item.quantity}</td>
                  <td style={{ padding: "10px 0", textAlign: "right", color: "var(--muted)", borderBottom: "1px solid rgba(37,44,58,0.4)" }}>{fmt(item.unit_price)}</td>
                  <td style={{ padding: "10px 0", textAlign: "right", fontWeight: 600, borderBottom: "1px solid rgba(37,44,58,0.4)" }}>{fmt(item.amount)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="totals-box">
            <div className="total-row"><span>Subtotal</span><span>{fmt(data.subtotal)}</span></div>
            {data.tax_amount > 0 && <div className="total-row"><span>Tax ({data.tax_rate}%)</span><span>{fmt(data.tax_amount)}</span></div>}
            <div className="total-row grand"><span>Total</span><span>{fmt(data.total)}</span></div>
            {data.amount_paid > 0 && <div className="total-row" style={{ marginTop: 4 }}><span style={{ color: "var(--paid)" }}>Paid</span><span style={{ color: "var(--paid)" }}>-{fmt(data.amount_paid)}</span></div>}
            {data.balance_due > 0 && <div className="total-row" style={{ fontWeight: 700, fontSize: 15 }}><span>Balance Due</span><span style={{ color: "var(--overdue)" }}>{fmt(data.balance_due)}</span></div>}
          </div>
          {data.notes && <div className="info-box green" style={{ marginTop: 16 }}><strong>Notes:</strong> {data.notes}</div>}
          {(data.payments || []).length > 0 && (
            <div style={{ marginTop: 16 }}>
              <label style={{ fontSize: 11, color: "var(--muted)", textTransform: "uppercase", letterSpacing: "0.5px" }}>Payment History</label>
              {data.payments.map(p => (
                <div key={p.id} style={{ display: "flex", justifyContent: "space-between", padding: "8px 0", borderBottom: "1px solid var(--border)", fontSize: 13 }}>
                  <span style={{ color: "var(--muted)" }}>{p.created_at?.split("T")[0]} · {p.method}{p.last4 ? ` ····${p.last4}` : ""}</span>
                  <span style={{ color: "var(--paid)", fontWeight: 600 }}>{fmt(p.amount)}</span>
                </div>
              ))}
            </div>
          )}
        </div>
        <div className="modal-footer">
          <button className="btn btn-secondary btn-sm" onClick={() => printInvoice(data, settings)} style={{ marginRight: "auto" }}>🖨️ Print / PDF</button>
          {data.status === "draft" && <button className="btn btn-secondary btn-sm" onClick={handleSend} disabled={sending}>{sending ? "Sending…" : "📧 Send Invoice"}</button>}
          {(data.status === "pending" || data.status === "overdue" || data.status === "partial") && (
            <button className="btn btn-secondary btn-sm" onClick={handleSend} disabled={sending}>{sending ? "Sending…" : "📧 Resend Invoice"}</button>
          )}
          {(data.status === "pending" || data.status === "overdue" || data.status === "partial") && parseFloat(data.balance_due) > 0 && (
            <button className="btn btn-primary btn-sm" onClick={() => { onClose(); onPay(data); }}>💳 Collect Payment</button>
          )}
          <button className="btn btn-secondary btn-sm" onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  );
}

// ── PRINT ESTIMATE ───────────────────────────────────────────
function printEstimate(data, settings = {}) {
  const companyName = settings.name || settings.company_name || "GoApexPay";
  const companyEmail = settings.email || settings.company_email || "billing@goapexpay.com";
  const companyPhone = settings.phone || settings.company_phone || "";
  const companyAddress = [settings.address, settings.city, settings.state, settings.zip].filter(Boolean).join(", ");
  const logoData = settings.logo_data || null;

  const lineItemsHtml = (data.lineItems || []).map(item => `
    <tr>
      <td style="padding:10px 0;border-bottom:1px solid #eee;font-size:14px;">${item.description}</td>
      <td style="padding:10px 0;border-bottom:1px solid #eee;text-align:center;font-size:14px;color:#666;">${item.quantity}</td>
      <td style="padding:10px 0;border-bottom:1px solid #eee;text-align:right;font-size:14px;color:#666;">$${parseFloat(item.unit_price).toFixed(2)}</td>
      <td style="padding:10px 0;border-bottom:1px solid #eee;text-align:right;font-size:14px;font-weight:600;">$${parseFloat(item.amount).toFixed(2)}</td>
    </tr>
  `).join("");

  const html = `<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8">
  <title>Estimate ${data.id}</title>
  <style>
    * { box-sizing: border-box; margin: 0; padding: 0; }
    body { font-family: 'Helvetica Neue', Arial, sans-serif; color: #1a1a2e; background: #fff; padding: 48px; max-width: 800px; margin: 0 auto; }
    @media print { body { padding: 24px; } .no-print { display: none !important; } @page { margin: 0.5in; } }
    .header { display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 48px; padding-bottom: 32px; border-bottom: 2px solid #0a0d12; }
    .company-name { font-size: 26px; font-weight: 800; color: #0a0d12; letter-spacing: -0.5px; margin-bottom: 6px; }
    .company-name span { color: #00a87a; }
    .company-details { font-size: 13px; color: #666; line-height: 1.6; }
    .estimate-meta { text-align: right; }
    .estimate-number { font-size: 22px; font-weight: 700; color: #0a0d12; margin-bottom: 8px; }
    .estimate-label { display: inline-block; padding: 4px 12px; border-radius: 20px; font-size: 11px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.5px; background: rgba(79,142,247,0.1); color: #4f8ef7; margin-bottom: 12px; }
    .meta-row { font-size: 13px; color: #666; margin-bottom: 4px; }
    .meta-row strong { color: #1a1a2e; }
    .parties { display: grid; grid-template-columns: 1fr 1fr; gap: 32px; margin-bottom: 40px; }
    .party-label { font-size: 11px; color: #999; text-transform: uppercase; letter-spacing: 0.5px; margin-bottom: 8px; }
    .party-name { font-size: 16px; font-weight: 700; margin-bottom: 4px; }
    .party-detail { font-size: 13px; color: #666; line-height: 1.6; }
    .valid-banner { background: #f0fffe; border: 1px solid #00a87a; border-radius: 8px; padding: 14px 20px; margin-bottom: 32px; font-size: 13px; color: #00a87a; text-align: center; }
    table { width: 100%; border-collapse: collapse; margin-bottom: 24px; }
    thead tr { background: #f8f9fa; }
    th { padding: 12px 0; text-align: left; font-size: 11px; color: #999; text-transform: uppercase; letter-spacing: 0.5px; border-bottom: 2px solid #eee; }
    th:not(:first-child) { text-align: right; }
    .totals { margin-left: auto; width: 280px; }
    .totals-row { display: flex; justify-content: space-between; padding: 6px 0; font-size: 14px; color: #666; border-bottom: 1px solid #f0f0f0; }
    .totals-row.grand { font-size: 18px; font-weight: 700; color: #0a0d12; border-bottom: none; border-top: 2px solid #0a0d12; padding-top: 10px; margin-top: 4px; }
    .totals-row.grand span:last-child { color: #4f8ef7; }
    .notes { margin-top: 32px; padding: 16px 20px; background: #f8f9fa; border-left: 3px solid #4f8ef7; border-radius: 0 6px 6px 0; font-size: 13px; color: #555; }
    .accept-box { margin-top: 48px; border: 1px solid #ddd; border-radius: 8px; padding: 24px; }
    .accept-title { font-size: 14px; font-weight: 700; margin-bottom: 16px; color: #1a1a2e; }
    .sig-line { border-bottom: 1px solid #999; margin-bottom: 6px; height: 40px; }
    .sig-label { font-size: 11px; color: #999; }
    .sig-grid { display: grid; grid-template-columns: 2fr 1fr; gap: 32px; }
    .footer { margin-top: 32px; padding-top: 24px; border-top: 1px solid #eee; text-align: center; font-size: 12px; color: #999; }
    .print-btn { position: fixed; bottom: 30px; right: 30px; background: #0a0d12; color: #fff; border: none; padding: 14px 28px; border-radius: 8px; font-size: 15px; font-weight: 600; cursor: pointer; box-shadow: 0 4px 20px rgba(0,0,0,0.2); }
    .print-btn:hover { background: #4f8ef7; }
  </style>
</head>
<body>
  <div class="header">
    <div>
      ${logoData
        ? `<img src="${logoData}" alt="${companyName}" style="max-height:70px;max-width:200px;object-fit:contain;margin-bottom:8px;" />`
        : `<img src="https://apex-invoice.vercel.app/logo.png" alt="GoApexPay" style="height:36px;width:auto;object-fit:contain;" />`}
      <div class="company-details">${companyName}<br>${companyAddress ? companyAddress + "<br>" : ""}${companyEmail}${companyPhone ? " · " + companyPhone : ""}</div>
    </div>
    <div class="estimate-meta">
      <div class="estimate-number">${data.id}</div>
      <div class="estimate-label">ESTIMATE</div>
      <div class="meta-row">Date: <strong>${data.estimate_date?.split('T')[0] || data.estimate_date}</strong></div>
      ${data.expiry_date ? `<div class="meta-row">Valid Until: <strong>${data.expiry_date}</strong></div>` : ""}
    </div>
  </div>

  ${data.expiry_date ? `<div class="valid-banner">This estimate is valid until <strong>${data.expiry_date}</strong>. Please review and contact us to proceed.</div>` : ""}

  <div class="parties">
    <div>
      <div class="party-label">Prepared For</div>
      <div class="party-name">${data.client_name}</div>
      <div class="party-detail">
        ${data.client_email ? data.client_email + "<br>" : ""}
        ${data.client_phone ? data.client_phone + "<br>" : ""}
        ${data.client_address ? data.client_address + "<br>" : ""}
        ${data.client_city ? data.client_city + (data.client_state ? ", " + data.client_state : "") + (data.client_zip ? " " + data.client_zip : "") : ""}
      </div>
    </div>
    <div>
      <div class="party-label">Prepared By</div>
      <div class="party-name">${companyName}</div>
      <div class="party-detail">${companyEmail}</div>
    </div>
  </div>

  <table>
    <thead>
      <tr>
        <th style="width:50%">Description</th>
        <th style="text-align:right">Qty</th>
        <th style="text-align:right">Unit Price</th>
        <th style="text-align:right">Amount</th>
      </tr>
    </thead>
    <tbody>${lineItemsHtml}</tbody>
  </table>

  <div class="totals">
    <div class="totals-row"><span>Subtotal</span><span>$${parseFloat(data.subtotal).toFixed(2)}</span></div>
    ${parseFloat(data.tax_amount) > 0 ? `<div class="totals-row"><span>Tax (${data.tax_rate}%)</span><span>$${parseFloat(data.tax_amount).toFixed(2)}</span></div>` : ""}
    <div class="totals-row grand"><span>Estimate Total</span><span>$${parseFloat(data.total).toFixed(2)}</span></div>
  </div>

  ${data.notes ? `<div class="notes"><strong>Notes:</strong> ${data.notes}</div>` : ""}

  <div class="accept-box">
    <div class="accept-title">Acceptance</div>
    <div class="sig-grid">
      <div><div class="sig-line"></div><div class="sig-label">Authorized Signature</div></div>
      <div><div class="sig-line"></div><div class="sig-label">Date</div></div>
    </div>
  </div>

  <div class="footer">Thank you for the opportunity to serve you! · ${companyEmail}</div>

  <button class="print-btn no-print" onclick="window.print()">🖨️ Print / Save PDF</button>
</body>
</html>`;

  const win = window.open("", "_blank");
  win.document.write(html);
  win.document.close();
}

// ── ESTIMATE DETAIL MODAL ────────────────────────────────────
function EstimateDetailModal({ estimateId, onClose, onConverted, showToast, settings = {} }) {
  const [data, setData] = useState(null);
  const [converting, setConverting] = useState(false);

  useEffect(() => {
    api.get(`/estimates/${estimateId}`)
      .then(setData)
      .catch(err => showToast(err.message, "error"));
  }, [estimateId]);

  const handleConvert = async () => {
    setConverting(true);
    try {
      const r = await api.post(`/estimates/${estimateId}/convert`);
      showToast(`Converted to invoice ${r.invoice_id}`);
      onConverted(r.invoice_id);
      onClose();
    } catch (err) {
      showToast(err.message, "error");
    } finally {
      setConverting(false);
    }
  };

  if (!data) return (
    <div className="modal-overlay">
      <div className="modal"><div className="loading"><div className="spinner" />Loading…</div></div>
    </div>
  );

  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal modal-wide">
        <div className="modal-head">
          <div>
            <div className="modal-title">{data.id}</div>
            <div style={{ marginTop: 8, display: "flex", gap: 8, alignItems: "center" }}>
              <span className={`status-badge status-${data.status}`}>{data.status}</span>
              {data.converted_to && <span style={{ fontSize: 12, color: "var(--muted)" }}>→ converted to {data.converted_to}</span>}
            </div>
          </div>
          <button className="close-btn" onClick={onClose}>×</button>
        </div>
        <div className="modal-body">
          <div className="detail-grid">
            <div className="detail-section">
              <div style={{ fontSize: 11, color: "var(--muted)", textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: 8 }}>Prepared For</div>
              <div style={{ fontWeight: 700 }}>{data.client_name}</div>
              {data.client_email && <div style={{ fontSize: 13, color: "var(--muted)", marginTop: 4 }}>{data.client_email}</div>}
              {data.client_phone && <div style={{ fontSize: 13, color: "var(--muted)" }}>{data.client_phone}</div>}
            </div>
            <div className="detail-section">
              <div style={{ fontSize: 11, color: "var(--muted)", textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: 8 }}>Estimate Details</div>
              <div style={{ fontSize: 13, display: "grid", gridTemplateColumns: "auto 1fr", gap: "4px 12px" }}>
                <span style={{ color: "var(--muted)" }}>Date:</span><span>{fmtDate(data.estimate_date)}</span>
                <span style={{ color: "var(--muted)" }}>Valid Until:</span>
                <span style={{ color: data.expiry_date && new Date(data.expiry_date) < new Date() ? "var(--overdue)" : "inherit" }}>
                  {data.expiry_date || "—"}
                  {data.expiry_date && new Date(data.expiry_date) < new Date() ? " (expired)" : ""}
                </span>
              </div>
            </div>
          </div>

          <table style={{ width: "100%", borderCollapse: "collapse", marginBottom: 16 }}>
            <thead><tr>
              {["Description", "Qty", "Rate", "Amount"].map((h, i) => (
                <th key={h} style={{ padding: "10px 0", textAlign: i > 0 ? "right" : "left", fontSize: 11, color: "var(--muted)", textTransform: "uppercase", borderBottom: "1px solid var(--border)" }}>{h}</th>
              ))}
            </tr></thead>
            <tbody>
              {(data.lineItems || []).map((item, i) => (
                <tr key={i}>
                  <td style={{ padding: "10px 0", borderBottom: "1px solid rgba(37,44,58,0.4)" }}>{item.description}</td>
                  <td style={{ padding: "10px 0", textAlign: "right", color: "var(--muted)", borderBottom: "1px solid rgba(37,44,58,0.4)" }}>{item.quantity}</td>
                  <td style={{ padding: "10px 0", textAlign: "right", color: "var(--muted)", borderBottom: "1px solid rgba(37,44,58,0.4)" }}>{fmt(item.unit_price)}</td>
                  <td style={{ padding: "10px 0", textAlign: "right", fontWeight: 600, borderBottom: "1px solid rgba(37,44,58,0.4)" }}>{fmt(item.amount)}</td>
                </tr>
              ))}
            </tbody>
          </table>

          <div className="totals-box">
            <div className="total-row"><span>Subtotal</span><span>{fmt(data.subtotal)}</span></div>
            {parseFloat(data.tax_amount) > 0 && <div className="total-row"><span>Tax ({data.tax_rate}%)</span><span>{fmt(data.tax_amount)}</span></div>}
            <div className="total-row grand"><span>Estimate Total</span><span>{fmt(data.total)}</span></div>
          </div>

          {data.notes && <div className="info-box green" style={{ marginTop: 16 }}><strong>Notes:</strong> {data.notes}</div>}

          {!data.converted_to && (
            <div className="info-box yellow" style={{ marginTop: 16 }}>
              Ready to proceed? Click <strong>Convert to Invoice</strong> to turn this estimate into a billable invoice.
            </div>
          )}
        </div>

        <div className="modal-footer">
          <button className="btn btn-secondary btn-sm" onClick={() => printEstimate(data, settings)} style={{ marginRight: "auto" }}>🖨️ Print / PDF</button>
          {!data.converted_to && !data.sign_token && (
            <button className="btn btn-secondary btn-sm" onClick={async () => {
              try {
                const r = await api.post(`/estimates/${data.id}/send`);
                showToast(`✅ Estimate emailed to ${r.sentTo || "client"}`);
                setData(p => ({ ...p, sign_token: "sent", status: "sent" }));
              } catch (err) { showToast(err.message, "error"); }
            }}>📧 Send to Client</button>
          )}
          {data.sign_token && !data.converted_to && (
            <button className="btn btn-secondary btn-sm" onClick={() => {
              const link = `${window.location.origin}/estimate/${data.sign_token}`;
              navigator.clipboard.writeText(link);
              showToast("Sign link copied to clipboard!");
            }}>🔗 Copy Sign Link</button>
          )}
          {!data.converted_to && (
            <button className="btn btn-primary btn-sm" onClick={handleConvert} disabled={converting}>
              {converting ? "Converting…" : "→ Convert to Invoice"}
            </button>
          )}
          <button className="btn btn-secondary btn-sm" onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  );
}

// ── INVOICE TABLE ────────────────────────────────────────────
function InvoiceTable({ invoices, loading, filter, setFilter, onView, onPay, onSend, onRelease, showToast, title }) {
  const [search, setSearch] = useState("");
  const filtered = invoices
    .filter(i => filter === "all" || i.status === filter)
    .filter(i => !search || i.id?.toLowerCase().includes(search.toLowerCase()) || i.client_name?.toLowerCase().includes(search.toLowerCase()) || String(i.total).includes(search));
  return (
    <div className="table-card">
      <div className="table-header">
        <div className="table-title">{title}</div>
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <input
            placeholder="Search invoices…"
            value={search}
            onChange={e => setSearch(e.target.value)}
            style={{ background: "var(--surface2)", border: "1px solid var(--border)", borderRadius: 8, padding: "6px 12px", fontSize: 13, color: "var(--text)", width: 180, outline: "none" }}
          />
          <div className="filters">
            {["all", "draft", "pending", "paid", "overdue"].map(f => (
              <button key={f} className={`filter-btn ${filter === f ? "active" : ""}`} onClick={() => setFilter(f)}>{f.charAt(0).toUpperCase() + f.slice(1)}</button>
            ))}
          </div>
        </div>
      </div>
      {loading ? <div className="loading"><div className="spinner" />Loading…</div>
        : filtered.length === 0 ? <div className="empty"><div className="empty-icon">◻</div><div>No invoices found</div></div>
        : (
          <table>
            <thead><tr><th>ID</th><th>Client</th><th>Date</th><th>Due Date</th><th>Amount</th><th>Status</th><th>Actions</th></tr></thead>
            <tbody>
              {filtered.map(inv => (
                <tr key={inv.id}>
                  <td><strong style={{ fontFamily: "var(--font-head)", fontSize: 13 }}>{inv.id}</strong></td>
                  <td>{inv.client_name}</td>
                  <td style={{ color: "var(--muted)" }}>{fmtDate(inv.invoice_date)}</td>
                  <td style={{ color: inv.status === "overdue" ? "var(--overdue)" : "var(--muted)" }}>{fmtDate(inv.due_date)}</td>
                  <td><span className="amount">{fmt(inv.total)}</span></td>
                  <td>
                    <span className={`status-badge status-${inv.status}`}>{inv.status}</span>
                    {inv.invoice_type === "deposit" && <span style={{ marginLeft: 4, fontSize: 10, background: "rgba(245,166,35,0.12)", color: "var(--pending)", padding: "2px 6px", borderRadius: 10, fontWeight: 600 }}>DEPOSIT</span>}
                    {inv.invoice_type === "completion" && <span style={{ marginLeft: 4, fontSize: 10, background: "rgba(79,142,247,0.12)", color: "#4f8ef7", padding: "2px 6px", borderRadius: 10, fontWeight: 600 }}>FINAL</span>}
                    {inv.invoice_type === "milestone" && <span style={{ marginLeft: 4, fontSize: 10, background: "rgba(0,212,170,0.12)", color: "var(--accent)", padding: "2px 6px", borderRadius: 10, fontWeight: 600, whiteSpace: "nowrap" }}>{inv.milestone_name ? inv.milestone_name.length > 12 ? inv.milestone_name.substring(0,12)+'…' : inv.milestone_name : `${inv.milestone_number}/${inv.milestone_total}`}</span>}
                  </td>
                  <td>
                    <button className="action-btn" onClick={() => onView(inv.id)}>View</button>
                    {inv.status === "draft" && inv.invoice_type !== "completion" && inv.invoice_type !== "milestone" && <button className="action-btn" onClick={() => onView(inv.id)}>Send</button>}
                    {(inv.invoice_type === "completion" || inv.invoice_type === "milestone") && inv.status === "draft" && (
                      <button className="action-btn" style={{ color: "var(--accent)", borderColor: "var(--accent)" }} onClick={async () => {
                        const milestoneName = inv.milestone_name || "this milestone";
                        const currentAmt = parseFloat(inv.total);
                        const newAmt = window.prompt(`Release "${milestoneName}" invoice?\n\nCurrent amount: $${currentAmt.toFixed(2)}\n\nEnter amount to bill (or leave unchanged):`, currentAmt.toFixed(2));
                        if (newAmt === null) return; // cancelled
                        try {
                          const amount = parseFloat(newAmt) || currentAmt;
                          await api.post(`/invoices/${inv.id}/release`, { amount });
                          showToast(`${milestoneName} invoice released and emailed to client!`);
                          onRelease && onRelease();
                        } catch (err) { showToast(err.message, "error"); }
                      }}>🚀 Release</button>
                    )}
                    {(inv.status === "pending" || inv.status === "overdue") && <button className="action-btn" onClick={() => onPay(inv)}>Collect</button>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
    </div>
  );
}

// ── NEW ITEM MODAL ───────────────────────────────────────────
function NewItemModal({ onSave, onClose, showToast, qboConnected }) {
  const [form, setForm] = useState({ name: "", description: "", type: "Service", unit_price: "", taxable: false });
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    if (!form.name.trim()) return showToast("Item name is required", "error");
    setSaving(true);
    try {
      const item = await api.post("/items", {
        name: form.name.trim(),
        description: form.description || null,
        type: form.type,
        unit_price: parseFloat(form.unit_price) || 0,
        taxable: form.taxable,
      });
      onSave(item);
      showToast(`"${item.name}" created${item.qbo_item_id ? " and synced to QuickBooks ✓" : ""}!`);
      onClose();
    } catch (err) { showToast(err.message, "error"); }
    finally { setSaving(false); }
  };

  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal">
        <div className="modal-head">
          <div className="modal-title">New Item</div>
          <button className="close-btn" onClick={onClose}>×</button>
        </div>
        <div className="modal-body">
          <div className="form-grid">
            <div className="form-group full">
              <label>Item Name *</label>
              <input placeholder="e.g. Landscape Installation, Consulting Hour" value={form.name} onChange={e => setForm(p => ({...p, name: e.target.value}))} autoFocus />
            </div>
            <div className="form-group full">
              <label>Description</label>
              <input placeholder="Optional — appears on invoices" value={form.description} onChange={e => setForm(p => ({...p, description: e.target.value}))} />
            </div>
            <div className="form-group">
              <label>Type</label>
              <select value={form.type} onChange={e => setForm(p => ({...p, type: e.target.value}))}>
                <option value="Service">Service</option>
                <option value="NonInventory">Product (Non-Inventory)</option>
                <option value="Inventory">Product (Inventory)</option>
              </select>
            </div>
            <div className="form-group">
              <label>Unit Price</label>
              <input type="number" min="0" step="0.01" placeholder="0.00" value={form.unit_price} onChange={e => setForm(p => ({...p, unit_price: e.target.value}))} />
            </div>
            <div className="form-group full">
              <label style={{display:"flex",alignItems:"center",gap:10,cursor:"pointer"}}>
                <input type="checkbox" checked={form.taxable} onChange={e => setForm(p => ({...p, taxable: e.target.checked}))} style={{width:16,height:16}} />
                Taxable item
                <span style={{fontSize:12,color:"var(--muted)",fontWeight:400}}>— tax will be calculated on this item when a tax rate is set</span>
              </label>
            </div>
          </div>
          {qboConnected && (
            <div className="info-box green" style={{marginTop:4}}>
              This item will be automatically created in QuickBooks Online.
            </div>
          )}
        </div>
        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
            {saving ? "Saving…" : "Create Item"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── MERCHANT APPLICATION MODAL ───────────────────────────────
function MerchantApplicationModal({ onClose, showToast }) {
  const [step, setStep] = useState(1);
  const [submitting, setSubmitting] = useState(false);
  const [applicationLink, setApplicationLink] = useState(null);
  const [form, setForm] = useState({
    business_name: "", dba_name: "", business_type: "LLC",
    business_start_date: "", tax_id: "", website: "",
    business_description: "", mcc_code: "",
    owner_first_name: "", owner_last_name: "",
    owner_email: "", owner_phone: "", owner_dob: "",
    owner_ssn: "", owner_title: "", owner_suffix: "", owner_ownership_pct: 100,
    address: "", city: "", state: "", zip: "",
    annual_volume: "", avg_ticket: "", max_ticket: "",
  });

  const f = field => ({ value: form[field] || "", onChange: e => setForm(p => ({ ...p, [field]: e.target.value })) });

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      const r = await api.post("/onboarding/apply", form);
      setApplicationLink(r.application_link);
      showToast("Application submitted! Complete it using the link below.");
      setStep(4);
    } catch (err) {
      showToast(err.message, "error");
    } finally { setSubmitting(false); }
  };

  const steps = ["Business Info", "Owner Info", "Processing Info", "Complete"];

  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal modal-wide">
        <div className="modal-head">
          <div>
            <div className="modal-title">Apply for Credit Card Processing</div>
            <div style={{ display: "flex", gap: 6, marginTop: 10 }}>
              {steps.map((s, i) => (
                <div key={i} style={{ display: "flex", alignItems: "center", gap: 6 }}>
                  <div style={{ width: 24, height: 24, borderRadius: "50%", background: step > i + 1 ? "var(--paid)" : step === i + 1 ? "var(--accent)" : "var(--border)", color: step >= i + 1 ? "#0a0d12" : "var(--muted)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 11, fontWeight: 700 }}>
                    {step > i + 1 ? "✓" : i + 1}
                  </div>
                  <span style={{ fontSize: 12, color: step === i + 1 ? "var(--text)" : "var(--muted)", fontWeight: step === i + 1 ? 600 : 400 }}>{s}</span>
                  {i < steps.length - 1 && <div style={{ width: 20, height: 1, background: "var(--border)" }} />}
                </div>
              ))}
            </div>
          </div>
          <button className="close-btn" onClick={onClose}>×</button>
        </div>

        <div className="modal-body">
          {/* Step 1 — Business Info */}
          {step === 1 && (
            <div>
              <div className="info-box green" style={{ marginBottom: 20 }}>
                This information is used to create your CardPointe merchant account. All data is transmitted securely to FiServ for processing.
              </div>
              <div className="form-grid">
                <div className="form-group"><label>Legal Business Name *</label><input placeholder="Acme Landscaping LLC" {...f("business_name")} /></div>
                <div className="form-group"><label>DBA Name (if different)</label><input placeholder="Acme Landscaping" {...f("dba_name")} /></div>
                <div className="form-group"><label>Business Type *</label>
                  <select {...f("business_type")} required>
                    <option value="INDIVSOLE">Individual / Sole Proprietor</option>
                    <option value="LLC">LLC</option>
                    <option value="PRIVCORP">Private Corporation</option>
                    <option value="PUBCORP">Public Corporation</option>
                    <option value="PRTNRSHP">Partnership</option>
                    <option value="NONPRFT">Non-Profit Org</option>
                    <option value="GOVT">Government</option>
                    <option value="TAXEXMPT">Tax Exempt</option>
                  </select>
                </div>
                <div className="form-group"><label>Business Start Date *</label><input type="date" {...f("business_start_date")} /></div>
                <div className="form-group"><label>EIN / Tax ID *</label><input placeholder="12-3456789" {...f("tax_id")} /></div>
                <div className="form-group"><label>Website</label><input placeholder="https://acme.com" {...f("website")} /></div>
                <div className="form-group full"><label>Address *</label><input placeholder="123 Main St" {...f("address")} /></div>
                <div className="form-group"><label>City *</label><input placeholder="Tampa" {...f("city")} /></div>
                <div className="form-group"><label>State *</label><input placeholder="FL" {...f("state")} /></div>
                <div className="form-group"><label>ZIP *</label><input placeholder="33601" {...f("zip")} /></div>
                <div className="form-group full"><label>Business Description</label><textarea placeholder="Brief description of your business and the products/services you sell" {...f("business_description")} /></div>
              </div>
            </div>
          )}

          {/* Step 2 — Owner Info */}
          {step === 2 && (
            <div>
              <div className="info-box yellow" style={{ marginBottom: 20 }}>
                Owner information is required for identity verification and compliance. This is standard for all merchant accounts.
              </div>
              <div className="form-grid">
                <div className="form-group"><label>First Name *</label><input placeholder="John" {...f("owner_first_name")} /></div>
                <div className="form-group"><label>Last Name *</label><input placeholder="Smith" {...f("owner_last_name")} /></div>
                <div className="form-group"><label>Suffix</label><select {...f("owner_suffix")}><option value="">None</option><option value="Jr">Jr</option><option value="Sr">Sr</option><option value="II">II</option><option value="III">III</option><option value="IV">IV</option><option value="V">V</option><option value="PhD">PhD</option><option value="MD">MD</option><option value="Esq">Esq</option></select></div>
                <div className="form-group"><label>Email *</label><input type="email" placeholder="john@acme.com" {...f("owner_email")} /></div>
                <div className="form-group"><label>Phone *</label><input placeholder="555-555-5555" {...f("owner_phone")} /></div>
                <div className="form-group"><label>Date of Birth</label><input type="date" {...f("owner_dob")} /></div>
                <div className="form-group"><label>Owner SSN</label><input placeholder="123-45-6789" maxLength={11} type="password" autoComplete="off" {...f("owner_ssn")} /></div>
                <div className="form-group"><label>Title</label>
                  <select {...f("owner_title")} required>
                    <option value="">— Select title —</option>
                    {form.business_type === "INDIVSOLE" && <option value="OWNER">Owner</option>}
                    {form.business_type === "PRTNRSHP" && <option value="PARTNER">Partner</option>}
                    {["LLC","PRIVCORP","PUBCORP","GOVT","TAXEXMPT","NONPRFT"].includes(form.business_type) && <>
                      <option value="PRESIDENT">President</option>
                      <option value="CEO">CEO</option>
                      <option value="CFO">CFO</option>
                      <option value="COO">COO</option>
                      <option value="VICE_PRESIDENT">Vice President</option>
                      <option value="SECRETARY">Secretary</option>
                      <option value="TREASURER">Treasurer</option>
                    </>}
                    {["LLC","GOVT","TAXEXMPT","NONPRFT"].includes(form.business_type) && <option value="MEMBER_LLC">Member (LLC)</option>}
                    {["GOVT","TAXEXMPT","NONPRFT"].includes(form.business_type) && <>
                      <option value="OWNER">Owner</option>
                      <option value="PARTNER">Partner</option>
                    </>}
                  </select>
                </div>
                <div className="form-group"><label>Ownership %</label><input type="number" min="1" max="100" {...f("owner_ownership_pct")} /></div>
              </div>
            </div>
          )}

          {/* Step 3 — Processing Info */}
          {step === 3 && (
            <div>
              <div className="info-box green" style={{ marginBottom: 20 }}>
                Processing estimates help CardPointe set appropriate risk limits for your account. Use your best estimates — these can be updated later.
              </div>
              <div className="form-grid">
                <div className="form-group"><label>Estimated Annual Volume ($) *</label><input type="number" placeholder="50000" {...f("annual_volume")} /></div>
                <div className="form-group"><label>Average Transaction Amount ($) *</label><input type="number" placeholder="250" {...f("avg_ticket")} /></div>
                <div className="form-group"><label>Largest Expected Transaction ($)</label><input type="number" placeholder="5000" {...f("max_ticket")} /></div>
                <div className="form-group"><label>Business Category (MCC)</label>
                  <select {...f("mcc_code")} required>
                    <option value="">— Select your industry —</option>
                    <optgroup label="Construction &amp; Trades">
                      <option value="1520">General Contractors</option>
                      <option value="1711">Heating, Plumbing &amp; A/C</option>
                      <option value="1731">Electrical Contractors</option>
                      <option value="1750">Carpentry Contractors</option>
                      <option value="1761">Roofing / Siding / Sheet Metal</option>
                      <option value="1771">Concrete Work Contractors</option>
                      <option value="1799">Special Trade Contractors (Other)</option>
                    </optgroup>
                    <optgroup label="Professional Services">
                      <option value="8931">Accounting, Auditing &amp; Bookkeeping</option>
                      <option value="8111">Legal Services / Attorneys</option>
                      <option value="7392">Management &amp; Consulting Services</option>
                      <option value="8911">Architecture, Engineering &amp; Surveying</option>
                      <option value="7311">Advertising Services</option>
                      <option value="7333">Photography, Art &amp; Graphics</option>
                      <option value="7372">Computer / Software / IT Services</option>
                    </optgroup>
                    <optgroup label="Health &amp; Medical">
                      <option value="8099">Medical / Health Practitioners</option>
                      <option value="8071">Medical &amp; Dental Labs</option>
                      <option value="742">Veterinary Services</option>
                    </optgroup>
                    <optgroup label="Automotive &amp; Transport">
                      <option value="7531">Auto Body Repair</option>
                      <option value="7549">Towing Services</option>
                      <option value="4214">Motor Freight / Trucking</option>
                    </optgroup>
                    <optgroup label="Home &amp; Property Services">
                      <option value="780">Landscaping Services</option>
                      <option value="7349">Cleaning / Janitorial Services</option>
                      <option value="7393">Security Services</option>
                      <option value="7692">Welding Repair</option>
                      <option value="7623">A/C &amp; Refrigeration Repair</option>
                      <option value="7629">Electrical &amp; Appliance Repair</option>
                      <option value="7622">Electronics Repair</option>
                    </optgroup>
                    <optgroup label="Food &amp; Events">
                      <option value="5811">Caterers / Food Preparation &amp; Delivery</option>
                    </optgroup>
                    <optgroup label="Media &amp; Print">
                      <option value="7221">Photographic Studios</option>
                      <option value="2741">Printing &amp; Publishing</option>
                    </optgroup>
                  </select>
                </div>
              </div>
              <div className="info-box yellow" style={{ marginTop: 16 }}>
                <strong>What happens next:</strong> After submitting, you'll receive a link to complete and digitally sign your merchant agreement directly on CardPointe's secure platform. Approval typically takes 1-2 business days. Once approved, your Merchant ID will be automatically added to your account settings.
              </div>
            </div>
          )}

          {/* Step 4 — Success */}
          {step === 4 && (
            <div style={{ textAlign: "center", padding: "20px 0" }}>
              <div style={{ fontSize: 56, marginBottom: 16 }}>🎉</div>
              <div style={{ fontFamily: "var(--font-head)", fontSize: 22, fontWeight: 700, marginBottom: 12, color: "var(--paid)" }}>Application Submitted!</div>
              <div style={{ color: "var(--muted)", fontSize: 14, marginBottom: 28, lineHeight: 1.6, maxWidth: 440, margin: "0 auto 28px" }}>
                Your merchant application has been created with CardPointe. Click the button below to complete and sign your merchant agreement. The entire process takes about 5 minutes.
              </div>
              {applicationLink && (
                <div>
                  <a href={applicationLink} target="_blank" rel="noopener noreferrer" className="btn btn-primary" style={{ display: "inline-flex", marginBottom: 16, textDecoration: "none" }}>
                    ✍️ Complete & Sign Merchant Agreement
                  </a>
                  <div style={{ fontSize: 12, color: "var(--muted)", marginTop: 8 }}>
                    This link has also been emailed to you. Approval takes 1-2 business days.
                  </div>
                  <div className="info-box green" style={{ marginTop: 16, textAlign: "left" }}>
                    Once approved, go to <strong>Integrations → Check Approval Status</strong> to automatically pull your Merchant ID into your account.
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        <div className="modal-footer">
          {step < 4 && <button className="btn btn-secondary" onClick={() => step > 1 ? setStep(s => s - 1) : onClose()}>{step > 1 ? "← Back" : "Cancel"}</button>}
          {step < 3 && <button className="btn btn-primary" onClick={() => setStep(s => s + 1)}>Continue →</button>}
          {step === 3 && <button className="btn btn-primary" onClick={handleSubmit} disabled={submitting}>{submitting ? "Submitting…" : "Submit Application"}</button>}
          {step === 4 && <button className="btn btn-primary" onClick={onClose}>Done</button>}
        </div>
      </div>
    </div>
  );
}

// ── LEGAL PAGES CSS ──────────────────────────────────────────
const legalCss = `
  @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@700;800&family=DM+Sans:wght@300;400;500&display=swap');
  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
  body { background: #f0f4f8; font-family: 'DM Sans', sans-serif; color: #1a1a2e; }
  .legal-nav { background: #0a0d12; padding: 0 40px; height: 64px; display: flex; align-items: center; justify-content: space-between; }
  .legal-logo { display: flex; align-items: center; text-decoration: none; }
  .legal-logo img { height: 150px; width: auto; object-fit: contain; }
  .legal-back { color: #6b7590; font-size: 14px; cursor: pointer; text-decoration: none; }
  .legal-back:hover { color: #e8ecf4; }
  .legal-page { max-width: 800px; margin: 0 auto; padding: 48px 24px 80px; }
  .legal-title { font-family: 'Plus Jakarta Sans', sans-serif; font-size: 36px; font-weight: 800; margin-bottom: 8px; color: #0a0d12; }
  .legal-updated { font-size: 13px; color: #999; margin-bottom: 40px; }
  .legal-section { margin-bottom: 36px; }
  .legal-h2 { font-family: 'Plus Jakarta Sans', sans-serif; font-size: 20px; font-weight: 700; margin-bottom: 12px; color: #0a0d12; padding-top: 8px; border-top: 2px solid #e8ecf4; }
  .legal-p { font-size: 15px; line-height: 1.75; color: #444; margin-bottom: 12px; }
  .legal-ul { padding-left: 20px; margin-bottom: 12px; }
  .legal-ul li { font-size: 15px; line-height: 1.75; color: #444; margin-bottom: 6px; }
  .legal-contact { background: #f8f9fa; border-radius: 12px; padding: 24px 28px; margin-top: 40px; }
  .legal-contact p { font-size: 15px; color: #444; line-height: 1.75; }
  a.legal-link { color: #00a87a; }
`;

function LegalLayout({ title, updated, children }) {
  return (
    <><style>{legalCss}</style>
    <nav className="legal-nav">
      <a className="legal-logo" href="/" onClick={e=>{e.preventDefault();window.location.href="/"}}><img src={require("./logo.png")} alt="GoApexPay" /></a>
      <a className="legal-back" href="/" onClick={e=>{e.preventDefault();window.location.href="/"}}>← Back to home</a>
    </nav>
    <div className="legal-page">
      <h1 className="legal-title">{title}</h1>
      <div className="legal-updated">Last updated: {updated}</div>
      {children}
    </div></>
  );
}

// ── PRIVACY POLICY ───────────────────────────────────────────
function PrivacyPolicyPage() {
  return (
    <LegalLayout title="Privacy Policy" updated="April 27, 2026">

      <div className="legal-section">
        <p className="legal-p">GoApexPay ("we," "our," or "us") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our invoicing and payment processing platform at <strong>www.goapexpay.com</strong>.</p>
        <p className="legal-p">By using GoApexPay, you agree to the collection and use of information in accordance with this policy.</p>
      </div>

      <div className="legal-section">
        <h2 className="legal-h2">1. Information We Collect</h2>
        <p className="legal-p"><strong>Account Information:</strong> When you create an account, we collect your name, email address, password, and business name.</p>
        <p className="legal-p"><strong>Business Information:</strong> We collect information you provide about your business, including address, phone number, logo, and tax settings.</p>
        <p className="legal-p"><strong>Client Data:</strong> We store information about your clients that you enter into the platform, including names, email addresses, phone numbers, and billing addresses.</p>
        <p className="legal-p"><strong>Invoice and Transaction Data:</strong> We store invoices, estimates, line items, and payment records associated with your account.</p>
        <p className="legal-p"><strong>Payment Processing Data:</strong> Credit card transactions are processed by CardPointe (a FiServ company). We do not store full card numbers. We store transaction references, last 4 digits, and authorization codes for record-keeping purposes.</p>
        <p className="legal-p"><strong>QuickBooks Data:</strong> If you connect QuickBooks Online, we store OAuth tokens to maintain the connection and sync data on your behalf. We access only the data necessary to provide the sync functionality.</p>
        <p className="legal-p"><strong>Usage Data:</strong> We may collect information about how you access and use the platform, including IP address, browser type, and pages visited.</p>
      </div>

      <div className="legal-section">
        <h2 className="legal-h2">2. How We Use Your Information</h2>
        <ul className="legal-ul">
          <li>To provide, operate, and maintain the GoApexPay platform</li>
          <li>To process payments on your behalf through CardPointe</li>
          <li>To send invoices, estimates, payment reminders, and receipts to your clients</li>
          <li>To sync data with QuickBooks Online when you have authorized the connection</li>
          <li>To communicate with you about your account, updates, and support</li>
          <li>To improve and develop our services</li>
          <li>To comply with legal obligations</li>
        </ul>
      </div>

      <div className="legal-section">
        <h2 className="legal-h2">3. How We Share Your Information</h2>
        <p className="legal-p">We do not sell your personal information. We share data only in the following circumstances:</p>
        <p className="legal-p"><strong>CardPointe / FiServ:</strong> Payment processing data is shared with CardPointe as necessary to process credit card transactions.</p>
        <p className="legal-p"><strong>Intuit / QuickBooks:</strong> If you connect QuickBooks Online, data is shared with Intuit's APIs as authorized by you.</p>
        <p className="legal-p"><strong>Resend:</strong> We use Resend to send transactional emails. Email content including invoice details may pass through their infrastructure.</p>
        <p className="legal-p"><strong>Infrastructure Providers:</strong> We use Railway (backend hosting) and Vercel (frontend hosting). Your data is stored on Railway's PostgreSQL database infrastructure.</p>
        <p className="legal-p"><strong>Legal Requirements:</strong> We may disclose your information if required by law or in response to a valid legal process.</p>
      </div>

      <div className="legal-section">
        <h2 className="legal-h2">4. Multi-Tenant Data Isolation</h2>
        <p className="legal-p">GoApexPay is a multi-tenant platform. Each business account is completely isolated from other accounts. Your clients, invoices, and settings are never accessible to other businesses using the platform. All database queries are scoped to your company ID.</p>
      </div>

      <div className="legal-section">
        <h2 className="legal-h2">5. Data Security</h2>
        <p className="legal-p">We implement industry-standard security measures to protect your data, including:</p>
        <ul className="legal-ul">
          <li>HTTPS encryption for all data in transit</li>
          <li>Encrypted passwords using bcrypt hashing</li>
          <li>JWT-based authentication with expiring tokens</li>
          <li>PCI DSS compliant payment processing through CardPointe's CardSecure tokenization</li>
          <li>Separate credential storage per merchant account</li>
        </ul>
        <p className="legal-p">No method of transmission over the internet or electronic storage is 100% secure. We strive to use commercially acceptable means to protect your data but cannot guarantee absolute security.</p>
      </div>

      <div className="legal-section">
        <h2 className="legal-h2">6. Data Retention</h2>
        <p className="legal-p">We retain your data for as long as your account is active. If you close your account, we will retain your data for 90 days before deletion to allow for data export. Payment transaction records may be retained longer as required by financial regulations.</p>
      </div>

      <div className="legal-section">
        <h2 className="legal-h2">7. Your Rights</h2>
        <p className="legal-p">You have the right to:</p>
        <ul className="legal-ul">
          <li>Access the personal data we hold about you</li>
          <li>Correct inaccurate data</li>
          <li>Request deletion of your data (subject to legal retention requirements)</li>
          <li>Export your data</li>
          <li>Disconnect third-party integrations (QuickBooks) at any time</li>
        </ul>
        <p className="legal-p">To exercise these rights, contact us at <a className="legal-link" href="mailto:billing@goapexpay.com">billing@goapexpay.com</a>.</p>
      </div>

      <div className="legal-section">
        <h2 className="legal-h2">8. Your Clients' Privacy</h2>
        <p className="legal-p">When you use GoApexPay to send invoices to your clients, you are acting as the data controller for your clients' information. You are responsible for ensuring you have a lawful basis for processing their data and that you comply with applicable privacy laws. GoApexPay acts as a data processor on your behalf.</p>
      </div>

      <div className="legal-section">
        <h2 className="legal-h2">9. Cookies</h2>
        <p className="legal-p">GoApexPay uses local storage to maintain your login session. We do not use third-party tracking cookies or advertising cookies.</p>
      </div>

      <div className="legal-section">
        <h2 className="legal-h2">10. Children's Privacy</h2>
        <p className="legal-p">GoApexPay is not directed to individuals under the age of 18. We do not knowingly collect personal information from children.</p>
      </div>

      <div className="legal-section">
        <h2 className="legal-h2">11. Changes to This Policy</h2>
        <p className="legal-p">We may update this Privacy Policy from time to time. We will notify you of significant changes by email or by a prominent notice on our platform. Your continued use of GoApexPay after changes constitutes acceptance of the updated policy.</p>
      </div>

      <div className="legal-contact">
        <h2 className="legal-h2" style={{borderTop:"none",paddingTop:0}}>Contact Us</h2>
        <p className="legal-p">If you have questions about this Privacy Policy, please contact us at:<br /><br />
          <strong>GoApexPay</strong><br />
          Email: <a className="legal-link" href="mailto:billing@goapexpay.com">billing@goapexpay.com</a><br />
          Website: <a className="legal-link" href="https://www.goapexpay.com">www.goapexpay.com</a>
        </p>
      </div>
    </LegalLayout>
  );
}

// ── TERMS OF SERVICE ─────────────────────────────────────────
function TermsOfServicePage() {
  return (
    <LegalLayout title="Terms of Service" updated="April 27, 2026">

      <div className="legal-section">
        <p className="legal-p">These Terms of Service ("Terms") govern your use of GoApexPay ("Service"), operated by GoApexPay ("we," "us," or "our"). By creating an account or using the Service, you agree to be bound by these Terms.</p>
      </div>

      <div className="legal-section">
        <h2 className="legal-h2">1. Description of Service</h2>
        <p className="legal-p">GoApexPay is a cloud-based invoicing and payment processing platform that allows businesses to create and send invoices, collect electronic payments, send e-signature estimates, and synchronize data with QuickBooks Online. The software is provided free of charge. Payment processing services are provided through CardPointe (FiServ) at a 2.75% flat rate per transaction.</p>
      </div>

      <div className="legal-section">
        <h2 className="legal-h2">2. Account Registration</h2>
        <p className="legal-p">You must create an account to use GoApexPay. You agree to provide accurate and complete information and to keep your account credentials secure. You are responsible for all activity that occurs under your account. You must be at least 18 years old and have the legal authority to enter into these Terms on behalf of your business.</p>
      </div>

      <div className="legal-section">
        <h2 className="legal-h2">3. Payment Processing</h2>
        <p className="legal-p">Credit card processing is provided through CardPointe, a FiServ company. By using payment processing features, you agree to CardPointe's terms of service and acceptable use policies. You must apply for and be approved for a merchant account through the CardPointe application process.</p>
        <p className="legal-p"><strong>Processing Rate:</strong> The current processing rate is 2.75% per transaction. This rate may be updated with 30 days notice.</p>
        <p className="legal-p"><strong>Surcharging:</strong> If you enable surcharging, you are responsible for ensuring compliance with card network rules and applicable state laws regarding surcharging in your jurisdiction. GoApexPay provides the technical capability but does not provide legal advice on surcharging compliance.</p>
        <p className="legal-p"><strong>Prohibited Transactions:</strong> You may not use GoApexPay to process payments for illegal goods or services, or for any activity that violates card network rules.</p>
      </div>

      <div className="legal-section">
        <h2 className="legal-h2">4. Free Software / Processing Requirement</h2>
        <p className="legal-p">The GoApexPay software is provided free of charge. Access to payment collection features requires an active CardPointe merchant account obtained through GoApexPay. New accounts receive a 14-day trial period with full access. After the trial period, online payment collection requires an active merchant account.</p>
      </div>

      <div className="legal-section">
        <h2 className="legal-h2">5. QuickBooks Integration</h2>
        <p className="legal-p">QuickBooks Online integration is provided as a convenience feature. GoApexPay is not affiliated with Intuit Inc. By connecting QuickBooks, you authorize GoApexPay to access and modify your QuickBooks data as described in the integration settings. You can disconnect QuickBooks at any time.</p>
      </div>

      <div className="legal-section">
        <h2 className="legal-h2">6. Electronic Signatures</h2>
        <p className="legal-p">GoApexPay provides electronic signature functionality for estimates. Electronic signatures facilitated through GoApexPay are intended to be legally binding under the Electronic Signatures in Global and National Commerce Act (E-SIGN Act) and similar laws. You are responsible for ensuring that electronic signatures are appropriate and enforceable for your specific use case and jurisdiction.</p>
      </div>

      <div className="legal-section">
        <h2 className="legal-h2">7. Communications</h2>
        <p className="legal-p"><strong>Transactional Emails:</strong> By creating an account, you agree to receive transactional emails necessary to operate the Service, including invoice notifications, payment receipts, estimate signing links, payment reminders, and account security notices. These emails are essential to the Service and cannot be opted out of while your account is active.</p>
        <p className="legal-p"><strong>Marketing Communications:</strong> If you opt in during registration, you agree to receive occasional marketing communications from GoApexPay and its affiliated business services — which may include payroll processing, bookkeeping, accounting, and other financial services that may benefit your business. You may unsubscribe from marketing emails at any time by clicking the unsubscribe link in any marketing email or by contacting us at <a className="legal-link" href="mailto:billing@goapexpay.com">billing@goapexpay.com</a>. Unsubscribing from marketing emails will not affect your receipt of transactional emails.</p>
      </div>

      <div className="legal-section">
        <h2 className="legal-h2">7. Your Data and Content</h2>
        <p className="legal-p">You retain ownership of all data you enter into GoApexPay, including client information, invoices, and business data. You grant GoApexPay a limited license to store, process, and transmit your data as necessary to provide the Service. You are responsible for ensuring you have the right to upload and process any data you enter into the platform.</p>
      </div>

      <div className="legal-section">
        <h2 className="legal-h2">8. Acceptable Use</h2>
        <p className="legal-p">You agree not to:</p>
        <ul className="legal-ul">
          <li>Use the Service for any illegal purpose or in violation of any laws</li>
          <li>Send spam, unsolicited emails, or harassing communications through the platform</li>
          <li>Attempt to gain unauthorized access to any part of the Service or other users' accounts</li>
          <li>Reverse engineer, decompile, or attempt to extract the source code of the Service</li>
          <li>Use the Service to process fraudulent transactions</li>
          <li>Interfere with or disrupt the integrity or performance of the Service</li>
        </ul>
      </div>

      <div className="legal-section">
        <h2 className="legal-h2">9. Service Availability</h2>
        <p className="legal-p">We strive to maintain high availability of the Service but do not guarantee uninterrupted access. We may perform maintenance, updates, or experience outages. We are not liable for any losses resulting from Service unavailability.</p>
      </div>

      <div className="legal-section">
        <h2 className="legal-h2">10. Limitation of Liability</h2>
        <p className="legal-p">To the maximum extent permitted by law, GoApexPay shall not be liable for any indirect, incidental, special, consequential, or punitive damages, including but not limited to loss of profits, data, or business opportunities, arising from your use of or inability to use the Service.</p>
        <p className="legal-p">Our total liability to you for any claim arising from these Terms or your use of the Service shall not exceed the amount you paid us in the 12 months preceding the claim.</p>
      </div>

      <div className="legal-section">
        <h2 className="legal-h2">11. Disclaimer of Warranties</h2>
        <p className="legal-p">The Service is provided "as is" and "as available" without warranties of any kind, either express or implied. We do not warrant that the Service will be error-free, secure, or continuously available.</p>
      </div>

      <div className="legal-section">
        <h2 className="legal-h2">12. Indemnification</h2>
        <p className="legal-p">You agree to indemnify and hold harmless GoApexPay and its affiliates from any claims, losses, or damages arising from your use of the Service, your violation of these Terms, or your violation of any third party's rights.</p>
      </div>

      <div className="legal-section">
        <h2 className="legal-h2">13. Termination</h2>
        <p className="legal-p">You may close your account at any time. We may suspend or terminate your account if you violate these Terms, engage in fraudulent activity, or if required by law. Upon termination, your data will be retained for 90 days before deletion.</p>
      </div>

      <div className="legal-section">
        <h2 className="legal-h2">14. Changes to Terms</h2>
        <p className="legal-p">We may update these Terms from time to time. We will notify you of material changes by email or through the platform. Continued use of the Service after changes constitutes acceptance of the updated Terms.</p>
      </div>

      <div className="legal-section">
        <h2 className="legal-h2">15. Governing Law</h2>
        <p className="legal-p">These Terms are governed by the laws of the United States and the state in which GoApexPay operates, without regard to conflict of law principles.</p>
      </div>

      <div className="legal-contact">
        <h2 className="legal-h2" style={{borderTop:"none",paddingTop:0}}>Contact Us</h2>
        <p className="legal-p">If you have questions about these Terms, please contact us at:<br /><br />
          <strong>GoApexPay</strong><br />
          Email: <a className="legal-link" href="mailto:billing@goapexpay.com">billing@goapexpay.com</a><br />
          Website: <a className="legal-link" href="https://www.goapexpay.com">www.goapexpay.com</a>
        </p>
      </div>
    </LegalLayout>
  );
}

// ── CLIENT PORTAL PAGE ───────────────────────────────────────
function ClientPortalPage() {
  const token = currentPath.split("/client/")[1];
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const API_BASE_CP = (process.env.REACT_APP_API_URL || "http://localhost:3001") + "/api";

  useEffect(() => {
    if (!token) { setError("Invalid portal link"); setLoading(false); return; }
    fetch(`${API_BASE_CP}/public/client/${token}`)
      .then(r => r.json())
      .then(d => { if (d.error) setError(d.error); else setData(d); setLoading(false); })
      .catch(() => { setError("Could not load portal"); setLoading(false); });
  }, [token]);

  const fmtcp = (n) => "$" + (parseFloat(n)||0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const fmtdcp = (d) => { if (!d) return "—"; try { const s = d.split('T')[0]; const [y,m,day] = s.split('-').map(Number); return new Date(y,m-1,day).toLocaleDateString("en-US",{year:"numeric",month:"short",day:"numeric"}); } catch { return d; } };

  const portalCss = `
    @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@700;800&family=DM+Sans:wght@300;400;500&display=swap');
    *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
    body { background: #f0f4f8; font-family: 'DM Sans', sans-serif; color: #1a1a2e; }
    .cp-page { max-width: 780px; margin: 0 auto; padding: 32px 20px 80px; }
    .cp-header { background: #0a0d12; border-radius: 12px; padding: 24px 32px; margin-bottom: 28px; display: flex; justify-content: space-between; align-items: center; }
    .cp-logo { font-family: 'Plus Jakarta Sans', sans-serif; font-size: 22px; font-weight: 800; color: #00d4aa; }
    .cp-logo span { color: #e8ecf4; }
    .cp-card { background: #fff; border-radius: 12px; padding: 24px 28px; margin-bottom: 20px; box-shadow: 0 2px 12px rgba(0,0,0,0.06); }
    .cp-card-title { font-family: 'Plus Jakarta Sans', sans-serif; font-size: 16px; font-weight: 700; margin-bottom: 16px; }
    .cp-stats { display: grid; grid-template-columns: repeat(3, 1fr); gap: 16px; }
    .cp-stat { background: #f8f9fa; border-radius: 10px; padding: 16px; text-align: center; }
    .cp-stat-label { font-size: 11px; color: #999; text-transform: uppercase; letter-spacing: 0.5px; margin-bottom: 6px; }
    .cp-stat-value { font-family: 'Plus Jakarta Sans', sans-serif; font-size: 22px; font-weight: 700; }
    .cp-table { width: 100%; border-collapse: collapse; }
    .cp-table th { padding: 10px 0; text-align: left; font-size: 11px; color: #999; text-transform: uppercase; border-bottom: 2px solid #f0f0f0; }
    .cp-table td { padding: 12px 0; border-bottom: 1px solid #f5f5f5; font-size: 14px; }
    .cp-badge { display: inline-block; padding: 3px 8px; border-radius: 10px; font-size: 11px; font-weight: 700; text-transform: uppercase; }
    .cp-badge-paid { background: rgba(0,212,170,0.12); color: #00a87a; }
    .cp-badge-pending { background: rgba(245,166,35,0.12); color: #b8760a; }
    .cp-badge-overdue { background: rgba(255,71,87,0.12); color: #ff4757; }
    .cp-badge-partial { background: rgba(79,142,247,0.12); color: #4f8ef7; }
    .cp-pay-btn { background: #00d4aa; color: #0a0d12; border: none; border-radius: 8px; padding: 7px 16px; font-size: 13px; font-weight: 700; cursor: pointer; font-family: 'DM Sans', sans-serif; text-decoration: none; display: inline-block; }
    .cp-spinner { width: 32px; height: 32px; border: 3px solid #eee; border-top-color: #00d4aa; border-radius: 50%; animation: cpspin 0.7s linear infinite; margin: 60px auto; display: block; }
    @keyframes cpspin { to { transform: rotate(360deg); } }
  `;

  if (loading) return <><style>{portalCss}</style><div className="cp-page"><div className="cp-spinner" /></div></>;
  if (error) return <><style>{portalCss}</style><div className="cp-page"><div className="cp-header"><div className="cp-logo"><img src={require("./logo.png")} alt="GoApexPay" /></div></div><div style={{textAlign:"center",padding:"60px 0"}}><div style={{fontSize:40,marginBottom:12}}>⚠️</div><p style={{color:"#666"}}>{error}</p></div></div></>;

  return (
    <><style>{portalCss}</style>
    <div className="cp-page">
      <div className="cp-header">
        {data.logo_data
          ? <img src={data.logo_data} alt="Logo" style={{maxHeight:50,maxWidth:160,objectFit:"contain"}} />
          : <div className="cp-logo"><img src={require("./logo.png")} alt="GoApexPay" /></div>}
        <div style={{textAlign:"right",fontSize:13,color:"#6b7590"}}>
          <div style={{fontWeight:600,color:"#e8ecf4"}}>{data.client.name}</div>
          <div>{data.client.company_name}</div>
        </div>
      </div>

      {/* Summary stats */}
      <div className="cp-card">
        <div className="cp-stats">
          <div className="cp-stat">
            <div className="cp-stat-label">Total Billed</div>
            <div className="cp-stat-value">{fmtcp(data.total_billed)}</div>
          </div>
          <div className="cp-stat">
            <div className="cp-stat-label">Total Paid</div>
            <div className="cp-stat-value" style={{color:"#00a87a"}}>{fmtcp(data.total_paid)}</div>
          </div>
          <div className="cp-stat">
            <div className="cp-stat-label">Outstanding</div>
            <div className="cp-stat-value" style={{color:parseFloat(data.total_outstanding)>0?"#ff4757":"#00a87a"}}>{fmtcp(data.total_outstanding)}</div>
          </div>
        </div>
      </div>

      {/* Invoice list */}
      <div className="cp-card">
        <div className="cp-card-title">Your Invoices</div>
        {data.invoices.length === 0 ? (
          <div style={{textAlign:"center",padding:"32px",color:"#999"}}>No invoices yet</div>
        ) : (
          <table className="cp-table">
            <thead><tr><th>Invoice</th><th>Date</th><th>Due</th><th>Amount</th><th>Status</th><th></th></tr></thead>
            <tbody>
              {data.invoices.map(inv => (
                <tr key={inv.id}>
                  <td><strong style={{fontSize:13}}>{inv.id}</strong></td>
                  <td style={{color:"#666"}}>{fmtdcp(inv.invoice_date)}</td>
                  <td style={{color:inv.status==="overdue"?"#ff4757":"#666"}}>{fmtdcp(inv.due_date)}</td>
                  <td><strong>{fmtcp(inv.total)}</strong>{parseFloat(inv.amount_paid)>0 && <div style={{fontSize:11,color:"#00a87a"}}>Paid: {fmtcp(inv.amount_paid)}</div>}</td>
                  <td><span className={`cp-badge cp-badge-${inv.status}`}>{inv.status}</span></td>
                  <td>
                    {(inv.status === "pending" || inv.status === "overdue" || inv.status === "partial") && parseFloat(inv.balance_due) > 0 && (
                      <a href={`${window.location.origin}/pay/${inv.id}`} className="cp-pay-btn">Pay {fmtcp(inv.balance_due)}</a>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div></>
  );
}

// ── ESTIMATE SIGN PAGE ───────────────────────────────────────
// This is the public customer-facing page for viewing and signing estimates

const signCss = `
  @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@700;800&family=DM+Sans:wght@300;400;500&display=swap');
  .sign-page { max-width: 780px; margin: 0 auto; padding: 32px 20px 80px; background: #f0f4f8; min-height: 100vh; font-family: 'DM Sans', sans-serif; }
  .sign-header { background: #0a0d12; border-radius: 12px; padding: 24px 32px; margin-bottom: 28px; display: flex; justify-content: space-between; align-items: center; }
  .sign-logo { font-family: 'Plus Jakarta Sans', sans-serif; font-size: 22px; font-weight: 800; color: #4f8ef7; }
  .sign-logo span { color: #e8ecf4; }
  .est-badge { background: rgba(79,142,247,0.15); color: #4f8ef7; padding: 6px 14px; border-radius: 20px; font-size: 12px; font-weight: 700; text-transform: uppercase; }
  .sign-card { background: #fff; border-radius: 12px; padding: 28px 32px; margin-bottom: 20px; box-shadow: 0 2px 12px rgba(0,0,0,0.06); }
  .sign-card-title { font-family: 'Plus Jakarta Sans', sans-serif; font-size: 18px; font-weight: 700; margin-bottom: 16px; }
  .sign-meta { display: grid; grid-template-columns: 1fr 1fr; gap: 20px; }
  .sign-meta-label { font-size: 11px; color: #999; text-transform: uppercase; letter-spacing: 0.5px; margin-bottom: 4px; }
  .sign-meta-value { font-size: 15px; font-weight: 600; }
  .sign-meta-sub { font-size: 13px; color: #666; margin-top: 2px; }
  .sign-table { width: 100%; border-collapse: collapse; }
  .sign-table th { padding: 10px 0; text-align: left; font-size: 11px; color: #999; text-transform: uppercase; border-bottom: 2px solid #f0f0f0; }
  .sign-table th:not(:first-child) { text-align: right; }
  .sign-table td { padding: 12px 0; border-bottom: 1px solid #f5f5f5; font-size: 14px; }
  .sign-table td:not(:first-child) { text-align: right; }
  .sign-totals { margin-top: 16px; }
  .sign-total-row { display: flex; justify-content: space-between; padding: 6px 0; font-size: 14px; color: #666; }
  .sign-total-row.grand { font-family: 'Plus Jakarta Sans', sans-serif; font-size: 20px; font-weight: 700; color: #1a1a2e; border-top: 2px solid #eee; padding-top: 12px; margin-top: 4px; }
  .sign-total-row.grand span:last-child { color: #4f8ef7; }
  .sign-canvas-wrap { border: 2px dashed #d0d7e3; border-radius: 10px; background: #fafbfc; position: relative; overflow: hidden; }
  .sign-hint { position: absolute; top: 50%; left: 50%; transform: translate(-50%,-50%); font-size: 14px; color: #bbb; pointer-events: none; text-align: center; }
  .sign-clear { background: none; border: 1px solid #ddd; border-radius: 6px; padding: 6px 14px; font-size: 12px; color: #666; cursor: pointer; margin-top: 8px; font-family: 'DM Sans', sans-serif; }
  .sign-name-input { width: 100%; border: 1px solid #ddd; border-radius: 8px; padding: 12px 16px; font-size: 15px; font-family: 'DM Sans', sans-serif; margin-top: 16px; outline: none; }
  .sign-name-input:focus { border-color: #4f8ef7; }
  .sign-accept-btn { width: 100%; background: #00d4aa; color: #0a0d12; border: none; border-radius: 10px; padding: 16px; font-size: 16px; font-weight: 700; font-family: 'Plus Jakarta Sans', sans-serif; cursor: pointer; margin-top: 20px; transition: all 0.2s; }
  .sign-accept-btn:hover:not(:disabled) { background: #00eabb; transform: translateY(-1px); }
  .sign-accept-btn:disabled { opacity: 0.5; cursor: not-allowed; }
  .sign-legal { font-size: 11px; color: #aaa; text-align: center; margin-top: 12px; line-height: 1.6; }
  .sign-success { text-align: center; padding: 60px 20px; }
  .sign-success-icon { font-size: 64px; margin-bottom: 20px; }
  .sign-success-title { font-family: 'Plus Jakarta Sans', sans-serif; font-size: 28px; font-weight: 800; color: #00d4aa; margin-bottom: 12px; }
  .sign-success-sub { font-size: 16px; color: #666; max-width: 420px; margin: 0 auto; line-height: 1.6; }
  .sign-loading { text-align: center; padding: 80px 20px; color: #999; }
  .sign-spinner { width: 32px; height: 32px; border: 3px solid #eee; border-top-color: #4f8ef7; border-radius: 50%; animation: sspin 0.7s linear infinite; margin: 0 auto 16px; }
  @keyframes sspin { to { transform: rotate(360deg); } }
  .sign-warning { background: #fff8e6; border: 1px solid #f5a623; border-radius: 8px; padding: 12px 16px; font-size: 13px; color: #b8760a; margin-bottom: 20px; }
  .sign-already { background: #f0fffe; border: 1px solid #00d4aa; border-radius: 10px; padding: 20px; text-align: center; color: #00a87a; font-weight: 600; }
`;

function SignaturePad({ onSign, onClear }) {
  const sigRef = useRef(null);
  const [hasSig, setHasSig] = useState(false);
  return (
    <div>
      <div className="sign-canvas-wrap">
        <canvas
          ref={sigRef}
          width={716}
          height={180}
          style={{ width: "100%", height: 180, touchAction: "none" }}
          onMouseDown={e => {
            const canvas = sigRef.current;
            const ctx = canvas.getContext("2d");
            const rect = canvas.getBoundingClientRect();
            const scaleX = canvas.width / rect.width;
            const scaleY = canvas.height / rect.height;
            ctx.strokeStyle = "#1a1a2e";
            ctx.lineWidth = 2;
            ctx.lineCap = "round";
            ctx.lineJoin = "round";
            let drawing = true;
            let lastX = (e.clientX - rect.left) * scaleX;
            let lastY = (e.clientY - rect.top) * scaleY;
            ctx.beginPath();
            ctx.moveTo(lastX, lastY);
            const onMove = mv => {
              if (!drawing) return;
              const x = (mv.clientX - rect.left) * scaleX;
              const y = (mv.clientY - rect.top) * scaleY;
              ctx.lineTo(x, y);
              ctx.stroke();
              lastX = x; lastY = y;
            };
            const onUp = () => {
              drawing = false;
              setHasSig(true);
              onSign(canvas.toDataURL("image/png"));
              document.removeEventListener("mousemove", onMove);
              document.removeEventListener("mouseup", onUp);
            };
            document.addEventListener("mousemove", onMove);
            document.addEventListener("mouseup", onUp);
          }}
          onTouchStart={e => {
            e.preventDefault();
            const canvas = sigRef.current;
            const ctx = canvas.getContext("2d");
            const rect = canvas.getBoundingClientRect();
            const scaleX = canvas.width / rect.width;
            const scaleY = canvas.height / rect.height;
            ctx.strokeStyle = "#1a1a2e";
            ctx.lineWidth = 2.5;
            ctx.lineCap = "round";
            ctx.lineJoin = "round";
            const touch = e.touches[0];
            ctx.beginPath();
            ctx.moveTo((touch.clientX - rect.left) * scaleX, (touch.clientY - rect.top) * scaleY);
            const onMove = mv => {
              mv.preventDefault();
              const t = mv.touches[0];
              const x = (t.clientX - rect.left) * scaleX;
              const y = (t.clientY - rect.top) * scaleY;
              ctx.lineTo(x, y);
              ctx.stroke();
            };
            const onEnd = () => {
              setHasSig(true);
              onSign(canvas.toDataURL("image/png"));
              document.removeEventListener("touchmove", onMove);
              document.removeEventListener("touchend", onEnd);
            };
            document.addEventListener("touchmove", onMove, { passive: false });
            document.addEventListener("touchend", onEnd);
          }}
        />
        {!hasSig && <div className="sign-hint">✍️ Sign here with your mouse or finger</div>}
      </div>
      <button className="sign-clear" onClick={() => {
        const canvas = sigRef.current;
        const ctx = canvas.getContext("2d");
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        setHasSig(false);
        onClear();
      }}>Clear Signature</button>
    </div>
  );
}

function EstimateSignPage() {
  const token = currentPath.split("/estimate/")[1];
  const [estimate, setEstimate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [signedBy, setSignedBy] = useState("");
  const [sigData, setSigData] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(null);

  useEffect(() => {
    if (!token) { setError("Invalid estimate link"); setLoading(false); return; }
    fetch(`${API_BASE}/public/estimate/${token}`)
      .then(r => r.json())
      .then(data => { if (data.error) setError(data.error); else setEstimate(data); setLoading(false); })
      .catch(() => { setError("Could not load estimate"); setLoading(false); });
  }, [token]);

  const handleAccept = async () => {
    if (!signedBy.trim()) return alert("Please enter your full name");
    if (!sigData) return alert("Please provide your signature");
    setSubmitting(true);
    try {
      const res = await fetch(`${API_BASE}/public/estimate/${token}/sign`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ signature_data: sigData, signed_by: signedBy }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error);
      setSuccess(data);
    } catch (err) { alert("Error: " + err.message); }
    finally { setSubmitting(false); }
  };

  const fmts = (n) => "$" + (parseFloat(n)||0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  if (loading) return <><style>{signCss}</style><div className="sign-page"><div className="sign-loading"><div className="sign-spinner" /><div>Loading estimate…</div></div></div></>;
  if (error) return <><style>{signCss}</style><div className="sign-page"><div style={{textAlign:"center",padding:"80px 20px"}}><div style={{fontSize:48,marginBottom:16}}>⚠️</div><h2>Not Found</h2><p style={{color:"#666",marginTop:8}}>{error}</p></div></div></>;

  if (success) return (
    <><style>{signCss}</style>
    <div className="sign-page">
      <div className="sign-header"><div className="sign-logo"><img src={require("./logo.png")} alt="GoApexPay" /></div></div>
      <div className="sign-success">
        <div className="sign-success-icon">✅</div>
        <div className="sign-success-title">Estimate Accepted!</div>
        <div className="sign-success-sub">Thank you! Your estimate has been accepted. Invoice <strong>{success.invoice_id}</strong> has been created and sent to your email.</div>
      </div>
    </div></>
  );

  const isExpired = estimate.expiry_date && new Date(estimate.expiry_date) < new Date();
  const alreadySigned = estimate.status === "accepted";

  return (
    <><style>{signCss}</style>
    <div className="sign-page">
      <div className="sign-header">
        <div className="sign-logo"><img src={require("./logo.png")} alt="GoApexPay" /></div>
        <div className="est-badge">Estimate {estimate.id}</div>
      </div>

      {isExpired && <div className="sign-warning">⚠️ This estimate expired on {estimate.expiry_date}. Please contact us for a new estimate.</div>}
      {alreadySigned && <div className="sign-already">✓ This estimate has already been accepted. An invoice has been created.</div>}

      <div className="sign-card">
        <div className="sign-meta">
          <div>
            <div className="sign-meta-label">Prepared For</div>
            <div className="sign-meta-value">{estimate.client_name}</div>
            <div className="sign-meta-sub">{estimate.client_email}</div>
          </div>
          <div>
            <div className="sign-meta-label">From</div>
            <div className="sign-meta-value">{estimate.company_name}</div>
            <div className="sign-meta-sub">{estimate.company_email}</div>
            <div style={{marginTop:12}}>
              <div className="sign-meta-label">Date</div>
              <div className="sign-meta-value" style={{fontSize:14}}>{estimate.estimate_date}</div>
              {estimate.expiry_date && <div className="sign-meta-sub">Valid until {estimate.expiry_date}</div>}
            </div>
          </div>
        </div>
      </div>

      <div className="sign-card">
        <div className="sign-card-title">Services & Items</div>
        <table className="sign-table">
          <thead><tr><th>Description</th><th>Qty</th><th>Rate</th><th>Amount</th></tr></thead>
          <tbody>
            {(estimate.lineItems||[]).map((item,i) => (
              <tr key={i}>
                <td>{item.description}</td>
                <td style={{textAlign:"right"}}>{item.quantity}</td>
                <td style={{textAlign:"right"}}>{fmts(item.unit_price)}</td>
                <td style={{textAlign:"right",fontWeight:600}}>{fmts(item.amount)}</td>
              </tr>
            ))}
          </tbody>
        </table>
        <div className="sign-totals">
          <div className="sign-total-row"><span>Subtotal</span><span>{fmts(estimate.subtotal)}</span></div>
          {parseFloat(estimate.tax_amount) > 0 && <div className="sign-total-row"><span>Tax ({estimate.tax_rate}%)</span><span>{fmts(estimate.tax_amount)}</span></div>}
          <div className="sign-total-row grand"><span>Total</span><span>{fmts(estimate.total)}</span></div>
        </div>
      </div>

      {estimate.notes && (
        <div className="sign-card">
          <div className="sign-card-title">Notes</div>
          <p style={{fontSize:14,color:"#555",lineHeight:1.6}}>{estimate.notes}</p>
        </div>
      )}

      {/* Show billing schedule if progress billing */}
      {estimate.billing_type === 'progress' && estimate.billing_schedule && (
        <div className="sign-card">
          <div className="sign-card-title">Payment Schedule</div>
          <p style={{fontSize:13,color:"#666",marginBottom:16}}>By accepting this estimate you agree to the following payment schedule:</p>
          {(typeof estimate.billing_schedule === 'string' ? JSON.parse(estimate.billing_schedule) : estimate.billing_schedule).map((m, i) => (
            <div key={i} style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"10px 0",borderBottom:"1px solid #f0f0f0"}}>
              <div style={{display:"flex",alignItems:"center",gap:10}}>
                <div style={{width:28,height:28,borderRadius:"50%",background:i===0?"#00d4aa":"#f0f0f0",color:i===0?"#0a0d12":"#666",display:"flex",alignItems:"center",justifyContent:"center",fontSize:12,fontWeight:700}}>
                  {String.fromCharCode(65+i)}
                </div>
                <div>
                  <div style={{fontWeight:600,fontSize:14}}>{m.name}</div>
                  <div style={{fontSize:12,color:"#999"}}>{i===0?"Due upon signing":"Due upon milestone completion"}</div>
                </div>
              </div>
              <div style={{textAlign:"right"}}>
                <div style={{fontWeight:700,color:i===0?"#00d4aa":"#1a1a2e",fontSize:16}}>{fmts(Math.round(parseFloat(estimate.total) * (m.percent/100) * 100)/100)}</div>
                <div style={{fontSize:12,color:"#999"}}>{m.percent}%</div>
              </div>
            </div>
          ))}
        </div>
      )}

      {!alreadySigned && !isExpired && (
        <div className="sign-card">
          <div className="sign-card-title">Accept & Sign</div>
          <p className="sign-legal" style={{textAlign:"left",color:"#555",marginBottom:16}}>By signing below you agree to the terms of this estimate and authorize the work described above.</p>
          <SignaturePad onSign={setSigData} onClear={() => setSigData(null)} />
          <input className="sign-name-input" placeholder="Type your full legal name" value={signedBy} onChange={e => setSignedBy(e.target.value)} />
          <button className="sign-accept-btn" onClick={handleAccept} disabled={submitting || !sigData || !signedBy.trim()}>
            {submitting ? "Processing…" : `✅ Accept Estimate — ${fmts(estimate.total)}`}
          </button>
          <div className="sign-legal">By clicking "Accept Estimate" you are providing an electronic signature legally binding under the E-SIGN Act. An invoice will be automatically created and emailed to you.</div>
        </div>
      )}
    </div></>
  );
}

// ── MAIN APP ─────────────────────────────────────────────────
function MainApp() {
  const [user, setUser] = useState(null);
  const [company, setCompany] = useState(null);
  const [authChecked, setAuthChecked] = useState(false);
  const [view, setView] = useState("dashboard");
  const [invoices, setInvoices] = useState([]);
  const [estimates, setEstimates] = useState([]);
  const [clients, setClients] = useState([]);
  const [items, setItems] = useState([]);
  const [staffUsers, setStaffUsers] = useState([]);
  const [invLoading, setInvLoading] = useState(false);
  const [clientLoading, setClientLoading] = useState(false);
  const [filter, setFilter] = useState("all");
  const [estFilter, setEstFilter] = useState("all");
  const [estSearch, setEstSearch] = useState("");
  const [qboConnected, setQboConnected] = useState(false);
  const [toast, setToast] = useState(null);
  const [showNewInvoice, setShowNewInvoice] = useState(false);
  const [showNewEstimate, setShowNewEstimate] = useState(false);
  const [showNewClient, setShowNewClient] = useState(false);
  const [editClient, setEditClient] = useState(null);
  const [viewInvoice, setViewInvoice] = useState(null);
  const [viewEstimate, setViewEstimate] = useState(null);
  const [payInvoice, setPayInvoice] = useState(null);
  const [showMerchantApp, setShowMerchantApp] = useState(false);
  const [showNewItem, setShowNewItem] = useState(false);

  // Settings form state
  const [settings, setSettings] = useState(null);
  const [settingsSaving, setSettingsSaving] = useState(false);
  const [newUser, setNewUser] = useState({ name: "", email: "", password: "", role: "staff" });
  const [changePw, setChangePw] = useState({ current: "", next: "", confirm: "" });

  const showToast = useCallback((msg, type = "success") => {
    setToast({ msg, type }); setTimeout(() => setToast(null), 4000);
  }, []);

  useEffect(() => {
    if (authToken) {
      api.get("/auth/me").then(u => { setUser(u); setCompany(u.company); setAuthChecked(true); }).catch(() => { api.setToken(null); setAuthChecked(true); });
    } else setAuthChecked(true);
  }, []);

  useEffect(() => {
    if (!user) return;
    loadInvoices(); loadClients(); loadEstimates(); loadItems();
    api.get("/qbo/status").then(s => setQboConnected(s.connected)).catch(() => {});
    api.get("/settings").then(s => setSettings(s)).catch(() => {});
    api.get("/settings/users").then(u => setStaffUsers(u)).catch(() => {});
  }, [user]);

  const loadInvoices = async () => { setInvLoading(true); try { setInvoices(await api.get("/invoices")); } catch (e) { showToast(e.message, "error"); } finally { setInvLoading(false); } };
  const loadEstimates = async () => { try { setEstimates(await api.get("/estimates")); } catch {} };
  const loadClients = async () => { setClientLoading(true); try { setClients(await api.get("/clients")); } catch (e) { showToast(e.message, "error"); } finally { setClientLoading(false); } };
  const loadItems = async () => { try { setItems(await api.get("/items")); } catch {} };

  const handleLogin = (u, co) => { setUser(u); setCompany(co); };

  const handleSaveSettings = async () => {
    setSettingsSaving(true);
    try {
      const updated = await api.put("/settings", settings);
      setSettings(updated);
      setCompany(c => ({ ...c, name: updated.name }));
      showToast("Settings saved!");
    } catch (err) { showToast(err.message, "error"); }
    finally { setSettingsSaving(false); }
  };

  const handleAddUser = async () => {
    if (!newUser.name || !newUser.email || !newUser.password) return showToast("All fields required", "error");
    try {
      const u = await api.post("/settings/users", newUser);
      setStaffUsers(p => [...p, u]);
      setNewUser({ name: "", email: "", password: "", role: "staff" });
      showToast(`${u.name} added successfully`);
    } catch (err) { showToast(err.message, "error"); }
  };

  const handleChangePassword = async () => {
    if (changePw.next !== changePw.confirm) return showToast("New passwords do not match", "error");
    if (changePw.next.length < 8) return showToast("Password must be at least 8 characters", "error");
    try {
      await api.put("/auth/password", { current_password: changePw.current, new_password: changePw.next });
      setChangePw({ current: "", next: "", confirm: "" });
      showToast("Password changed successfully");
    } catch (err) { showToast(err.message, "error"); }
  };

  const stats = {
    paid:    invoices.filter(i => i.status === "paid").reduce((s, i) => s + (parseFloat(i.total) || 0), 0),
    pending: invoices.filter(i => i.status === "pending").reduce((s, i) => s + (parseFloat(i.balance_due) || parseFloat(i.total) || 0), 0),
    overdue: invoices.filter(i => i.status === "overdue").reduce((s, i) => s + (parseFloat(i.balance_due) || parseFloat(i.total) || 0), 0),
  };

  if (!authChecked) return <><style>{css}</style><div className="loading" style={{ minHeight: "100vh" }}><div className="spinner" /></div></>;
  if (!user) return <><style>{css}</style><LoginScreen onLogin={handleLogin} /></>;

  return (
    <>
      <style>{css}</style>
      <div className="app">
        <aside className="sidebar">
          <div className="logo"><img src={require("./logo.png")} alt="GoApexPay" /></div>
          {[["dashboard","◈","Dashboard"],["invoices","◻","Invoices"],["estimates","◫","Estimates"],["clients","◉","Clients"],["items","◧","Items"],["integrations","⬡","Integrations"],["settings","◌","Settings"]].map(([id,icon,label]) => (
            <div key={id} className={`nav-item ${view === id ? "active" : ""}`} onClick={() => setView(id)}>
              <span className="nav-icon">{icon}</span>{label}
            </div>
          ))}
          <div className="sidebar-footer">
            <div className={`qbo-badge ${qboConnected ? "" : "off"}`} onClick={() => setView("integrations")}>
              <div className={`qbo-dot ${qboConnected ? "" : "off"}`} />
              {qboConnected ? "QuickBooks Connected" : "Connect QuickBooks"}
            </div>
            <div style={{ marginTop: 12, fontSize: 12, color: "var(--muted)" }}>
              <div style={{ fontWeight: 600, color: "var(--text)", marginBottom: 2 }}>{company?.name || user.name}</div>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <span>{user.name} · {user.role}</span>
                <span style={{ cursor: "pointer", color: "var(--overdue)" }} onClick={() => { api.setToken(null); setUser(null); setCompany(null); }}>Sign out</span>
              </div>
            </div>
          </div>
        </aside>

        <main className="main">
          {/* Trial / Processing Activation Banner */}
          {settings && !settings.processing_activated && (
            <div style={{background:"linear-gradient(135deg,rgba(245,166,35,0.15),rgba(245,166,35,0.05))",border:"1px solid rgba(245,166,35,0.4)",borderRadius:10,padding:"14px 20px",margin:"16px 24px 0",display:"flex",alignItems:"center",justifyContent:"space-between",gap:16}}>
              <div style={{display:"flex",alignItems:"center",gap:12}}>
                <span style={{fontSize:20}}>⚠️</span>
                <div>
                  <div style={{fontWeight:700,fontSize:14,color:"var(--pending)"}}>
                    {settings.trial_days_left > 0 ? `Free trial — ${settings.trial_days_left} days remaining` : "Trial expired"}
                  </div>
                  <div style={{fontSize:12,color:"var(--muted)",marginTop:2}}>
                    {settings.trial_days_left > 0
                      ? "Activate CardPointe processing to collect payments online and keep full access after your trial."
                      : "Your trial has ended. Add CardPointe credentials in Settings to continue collecting payments."}
                  </div>
                </div>
              </div>
              <button className="btn btn-primary btn-sm" style={{flexShrink:0}} onClick={() => setView("settings")}>
                Activate Processing →
              </button>
            </div>
          )}

          {/* DASHBOARD */}
          {view === "dashboard" && <>
            <div className="topbar">
              <div className="page-title">Dashboard <span>·</span> Overview</div>
              <div className="topbar-actions">
                <button className="btn btn-secondary" onClick={() => setShowNewEstimate(true)}>+ New Estimate</button>
                <button className="btn btn-primary" onClick={() => setShowNewInvoice(true)}>+ New Invoice</button>
              </div>
            </div>
            <div className="stats">
              <div className="stat-card green"><div className="stat-label">Total Collected</div><div className="stat-value">{fmt(stats.paid)}</div><div className="stat-sub">{invoices.filter(i => i.status === "paid").length} invoices paid</div></div>
              <div className="stat-card orange"><div className="stat-label">Awaiting Payment</div><div className="stat-value">{fmt(stats.pending)}</div><div className="stat-sub">{invoices.filter(i => i.status === "pending").length} invoices</div></div>
              <div className="stat-card red"><div className="stat-label">Overdue</div><div className="stat-value">{fmt(stats.overdue)}</div><div className="stat-sub">{invoices.filter(i => i.status === "overdue").length} invoices</div></div>
              <div className="stat-card blue"><div className="stat-label">Total Clients</div><div className="stat-value">{clients.length}</div><div className="stat-sub">{invoices.length} invoices total</div></div>
            </div>
            <InvoiceTable invoices={invoices.slice(0, 8)} loading={invLoading} filter={filter} setFilter={setFilter} onView={id => setViewInvoice(id)} onPay={setPayInvoice} onSend={id => setViewInvoice(id)} onRelease={loadInvoices} showToast={showToast} title="Recent Invoices" />
          </>}

          {/* INVOICES */}
          {view === "invoices" && <>
            <div className="topbar"><div className="page-title">Invoices</div><button className="btn btn-primary" onClick={() => setShowNewInvoice(true)}>+ New Invoice</button></div>
            <InvoiceTable invoices={invoices} loading={invLoading} filter={filter} setFilter={setFilter} onView={id => setViewInvoice(id)} onPay={setPayInvoice} onSend={id => setViewInvoice(id)} onRelease={loadInvoices} showToast={showToast} title="All Invoices" />
          </>}

          {/* ESTIMATES */}
          {view === "estimates" && <>
            <div className="topbar"><div className="page-title">Estimates</div><button className="btn btn-primary" onClick={() => setShowNewEstimate(true)}>+ New Estimate</button></div>
            <div className="table-card">
              <div className="table-header">
                <div className="table-title">All Estimates</div>
                <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                  <input
                    placeholder="Search estimates…"
                    value={estSearch}
                    onChange={e => setEstSearch(e.target.value)}
                    style={{ background: "var(--surface2)", border: "1px solid var(--border)", borderRadius: 8, padding: "6px 12px", fontSize: 13, color: "var(--text)", width: 180, outline: "none" }}
                  />
                  <div className="filters">{["all","draft","sent","accepted"].map(f => <button key={f} className={`filter-btn ${estFilter===f?"active":""}`} onClick={() => setEstFilter(f)}>{f.charAt(0).toUpperCase()+f.slice(1)}</button>)}</div>
                </div>
              </div>
              {estimates.length === 0 ? <div className="empty"><div className="empty-icon">◫</div><div>No estimates yet</div></div> : (
                <table>
                  <thead><tr><th>ID</th><th>Client</th><th>Date</th><th>Expires</th><th>Amount</th><th>Status</th><th>Actions</th></tr></thead>
                  <tbody>
                    {estimates
                      .filter(e => estFilter === "all" || e.status === estFilter)
                      .filter(e => !estSearch || e.id?.toLowerCase().includes(estSearch.toLowerCase()) || e.client_name?.toLowerCase().includes(estSearch.toLowerCase()))
                      .map(est => (
                      <tr key={est.id}>
                        <td><strong style={{ fontFamily: "var(--font-head)", fontSize: 13 }}>{est.id}</strong></td>
                        <td>{est.client_name}</td>
                        <td style={{ color: "var(--muted)" }}>{fmtDate(est.estimate_date)}</td>
                        <td style={{ color: "var(--muted)" }}>{est.expiry_date ? fmtDate(est.expiry_date) : "—"}</td>
                        <td><span className="amount">{fmt(est.total)}</span></td>
                        <td><span className={`status-badge status-${est.status}`}>{est.status}</span></td>
                        <td>
                          <button className="action-btn" onClick={() => setViewEstimate(est.id)}>View</button>
                          {!est.converted_to && !est.sign_token &&
                            <button className="action-btn" onClick={async () => {
                              try {
                                const r = await api.post(`/estimates/${est.id}/send`);
                                showToast(`✅ Estimate emailed to ${r.sentTo || "client"}`);
                                loadEstimates();
                              } catch (err) { showToast(err.message, "error"); }
                            }}>📧 Send</button>
                          }
                          {est.sign_token && !est.converted_to &&
                            <button className="action-btn" onClick={() => {
                              const link = `${window.location.origin}/estimate/${est.sign_token}`;
                              navigator.clipboard.writeText(link);
                              showToast("Sign link copied to clipboard!");
                            }}>🔗 Link</button>
                          }
                          {!est.converted_to
                            ? <button className="action-btn" onClick={async () => { try { const r = await api.post(`/estimates/${est.id}/convert`); showToast(`Converted → ${r.invoice_id}`); loadInvoices(); loadEstimates(); } catch (err) { showToast(err.message, "error"); } }}>→ Invoice</button>
                            : <span style={{ fontSize: 12, color: "var(--muted)" }}>→ {est.converted_to}</span>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </>}

          {/* CLIENTS */}
          {view === "clients" && <>
            <div className="topbar"><div className="page-title">Clients</div><button className="btn btn-primary" onClick={() => setShowNewClient(true)}>+ Add Client</button></div>
            <div className="table-card">
              <div className="table-header"><div className="table-title">Client Directory</div></div>
              {clientLoading ? <div className="loading"><div className="spinner" />Loading…</div>
                : clients.length === 0 ? <div className="empty"><div className="empty-icon">◉</div><div>No clients yet</div></div>
                : (
                  <table>
                    <thead><tr><th>Name</th><th>Email</th><th>Phone</th><th>Invoices</th><th>Total Billed</th><th>QBO</th><th>Actions</th></tr></thead>
                    <tbody>
                      {clients.map(c => (
                        <tr key={c.id}>
                          <td><strong>{c.name}</strong></td>
                          <td style={{ color: "var(--muted)" }}>{c.email || "—"}</td>
                          <td style={{ color: "var(--muted)" }}>{c.phone || "—"}</td>
                          <td>{c.invoice_count || 0}</td>
                          <td><span className="amount">{fmt(c.total_billed || 0)}</span></td>
                          <td>{c.qbo_customer_id ? <span style={{ color: "var(--paid)", fontSize: 12 }}>✓ Synced</span> : <span style={{ color: "var(--muted)", fontSize: 12 }}>—</span>}</td>
                          <td>
                            <button className="action-btn" onClick={() => setEditClient(c)}>Edit</button>
                            <button className="action-btn danger" onClick={async () => { if (!window.confirm(`Delete ${c.name}?`)) return; try { await api.delete(`/clients/${c.id}`); setClients(p => p.filter(x => x.id !== c.id)); showToast("Client deleted"); } catch (err) { showToast(err.message, "error"); } }}>Delete</button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
            </div>
          </>}

          {/* ITEMS */}
          {view === "items" && <>
            <div className="topbar">
              <div className="page-title">Items <span>·</span> Products & Services</div>
              <button className="btn btn-primary" onClick={() => setShowNewItem(true)}>+ New Item</button>
            </div>
            <div className="table-card">
              <div className="table-header">
                <div className="table-title">Item List</div>
                <div style={{ fontSize: 13, color: "var(--muted)" }}>{items.length} items — select when creating invoices & estimates</div>
              </div>
              {items.length === 0 ? (
                <div className="empty">
                  <div className="empty-icon">◧</div>
                  <div>No items yet</div>
                  <div style={{ fontSize: 13, marginTop: 8 }}>Import from QuickBooks or click <strong>+ New Item</strong> to create one</div>
                </div>
              ) : (
                <table>
                  <thead><tr><th>Name</th><th>Description</th><th>Type</th><th>Unit Price</th><th>Tax</th><th>QBO</th><th>Actions</th></tr></thead>
                  <tbody>
                    {items.map(item => (
                      <tr key={item.id}>
                        <td><strong>{item.name}</strong></td>
                        <td style={{ color: "var(--muted)", fontSize: 13 }}>{item.description || "—"}</td>
                        <td><span className="status-badge status-draft">{item.type}</span></td>
                        <td><span className="amount">{fmt(item.unit_price)}</span></td>
                        <td>
                          {item.taxable
                            ? <span style={{ fontSize: 11, background: "rgba(245,166,35,0.12)", color: "var(--pending)", padding: "2px 8px", borderRadius: 10, fontWeight: 600 }}>🔶 Taxable</span>
                            : <span style={{ color: "var(--muted)", fontSize: 12 }}>Non-taxable</span>}
                        </td>
                        <td>{item.qbo_item_id ? <span style={{ color: "var(--paid)", fontSize: 12 }}>✓ Synced</span> : <span style={{ color: "var(--muted)", fontSize: 12 }}>—</span>}</td>
                        <td>
                          <button className="action-btn danger" onClick={async () => {
                            if (!window.confirm(`Remove "${item.name}"?`)) return;
                            try { await api.delete(`/items/${item.id}`); setItems(p => p.filter(i => i.id !== item.id)); showToast("Item removed"); }
                            catch (err) { showToast(err.message, "error"); }
                          }}>Remove</button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </>}

          {/* INTEGRATIONS */}
          {view === "integrations" && <>
            <div className="topbar"><div className="page-title">Integrations</div></div>
            <div className="int-card">
              <div className="int-header">
                <div><div className="int-title">🟢 QuickBooks Online</div><div style={{ fontSize: 13, color: "var(--muted)", marginTop: 4 }}>Sync customers, invoices, and payments automatically</div></div>
                <div className={`int-status ${qboConnected ? "int-connected" : "int-disconnected"}`}>{qboConnected ? "✓ Connected" : "Not Connected"}</div>
              </div>
              {qboConnected ? (
                <div>
                  <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                    <button className="btn btn-primary btn-sm" onClick={async () => {
                      showToast("Importing from QuickBooks…", "warning");
                      try {
                        const r = await api.post("/qbo/import", { customers: true, invoices: true });
                        showToast(`✓ ${r.summary}`);
                        loadClients();
                        loadInvoices();
                      } catch (err) { showToast(err.message, "error"); }
                    }}>↓ Import Customers & Invoices</button>
                    <button className="btn btn-primary btn-sm" onClick={async () => {
                      showToast("Importing items from QuickBooks…", "warning");
                      try {
                        const r = await api.post("/qbo/import-items");
                        showToast(`✓ ${r.summary}`);
                        loadItems();
                      } catch (err) { showToast(err.message, "error"); }
                    }}>↓ Import Items</button>
                    <button className="btn btn-secondary btn-sm" onClick={async () => { try { const r = await api.post("/qbo/sync-all"); showToast(`Pushed: ${r.clients} clients, ${r.invoices} invoices to QBO`); } catch (err) { showToast(err.message, "error"); } }}>↑ Push to QBO</button>
                    <button className="btn btn-secondary btn-sm" onClick={async () => { try { const c = await api.get("/qbo/company"); showToast(`Connected to: ${c.CompanyName}`); } catch (err) { showToast(err.message, "error"); } }}>Test Connection</button>
                    <button className="btn btn-secondary btn-sm" onClick={async () => { await api.post("/qbo/disconnect"); setQboConnected(false); showToast("QuickBooks disconnected", "warning"); }}>Disconnect</button>
                  </div>
                  <div className="info-box green" style={{ marginTop: 14 }}>
                    <strong>↓ Import Customers & Invoices</strong> — pulls existing customers and invoices from QBO into Apex. Safe to run multiple times.<br /><br />
                    <strong>↓ Import Items</strong> — pulls your products & services list from QBO so you can pick them when creating invoices.<br /><br />
                    <strong>↑ Push to QBO</strong> — pushes new Apex clients and invoices up to QuickBooks. Also runs automatically when you create records.
                  </div>
                </div>
              ) : (
                <div>
                  <button className="btn btn-primary btn-sm" onClick={async () => { try { const r = await api.get("/qbo/connect"); window.open(r.authUrl, "_blank"); showToast("QBO auth window opened — approve then return here", "warning"); } catch (err) { showToast(err.message, "error"); } }}>Connect via OAuth2</button>
                  <div className="info-box yellow" style={{ marginTop: 12 }}>Get your free QBO sandbox at <strong>developer.intuit.com</strong>, add your Client ID + Secret to the backend <code>.env</code> file, then click Connect.</div>
                </div>
              )}
            </div>
            <div className="int-card">
              <div className="int-header">
                <div><div className="int-title">💳 CardPointe (FiServ)</div><div style={{ fontSize: 13, color: "var(--muted)", marginTop: 4 }}>Credit card processing at your rates</div></div>
                <div className={`int-status ${settings?.cardpointe_merchant_id ? "int-connected" : "int-disconnected"}`}>
                  {settings?.cardpointe_merchant_id ? "✓ Configured" : "Not Configured"}
                </div>
              </div>
              {settings?.cardpointe_merchant_id ? (
                <div>
                  <div className="info-box green">CardPointe Merchant ID is configured. Payments will process through your account.</div>
                  <div style={{ marginTop: 12 }}>
                    <button className="btn btn-secondary btn-sm" onClick={() => setView("settings")}>Update Credentials in Settings</button>
                    <button className="btn btn-secondary btn-sm" style={{ marginLeft: 8 }} onClick={async () => {
                      try { const r = await api.get("/onboarding/check-status"); showToast(`Status: ${r.status}`); } catch (err) { showToast(err.message, "error"); }
                    }}>Check Approval Status</button>
                  </div>
                </div>
              ) : (
                <div>
                  <p style={{ fontSize: 14, color: "var(--muted)", marginBottom: 16, lineHeight: 1.6 }}>
                    Accept credit card payments through CardPointe (FiServ) at competitive rates. Already have a merchant account? Enter your credentials in Settings. Need to apply for processing?
                  </p>
                  <div style={{ display: "flex", gap: 10 }}>
                    <button className="btn btn-primary" onClick={() => setShowMerchantApp(true)}>
                      Apply for Credit Card Processing
                    </button>
                    <button className="btn btn-secondary" onClick={() => setView("settings")}>
                      I Have Credentials
                    </button>
                  </div>
                  <div className="info-box yellow" style={{ marginTop: 12 }}>
                    Applying takes about 5 minutes. You'll receive a link to complete and sign your merchant agreement. Approval typically takes 1-2 business days.
                  </div>
                </div>
              )}
            </div>
          </>}

          {/* SETTINGS */}
          {view === "settings" && <>
            <div className="topbar"><div className="page-title">Settings</div><button className="btn btn-primary" onClick={handleSaveSettings} disabled={settingsSaving}>{settingsSaving ? "Saving…" : "Save Changes"}</button></div>

            {/* Business Info */}
            <div className="int-card">
              <div className="settings-title">Business Info</div>

              {/* Logo Upload */}
              <div style={{ marginBottom: 20, padding: "16px 20px", background: "var(--surface2)", borderRadius: 10, border: "1px solid var(--border)" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 20 }}>
                  <div style={{ width: 80, height: 80, borderRadius: 8, background: "var(--surface)", border: "1px solid var(--border)", display: "flex", alignItems: "center", justifyContent: "center", overflow: "hidden", flexShrink: 0 }}>
                    {settings?.logo_data
                      ? <img src={settings.logo_data} alt="Logo" style={{ width: "100%", height: "100%", objectFit: "contain" }} />
                      : <span style={{ fontSize: 28 }}>🏢</span>}
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 4 }}>Company Logo</div>
                    <div style={{ fontSize: 12, color: "var(--muted)", marginBottom: 10 }}>Appears on invoices, estimates, and emails. PNG or JPG, under 750KB recommended.</div>
                    <div style={{ display: "flex", gap: 8 }}>
                      <label className="btn btn-secondary btn-sm" style={{ cursor: "pointer" }}>
                        📁 Upload Logo
                        <input type="file" accept="image/*" style={{ display: "none" }} onChange={e => {
                          const file = e.target.files[0];
                          if (!file) return;
                          if (file.size > 1000000) return showToast("Image too large. Please use an image under 750KB.", "error");
                          const reader = new FileReader();
                          reader.onload = ev => setSettings(p => ({ ...p, logo_data: ev.target.result }));
                          reader.readAsDataURL(file);
                        }} />
                      </label>
                      {settings?.logo_data && (
                        <button className="btn btn-secondary btn-sm" onClick={() => setSettings(p => ({ ...p, logo_data: null }))}>Remove</button>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              <div className="form-grid">
                <div className="form-group"><label>Company Name</label><input value={settings?.name||""} onChange={e => setSettings(p => ({...p, name: e.target.value}))} /></div>
                <div className="form-group"><label>Email</label><input type="email" value={settings?.email||""} onChange={e => setSettings(p => ({...p, email: e.target.value}))} /></div>
                <div className="form-group"><label>Phone</label><input value={settings?.phone||""} onChange={e => setSettings(p => ({...p, phone: e.target.value}))} /></div>
                <div className="form-group"><label>Website</label><input value={settings?.website||""} onChange={e => setSettings(p => ({...p, website: e.target.value}))} /></div>
                <div className="form-group"><label>Address</label><input value={settings?.address||""} onChange={e => setSettings(p => ({...p, address: e.target.value}))} /></div>
                <div className="form-group"><label>City</label><input value={settings?.city||""} onChange={e => setSettings(p => ({...p, city: e.target.value}))} /></div>
                <div className="form-group"><label>State</label><input value={settings?.state||""} onChange={e => setSettings(p => ({...p, state: e.target.value}))} /></div>
                <div className="form-group"><label>ZIP</label><input value={settings?.zip||""} onChange={e => setSettings(p => ({...p, zip: e.target.value}))} /></div>
              </div>
            </div>

            {/* Invoice Defaults */}
            <div className="int-card">
              <div className="settings-title">Invoice Defaults</div>
              <div className="form-grid">
                <div className="form-group"><label>Net Terms (days)</label>
                  <select value={settings?.default_net_terms||30} onChange={e => setSettings(p => ({...p, default_net_terms: parseInt(e.target.value)}))}>
                    <option value={0}>Due on Receipt</option><option value={15}>Net 15</option><option value={30}>Net 30</option><option value={60}>Net 60</option>
                  </select>
                </div>
                <div className="form-group"><label>Default Tax Rate (%)</label><input type="number" min="0" step="0.1" value={settings?.default_tax_rate||0} onChange={e => setSettings(p => ({...p, default_tax_rate: parseFloat(e.target.value)||0}))} /></div>
                <div className="form-group"><label>Invoice Prefix</label><input value={settings?.invoice_prefix||"INV"} onChange={e => setSettings(p => ({...p, invoice_prefix: e.target.value}))} /></div>
                <div className="form-group"><label>Estimate Prefix</label><input value={settings?.estimate_prefix||"EST"} onChange={e => setSettings(p => ({...p, estimate_prefix: e.target.value}))} /></div>
                <div className="form-group full"><label>Default Invoice Notes / Terms</label><textarea value={settings?.invoice_notes||""} onChange={e => setSettings(p => ({...p, invoice_notes: e.target.value}))} placeholder="e.g. Payment due within 30 days. Thank you for your business!" /></div>
              </div>
            </div>

            {/* CardPointe Credentials */}
            <div className="int-card">
              <div className="settings-title">CardPointe Payment Processing</div>
              <div className="form-grid">
                <div className="form-group"><label>Merchant ID</label><input value={settings?.cardpointe_merchant_id||""} onChange={e => setSettings(p => ({...p, cardpointe_merchant_id: e.target.value}))} placeholder="Your CardPointe Merchant ID" /></div>
                <div className="form-group"><label>API Key {settings?.cardpointe_api_key_set ? <span style={{color:"var(--paid)",fontWeight:400}}>✓ Set</span> : <span style={{color:"var(--muted)",fontWeight:400}}>Not set</span>}</label><input type="password" onChange={e => setSettings(p => ({...p, cardpointe_api_key: e.target.value}))} placeholder={settings?.cardpointe_api_key_set ? "Leave blank to keep existing" : "Enter API key"} /></div>
                <div className="form-group"><label>Environment</label>
                  <select value={settings?.cardpointe_env||"sandbox"} onChange={e => setSettings(p => ({...p, cardpointe_env: e.target.value}))}>
                    <option value="sandbox">Sandbox (Testing)</option><option value="production">Production (Live)</option>
                  </select>
                </div>
                <div className="form-group"><label>Site ID</label><input value={settings?.cardpointe_site_id||"fts-uat"} onChange={e => setSettings(p => ({...p, cardpointe_site_id: e.target.value}))} /></div>
              </div>
              <div className="info-box yellow" style={{marginTop:12}}>Your CardPointe credentials are stored securely in the database, not in code files. Each business has their own separate credentials.</div>
            </div>

            {/* Payment Fee Model */}
            <div className="int-card">
              <div className="settings-title">Payment Fee Model</div>
              <p style={{fontSize:13,color:"var(--muted)",marginBottom:20,lineHeight:1.6}}>Choose how credit card processing fees are handled. This applies to all online payments through your payment portal.</p>

              {[
                { value: "absorb", label: "I absorb the fee", icon: "🏢", desc: "You pay the processing fee. Customers see clean prices with no additional charges. Best for premium businesses or when you want the simplest customer experience." },
                { value: "surcharge", label: "Customer pays (Surcharge)", icon: "💳", desc: "A card processing fee is added to the customer's total at checkout. Customers are shown the breakdown before paying and can pay by check to avoid the fee." },
                { value: "cash_discount", label: "Cash Discount", icon: "💵", desc: "Show two prices — a lower cash/check price and a card price. Customers choose at checkout. Common for contractors, auto shops, and medical offices." },
              ].map(opt => (
                <div key={opt.value} onClick={() => setSettings(p => ({...p, fee_model: opt.value}))} style={{display:"flex",alignItems:"flex-start",gap:14,padding:"16px",borderRadius:10,border:`2px solid ${settings?.fee_model === opt.value ? "var(--accent)" : "var(--border)"}`,background:settings?.fee_model === opt.value ? "rgba(0,212,170,0.05)" : "var(--surface2)",cursor:"pointer",marginBottom:10,transition:"all 0.2s"}}>
                  <div style={{fontSize:24,flexShrink:0,marginTop:2}}>{opt.icon}</div>
                  <div style={{flex:1}}>
                    <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:4}}>
                      <div style={{fontWeight:700,fontSize:14}}>{opt.label}</div>
                      {settings?.fee_model === opt.value && <span style={{fontSize:11,background:"var(--accent)",color:"#0a0d12",padding:"2px 8px",borderRadius:10,fontWeight:700}}>SELECTED</span>}
                    </div>
                    <div style={{fontSize:13,color:"var(--muted)",lineHeight:1.5}}>{opt.desc}</div>
                  </div>
                </div>
              ))}

              {/* Surcharge rate — locked, shown for info only */}
              {(settings?.fee_model === "surcharge" || settings?.fee_model === "cash_discount") && (
                <div style={{marginTop:16,padding:"16px 20px",background:"var(--surface2)",borderRadius:10,border:"1px solid var(--border)"}}>
                  <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:12}}>
                    <div>
                      <div style={{fontWeight:700,fontSize:14}}>Card Processing Rate</div>
                      <div style={{fontSize:12,color:"var(--muted)",marginTop:2}}>Applied to all card payments through your payment portal</div>
                    </div>
                    <div style={{fontSize:28,fontWeight:800,color:"var(--accent)",fontFamily:"var(--font-head)"}}>
                      {settings?.surcharge_percent || 2.75}%
                    </div>
                  </div>
                  <div className="form-group" style={{marginBottom:0}}>
                    <label>Disclosure Text <span style={{fontWeight:400,color:"var(--muted)"}}>shown to customers on payment portal</span></label>
                    <input value={settings?.surcharge_disclosure||""} onChange={e => setSettings(p => ({...p, surcharge_disclosure: e.target.value}))} placeholder={`A ${settings?.surcharge_percent||2.75}% card processing fee applies. Pay by check to avoid this fee.`} />
                  </div>
                  <div className="info-box green" style={{marginTop:12}}>
                    The processing rate is set at <strong>{settings?.surcharge_percent || 2.75}%</strong> — lower than QuickBooks Payments (2.9%). When customers pay by card this fee is added to their total automatically. Your business pays nothing.
                  </div>
                </div>
              )}
            </div>

            {/* Payment Reminders */}
            <div className="int-card">
              <div className="settings-title">Payment Reminders</div>
              <p style={{fontSize:13,color:"var(--muted)",marginBottom:16,lineHeight:1.6}}>Automatically email clients when invoices are due or overdue. Each reminder is sent once — clients won't receive duplicates.</p>
              <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",padding:"14px 0",borderBottom:"1px solid var(--border)"}}>
                <div>
                  <div style={{fontWeight:600,fontSize:14}}>Enable Automatic Reminders</div>
                  <div style={{fontSize:12,color:"var(--muted)",marginTop:2}}>Email clients about upcoming and overdue invoices automatically</div>
                </div>
                <div onClick={() => setSettings(p => ({...p, reminders_enabled: !p.reminders_enabled}))}
                  style={{width:44,height:24,borderRadius:12,background:settings?.reminders_enabled?"var(--accent)":"var(--border)",cursor:"pointer",position:"relative",transition:"background 0.2s",flexShrink:0}}>
                  <div style={{width:18,height:18,borderRadius:"50%",background:"#fff",position:"absolute",top:3,left:settings?.reminders_enabled?23:3,transition:"left 0.2s"}} />
                </div>
              </div>
              {settings?.reminders_enabled && (
                <div style={{marginTop:16}}>
                  {[
                    { key: "reminder_days_before", label: "Days before due date", desc: "Send a reminder this many days before due", type: "number", default: 3 },
                    { key: "reminder_day_of", label: "Remind on due date", desc: "Send a reminder on the day the invoice is due", type: "toggle" },
                    { key: "reminder_days_after_1", label: "First overdue reminder (days past due)", desc: "Send first overdue notice this many days after due date", type: "number", default: 7 },
                    { key: "reminder_days_after_2", label: "Second overdue reminder (days past due)", desc: "Send final overdue notice this many days after due date", type: "number", default: 14 },
                  ].map(opt => (
                    <div key={opt.key} style={{display:"flex",alignItems:"center",justifyContent:"space-between",padding:"12px 0",borderBottom:"1px solid rgba(37,44,58,0.4)"}}>
                      <div style={{flex:1,marginRight:16}}>
                        <div style={{fontWeight:600,fontSize:13}}>{opt.label}</div>
                        <div style={{fontSize:12,color:"var(--muted)",marginTop:2}}>{opt.desc}</div>
                      </div>
                      {opt.type === "toggle" ? (
                        <div onClick={() => setSettings(p => ({...p, [opt.key]: !p[opt.key]}))}
                          style={{width:44,height:24,borderRadius:12,background:settings?.[opt.key]?"var(--accent)":"var(--border)",cursor:"pointer",position:"relative",transition:"background 0.2s",flexShrink:0}}>
                          <div style={{width:18,height:18,borderRadius:"50%",background:"#fff",position:"absolute",top:3,left:settings?.[opt.key]?23:3,transition:"left 0.2s"}} />
                        </div>
                      ) : (
                        <div style={{display:"flex",alignItems:"center",gap:6,flexShrink:0}}>
                          <input type="number" min="1" max="90" value={settings?.[opt.key] ?? opt.default} onChange={e => setSettings(p => ({...p, [opt.key]: parseInt(e.target.value)||opt.default}))} style={{width:64,textAlign:"center"}} />
                          <span style={{fontSize:12,color:"var(--muted)"}}>days</span>
                        </div>
                      )}
                    </div>
                  ))}
                  <div className="info-box green" style={{marginTop:14}}>Reminders run daily. Each reminder is sent only once per invoice — no spam to your clients.</div>
                </div>
              )}
            </div>

            <div className="int-card">
              <div className="settings-title">Change Password</div>
              <div className="form-grid">
                <div className="form-group"><label>Current Password</label><input type="password" value={changePw.current} onChange={e => setChangePw(p => ({...p, current: e.target.value}))} /></div>
                <div className="form-group"><label>New Password</label><input type="password" value={changePw.next} onChange={e => setChangePw(p => ({...p, next: e.target.value}))} /></div>
                <div className="form-group"><label>Confirm New Password</label><input type="password" value={changePw.confirm} onChange={e => setChangePw(p => ({...p, confirm: e.target.value}))} /></div>
              </div>
              <div style={{marginTop:14}}>
                <button className="btn btn-secondary btn-sm" onClick={handleChangePassword}>Update Password</button>
              </div>
            </div>

            {/* User Management — admin only */}
            {user.role === "admin" && (
              <div className="int-card">
                <div className="settings-title">Team Members</div>
                <table style={{width:"100%",borderCollapse:"collapse",marginBottom:20}}>
                  <thead><tr>
                    <th style={{padding:"8px 0",textAlign:"left",fontSize:11,color:"var(--muted)",textTransform:"uppercase",borderBottom:"1px solid var(--border)"}}>Name</th>
                    <th style={{padding:"8px 0",textAlign:"left",fontSize:11,color:"var(--muted)",textTransform:"uppercase",borderBottom:"1px solid var(--border)"}}>Email</th>
                    <th style={{padding:"8px 0",textAlign:"left",fontSize:11,color:"var(--muted)",textTransform:"uppercase",borderBottom:"1px solid var(--border)"}}>Role</th>
                    <th style={{padding:"8px 0",borderBottom:"1px solid var(--border)"}}></th>
                  </tr></thead>
                  <tbody>
                    {staffUsers.map(u => (
                      <tr key={u.id}>
                        <td style={{padding:"10px 0",borderBottom:"1px solid rgba(37,44,58,0.4)"}}><strong>{u.name}</strong>{u.id === user.id ? <span style={{fontSize:11,color:"var(--accent)",marginLeft:8}}>You</span> : ""}</td>
                        <td style={{padding:"10px 0",borderBottom:"1px solid rgba(37,44,58,0.4)",color:"var(--muted)"}}>{u.email}</td>
                        <td style={{padding:"10px 0",borderBottom:"1px solid rgba(37,44,58,0.4)"}}><span className="status-badge status-draft">{u.role}</span></td>
                        <td style={{padding:"10px 0",borderBottom:"1px solid rgba(37,44,58,0.4)",textAlign:"right"}}>
                          {u.id !== user.id && <button className="action-btn danger" onClick={async () => { if (!window.confirm(`Remove ${u.name}?`)) return; try { await api.delete(`/settings/users/${u.id}`); setStaffUsers(p => p.filter(x => x.id !== u.id)); showToast(`${u.name} removed`); } catch (err) { showToast(err.message, "error"); } }}>Remove</button>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <div style={{borderTop:"1px solid var(--border)",paddingTop:16}}>
                  <div className="settings-title" style={{marginBottom:12}}>Invite Team Member</div>
                  <div className="form-grid">
                    <div className="form-group"><label>Name</label><input value={newUser.name} onChange={e => setNewUser(p => ({...p, name: e.target.value}))} placeholder="Jane Smith" /></div>
                    <div className="form-group"><label>Email</label><input type="email" value={newUser.email} onChange={e => setNewUser(p => ({...p, email: e.target.value}))} placeholder="jane@company.com" /></div>
                    <div className="form-group"><label>Temporary Password</label><input type="password" value={newUser.password} onChange={e => setNewUser(p => ({...p, password: e.target.value}))} placeholder="They can change it later" /></div>
                    <div className="form-group"><label>Role</label>
                      <select value={newUser.role} onChange={e => setNewUser(p => ({...p, role: e.target.value}))}>
                        <option value="staff">Staff (create invoices)</option><option value="admin">Admin (full access)</option>
                      </select>
                    </div>
                  </div>
                  <div style={{marginTop:14}}>
                    <button className="btn btn-primary btn-sm" onClick={handleAddUser}>Add Team Member</button>
                  </div>
                </div>
              </div>
            )}
          </>}
        </main>
      </div>

      {/* MODALS */}
      {showNewItem && <NewItemModal onSave={item => { setItems(p => [...p, item]); }} onClose={() => setShowNewItem(false)} showToast={showToast} qboConnected={qboConnected} />}
      {showMerchantApp && <MerchantApplicationModal onClose={() => setShowMerchantApp(false)} showToast={showToast} />}
      {showNewInvoice && <InvoiceFormModal type="invoice" clients={clients} items={items} onSave={inv => { setInvoices(p => [inv, ...p]); }} onClose={() => setShowNewInvoice(false)} showToast={showToast} qboConnected={qboConnected} onNewItem={() => { setShowNewInvoice(false); setShowNewItem(true); }} />}
      {showNewEstimate && <InvoiceFormModal type="estimate" clients={clients} items={items} onSave={est => { setEstimates(p => [est, ...p]); }} onClose={() => setShowNewEstimate(false)} showToast={showToast} qboConnected={qboConnected} onNewItem={() => { setShowNewEstimate(false); setShowNewItem(true); }} />}
      {(showNewClient || editClient) && (
        <ClientFormModal
          client={editClient}
          onSave={c => { if (editClient) setClients(p => p.map(x => x.id === c.id ? c : x)); else setClients(p => [c, ...p]); setEditClient(null); setShowNewClient(false); }}
          onClose={() => { setShowNewClient(false); setEditClient(null); }}
          showToast={showToast}
        />
      )}
      {viewEstimate && (
        <EstimateDetailModal
          estimateId={viewEstimate}
          onClose={() => setViewEstimate(null)}
          onConverted={invoiceId => { loadInvoices(); loadEstimates(); }}
          showToast={showToast}
          settings={settings}
        />
      )}
      {viewInvoice && (
        <InvoiceDetailModal
          invoiceId={viewInvoice}
          onClose={() => setViewInvoice(null)}
          onSend={id => setInvoices(p => p.map(i => i.id === id ? { ...i, status: "pending" } : i))}
          onPay={inv => { setViewInvoice(null); setPayInvoice(inv); }}
          showToast={showToast}
          settings={settings}
        />
      )}
      {payInvoice && (
        <PaymentModal
          invoice={payInvoice}
          qboConnected={qboConnected}
          onSuccess={(id, status, newBalance) => { setInvoices(p => p.map(i => i.id === id ? { ...i, status: status || "paid", balance_due: newBalance ?? 0 } : i)); setPayInvoice(null); }}
          onClose={() => setPayInvoice(null)}
          showToast={showToast}
        />
      )}

      {toast && <div className={`toast ${toast.type || ""}`}>{toast.msg}</div>}
    </>
  );
}

// ── ROUTER — default export decides which page to show ───────
export default function App() {
  const [page, setPage] = useState(() => {
    // Show landing page only on root path with no token
    if (currentPath === "/" || currentPath === "") {
      return authToken ? "app" : "landing";
    }
    return "app";
  });

  if (currentPath.startsWith("/estimate/")) return <EstimateSignPage />;
  if (currentPath.startsWith("/pay/")) return <PaymentPortalPage />;
  if (currentPath.startsWith("/client/")) return <ClientPortalPage />;
  if (currentPath === "/privacy") return <PrivacyPolicyPage />;
  if (currentPath === "/terms") return <TermsOfServicePage />;

  if (page === "landing") return (
    <LandingPage
      onGetStarted={() => setPage("signup")}
      onLogin={() => setPage("login")}
    />
  );

  if (page === "login" || page === "signup") return (
    <><style>{css}</style>
    <LoginScreen
      initialMode={page === "signup" ? "signup" : "login"}
      onLogin={(u, co) => setPage("app")}
      onBack={() => setPage("landing")}
    /></>
  );

  return <MainApp />;
}
